/**
 * JUPAS Score Calculator Module
 * 
 * This module provides score calculation functions that can be shared across projects.
 * It includes:
 * - Grade to score conversions
 * - Recommendation score calculation
 * - User score calculation for different score types (min/current/max)
 * - Maximum possible score calculation
 * - Minimum requirements checking
 * - Category C language score conversions
 */

// Import JSON data files (generated by updater.py update-subjects)
import SUBJECT_CATEGORIES_DATA from '../../../data/subject_categories.json';
import { JupasProgram, DSEResults, CalculationResult, ScoreBreakdownItem, ScoreSystem, CategoryLimits, MERResult, Grade, ScoringFormula, SubjectRule, ReplacementRule, WeightedSubjectGroupLimit, SubjectGroupLimit } from '../types';
import { calcRecomRate } from './recommendationRate';
import { INSTITUTIONS_DATA } from './data';
import { getUniversityKey } from '../common';
import { SUBJECT_STRAND_MAP } from '../constants';
import { canonicalizeSubjectName } from '../lib/utils/subjectCategories';
// import { Noto_Serif_Yezidi } from 'next/font/google';

// ============================================================================
// Subject Categories (HKDSE Categories A, B, C)
// ============================================================================
const DEBUG_CALCULATOR = 0;
const TRACE_PROGRAMME_CALCULATOR = false;
const calculatorTraceNow = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const traceCalculator = (event: string, payload?: unknown) => {
  if (!TRACE_PROGRAMME_CALCULATOR || typeof window === 'undefined') return;
  console.log(`[CalculatorTrace] ${event}`, payload ?? '');
};
const warnCalculator = (event: string, payload?: unknown) => {
  if (!TRACE_PROGRAMME_CALCULATOR || typeof window === 'undefined') return;
  console.warn(`[CalculatorTrace] ${event}`, payload ?? '');
};



// Subject category mapping for HKDSE subjects
const SUBJECT_CATEGORIES: { [subject: string]: 'A' | 'B' | 'C' } = SUBJECT_CATEGORIES_DATA as { [subject: string]: 'A' | 'B' | 'C' };
const CANONICAL_SUBJECT_CATEGORIES: { [subject: string]: 'A' | 'B' | 'C' } = Object.entries(SUBJECT_CATEGORIES)
  .reduce<{ [subject: string]: 'A' | 'B' | 'C' }>((acc, [subject, category]) => {
    const canonicalSubject = canonicalizeSubjectName(subject);
    if (acc[canonicalSubject] === undefined) {
      acc[canonicalSubject] = category;
    }
    return acc;
  }, {});

/**
 * Get the category of a subject (defaults to 'A' if unknown)
 */
const SUBJECT_CATEGORY_CACHE = new Map<string, 'A' | 'B' | 'C'>();

export const getSubjectCategory = (subject: string): 'A' | 'B' | 'C' => {
  // Same reasoning as canonicalizeSubjectName: a pure lookup over bundled data, called once per
  // subject per programme across the whole scoring pass.
  const cached = SUBJECT_CATEGORY_CACHE.get(subject);
  if (cached !== undefined) return cached;

  const canonicalSubject = canonicalizeSubjectName(subject);
  const category =
    SUBJECT_CATEGORIES[subject] || SUBJECT_CATEGORIES[canonicalSubject] || CANONICAL_SUBJECT_CATEGORIES[canonicalSubject] || 'A';

  SUBJECT_CATEGORY_CACHE.set(subject, category);
  return category;
};

const CORE_SUBJECT_SET = new Set([
  'English Language',
  'Chinese Language',
  'Mathematics',
  'Citizenship and Social Development',
]);

const isCoreSubject = (subject: string): boolean => CORE_SUBJECT_SET.has(canonicalizeSubjectName(subject));

const subjectNamesMatch = (left: string, right: string): boolean => {
  return canonicalizeSubjectName(left) === canonicalizeSubjectName(right);
};

const subjectListIncludes = (subjects: string[] | undefined, subject: string): boolean => {
  return Array.isArray(subjects) && subjects.some(item => subjectNamesMatch(item, subject));
};

const POLYU_PREFERRED_CORE_SUBJECTS = [
  'Chinese Language',
  'English Language',
  'Mathematics',
];

const getPolyUPreferredCoreSubjects = (program: JupasProgram | undefined): string[] => {
  if (!program || getUniversityKey(program.jupas_id) !== '3') {
    return [];
  }

  const faculty = typeof program.faculty_eng === 'string' ? program.faculty_eng.trim() : '';
  if (faculty !== 'Faculty of Humanities') {
    return [];
  }

  const remark = typeof program.program_mer?.remark === 'string' ? program.program_mer.remark : '';
  const marker = 'Preferred subjects with the highest weighting for admission score calculation include:';
  const markerIndex = remark.indexOf(marker);
  if (markerIndex === -1) {
    return [];
  }

  const preferredSegment = remark.slice(markerIndex + marker.length)
    .split('Relevant Applied Learning subjects')[0]
    .split('Applied Learning Subjects:')[0]
    .split('Preference will be given')[0]
    .split('Preference is given')[0]
    .trim();

  if (!preferredSegment) {
    return [];
  }

  return POLYU_PREFERRED_CORE_SUBJECTS.filter(subject => preferredSegment.includes(subject));
};

type NormalizedElectiveRequirement = {
  label?: string;
  footnote?: number;
  categories?: ('A' | 'B' | 'C' | 'core')[];
  include_subjects?: string[];
  exclude_subjects?: string[];
  allowed_core_subjects?: string[];
  max_category_b?: number;
  category_b_exempt_subjects?: string[];
  subject_minimum_levels?: Record<string, number>;
  category_minimum_levels?: Partial<Record<'A' | 'B' | 'C', number>>;
  category_b_requires_distinction?: boolean;
  requireCount: number;
  min_level?: number;
};

const normalizeElectiveRequirements = (rawRequirements: unknown): NormalizedElectiveRequirement[] | undefined => {
  if (!Array.isArray(rawRequirements) || rawRequirements.length === 0) {
    return undefined;
  }

  const normalized = rawRequirements
    .map<NormalizedElectiveRequirement | null>(rawRequirement => {
      if (!rawRequirement || typeof rawRequirement !== 'object') {
        return null;
      }

      const requirement = rawRequirement as {
        label?: unknown;
        footnote?: unknown;
        categories?: unknown;
        category?: unknown;
        include_subjects?: unknown;
        exclude_subjects?: unknown;
        allowed_core_subjects?: unknown;
        max_category_b?: unknown;
        category_b_exempt_subjects?: unknown;
        subject_minimum_levels?: unknown;
        category_minimum_levels?: unknown;
        category_b_requires_distinction?: unknown;
        requireCount?: unknown;
        count?: unknown;
        min_level?: unknown;
      };

      const rawCategories = Array.isArray(requirement.categories)
        ? requirement.categories
        : typeof requirement.categories === 'string'
          ? [requirement.categories]
          : Array.isArray(requirement.category)
            ? requirement.category
            : typeof requirement.category === 'string'
              ? [requirement.category]
              : undefined;

      const categories = Array.isArray(rawCategories)
        ? rawCategories.filter(
          (category): category is 'A' | 'B' | 'C' | 'core' =>
            category === 'A' || category === 'B' || category === 'C' || category === 'core',
        )
        : undefined;

      const rawRequireCount = requirement.requireCount ?? requirement.count ?? 1;
      const parsedRequireCount = Number.parseInt(String(rawRequireCount), 10);

      return {
        label: typeof requirement.label === 'string' ? requirement.label : undefined,
        footnote: typeof requirement.footnote === 'number' ? requirement.footnote : undefined,
        categories: categories && categories.length > 0 ? categories : undefined,
        include_subjects: Array.isArray(requirement.include_subjects) ? requirement.include_subjects.filter((subject): subject is string => typeof subject === 'string') : undefined,
        exclude_subjects: Array.isArray(requirement.exclude_subjects) ? requirement.exclude_subjects.filter((subject): subject is string => typeof subject === 'string') : undefined,
        allowed_core_subjects: Array.isArray(requirement.allowed_core_subjects)
          ? requirement.allowed_core_subjects.filter((subject): subject is string => typeof subject === 'string')
          : undefined,
        max_category_b: typeof requirement.max_category_b === 'number' ? requirement.max_category_b : undefined,
        category_b_exempt_subjects: Array.isArray(requirement.category_b_exempt_subjects)
          ? requirement.category_b_exempt_subjects.filter((subject): subject is string => typeof subject === 'string')
          : undefined,
        subject_minimum_levels: requirement.subject_minimum_levels
          && typeof requirement.subject_minimum_levels === 'object'
          && !Array.isArray(requirement.subject_minimum_levels)
          ? Object.fromEntries(
              Object.entries(requirement.subject_minimum_levels)
                .filter((entry): entry is [string, number] => typeof entry[1] === 'number'),
            )
          : undefined,
        category_minimum_levels: requirement.category_minimum_levels
            && typeof requirement.category_minimum_levels === 'object'
            && !Array.isArray(requirement.category_minimum_levels)
            ? Object.fromEntries(
                Object.entries(requirement.category_minimum_levels)
                  .filter((entry): entry is ['A' | 'B' | 'C', number] =>
                    (entry[0] === 'A' || entry[0] === 'B' || entry[0] === 'C')
                    && typeof entry[1] === 'number',
                  ),
              )
            : undefined,
        category_b_requires_distinction: requirement.category_b_requires_distinction === true,
        requireCount: Number.isFinite(parsedRequireCount) && parsedRequireCount > 0 ? parsedRequireCount : 1,
        min_level: typeof requirement.min_level === 'number' ? requirement.min_level : undefined,
      };
    })
    .filter((requirement): requirement is NormalizedElectiveRequirement => requirement !== null);

  return normalized.length > 0 ? normalized : undefined;
};

const getExplicitSubjectWeight = (subjectWeights: Record<string, number>, subject: string): number | undefined => {
  if (subjectWeights[subject] !== undefined) {
    return subjectWeights[subject];
  }

  const canonicalSubject = canonicalizeSubjectName(subject);
  for (const [subjectKey, weight] of Object.entries(subjectWeights)) {
    if (subjectKey === 'any_one_subject') continue;
    if (canonicalizeSubjectName(subjectKey) === canonicalSubject) {
      return weight;
    }
  }

  return undefined;
};

const getResolvedSubjectWeight = (subjectWeights: Record<string, number>, subject: string): number | undefined => {
  let weight = getExplicitSubjectWeight(subjectWeights, subject);
  if (weight !== undefined) {
    return weight;
  }

  if (SUBJECT_STRAND_MAP[subject]) {
    weight = getExplicitSubjectWeight(subjectWeights, SUBJECT_STRAND_MAP[subject]);
    if (weight !== undefined) {
      return weight;
    }
  }

  if (subjectWeights['any_one_subject'] !== undefined) {
    return subjectWeights['any_one_subject'];
  }

  return undefined;
};

const subjectExcludedByRule = (rule: Pick<SubjectRule, 'exclude_subjects'> | undefined, subject: string): boolean => {
  return subjectListIncludes(rule?.exclude_subjects, subject);
};

const getRuleSubjectNamesWithoutWildcard = (rule: Pick<SubjectRule, 'subject_weights'> | undefined): string[] => {
  return Object.keys(rule?.subject_weights || {}).filter(subject => subject !== 'any_one_subject');
};

const isInformationalWildcardRule = (rule: SubjectRule | undefined): boolean => {
  if (!rule) return false;

  const subjectNames = getRuleSubjectNamesWithoutWildcard(rule);
  return !(rule.required === 1 || rule.required === true || rule.mandatory === 1)
    && rule.subject_weights?.any_one_subject !== undefined
    && subjectNames.length === 0
    && Array.isArray(rule.include_subjects)
    && rule.include_subjects.length > 0
    && typeof rule.note === 'string'
    && rule.note.trim().length > 0
    && !(Array.isArray(rule.exclude_subjects) && rule.exclude_subjects.length > 0);
};

const getActiveSubjectRules = (scoringFormula: ScoringFormula | undefined): SubjectRule[] => {
  if (!Array.isArray(scoringFormula?.subject_rules)) {
    return [];
  }

  return scoringFormula.subject_rules.filter(rule => !isInformationalWildcardRule(rule));
};

const isCuhkMedicineProgramme = (program: JupasProgram | undefined): boolean => {
  const normalizedJupasId = String(program?.jupas_id || '').replace(/^JS/i, '');
  return normalizedJupasId === '4501' || normalizedJupasId === '4502';
};

const isCuhkProgramme = (program: JupasProgram | undefined): boolean => {
  return getUniversityKey(String(program?.jupas_id || '')) === '4';
};
export const doesInstitutionScoreCsd = (instKey: string): boolean => {
  const normalizedKey = String(instKey || '').toUpperCase();
  // Allow HKSYU (SY), VTC (SV), and TWC (ST) to score CSD
  return normalizedKey === 'SY' || normalizedKey === 'SV' || normalizedKey === 'ST';
};

export const doesInstitutionLabelCsdAsCoreSubject = (instKey: string): boolean => {
  const normalizedKey = String(instKey || '').toUpperCase();
  // Allow HKSYU (SY), VTC (SV), and TWC (ST) to label CSD as core subject
  return normalizedKey === 'SY' || normalizedKey === 'SV' || normalizedKey === 'ST';
};

const isCuhkCalculatorExcludedSubject = (subject: string): boolean => {
  const canonicalSubject = canonicalizeSubjectName(subject);
  const category = getSubjectCategory(canonicalSubject);
  if (category === 'B' || category === 'C') {
    return true;
  }

  const lowered = canonicalSubject.toLowerCase();
  return lowered.includes('chinese') && ['gce', 'gcse', 'igcse', 'as-level', 'a-level', 'o-level'].some(token => lowered.includes(token));
};

const filterInstitutionExcludedResults = (
  program: JupasProgram | undefined,
  results: DSEResults,
): DSEResults => {
  if (!isCuhkProgramme(program) || isCuhkMedicineProgramme(program)) {
    return results;
  }

  return Object.fromEntries(
    Object.entries(results).filter(([subject]) => !isCuhkCalculatorExcludedSubject(subject)),
  ) as DSEResults;
};

const getProgrammeDefaultRuleCategories = (
  program: JupasProgram | undefined,
  includeCore: boolean = false,
): string[] => {
  if (isCuhkMedicineProgramme(program)) {
    return includeCore ? ['A', 'core'] : ['A'];
  }

  return includeCore ? ['A', 'B', 'C', 'core'] : ['A', 'B', 'C'];
};

const getRuleSubjectPriority = (subject: string, subjectPriority: string[] | undefined): number => {
  if (!Array.isArray(subjectPriority) || subjectPriority.length === 0) {
    return Number.MAX_SAFE_INTEGER;
  }

  const index = subjectPriority.findIndex(item => subjectNamesMatch(item, subject));
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const isAttainedGrade = (grade: unknown): boolean => {
  const normalized = String(grade || '').trim();
  return normalized === 'A' || normalized === 'Att' || normalized === 'Attained';
};

const isCategoryBDistinctionGrade = (grade: string): boolean => {
  const normalized = grade.toLowerCase().replace(/[^a-z0-9]/g, '');
  return [
    'dist',
    'disti',
    'distii',
    'distinction',
    'distinctioni',
    'distinctionii',
    'attainedwithdistinction',
    'attainedwithdistinctioni',
    'attainedwithdistinctionii',
  ].includes(normalized);
};

const matchesRuleCategoryList = (subject: string, allowedCategories: string[] | undefined): boolean => {
  const categories = allowedCategories || ['A', 'B', 'C'];
  const subjectCategory = getSubjectCategory(subject);
  const subjectIsCore = isCoreSubject(subject);

  for (const category of categories) {
    if (category === 'core') {
      if (subjectIsCore) return true;
      continue;
    }

    if (category === 'A') {
      if (subjectCategory === 'A' && !subjectIsCore) return true;
      continue;
    }

    if (category === subjectCategory) {
      return true;
    }
  }

  return false;
};

const getRuleCategoryPriority = (subject: string, allowedCategories: string[] | undefined): number => {
  const categories = allowedCategories || ['A', 'B', 'C'];
  const subjectCategory = getSubjectCategory(subject);
  const subjectIsCore = isCoreSubject(subject);

  for (let index = 0; index < categories.length; index++) {
    const category = categories[index];

    if (category === 'core') {
      if (subjectIsCore) return index;
      continue;
    }

    if (category === 'A') {
      if (subjectCategory === 'A' && !subjectIsCore) return index;
      continue;
    }

    if (category === subjectCategory) {
      return index;
    }
  }

  return Number.MAX_SAFE_INTEGER;
};

const getSixthSubjectCategoryPriority = (subject: string): number => {
  if (isCoreSubject(subject)) {
    return 0;
  }

  const category = getSubjectCategory(subject);
  if (category === 'A') {
    return 1;
  }
  if (category === 'C') {
    return 2;
  }
  if (category === 'B') {
    return 3;
  }

  return 4;
};

const isBetterSixthSubjectBonusCandidate = (
  candidateSubject: string,
  candidateRawScore: number,
  incumbentSubject: string | null,
  incumbentRawScore: number,
): boolean => {
  if (!incumbentSubject) {
    return true;
  }

  if (candidateRawScore !== incumbentRawScore) {
    return candidateRawScore > incumbentRawScore;
  }

  const candidatePriority = getSixthSubjectCategoryPriority(candidateSubject);
  const incumbentPriority = getSixthSubjectCategoryPriority(incumbentSubject);
  if (candidatePriority !== incumbentPriority) {
    return candidatePriority < incumbentPriority;
  }

  return candidateSubject.localeCompare(incumbentSubject) < 0;
};

const estimateBestRemainingSubjectWeight = (
  scoringFormula: ScoringFormula | undefined,
  subject: string,
): number => {
  if (!scoringFormula?.subject_rules || !Array.isArray(scoringFormula.subject_rules)) {
    return 1.0;
  }

  const canonicalSubject = canonicalizeSubjectName(subject);
  let bestWeight = 1.0;

  for (const rule of scoringFormula.subject_rules) {
    const subjectWeights = rule?.subject_weights || {};
    const explicitWeight = getExplicitSubjectWeight(subjectWeights, canonicalSubject);
    if (explicitWeight !== undefined) {
      bestWeight = Math.max(bestWeight, explicitWeight);
    }

    const wildcardWeight = subjectWeights['any_one_subject'];
    if (wildcardWeight === undefined) {
      continue;
    }

    const defaultCategories = ['A', 'B', 'C', 'core'];
    if (matchesRuleCategoryList(canonicalSubject, rule.categories || defaultCategories)) {
      bestWeight = Math.max(bestWeight, wildcardWeight);
    }
  }

  return bestWeight;
};

const hasExplicitProgrammeSubjectWeight = (subject: string, program: JupasProgram): boolean => {
  const subjectRules = program.scoring_formula?.subject_rules;
  if (!Array.isArray(subjectRules) || subjectRules.length === 0) {
    return false;
  }

  return subjectRules.some(rule => {
    const subjectWeights = rule.subject_weights;
    if (!subjectWeights || typeof subjectWeights !== 'object') {
      return false;
    }

    return getExplicitSubjectWeight(subjectWeights as Record<string, number>, subject) !== undefined;
  });
};

const isProgramAllowedCategoryBSubject = (subject: string, program: JupasProgram): boolean => {
  if (getSubjectCategory(subject) !== 'B') {
    return true;
  }

  // If the scoring formula explicitly assigns a weight to a Category B subject,
  // treat it as allowed even when a scraped sapl_subjects allowlist is stale.
  if (hasExplicitProgrammeSubjectWeight(subject, program)) {
    return true;
  }

  const saplSubjects = program.program_mer?.sapl_subjects;
  if (Array.isArray(saplSubjects) && saplSubjects.length > 0) {
    return subjectListIncludes(saplSubjects, subject);
  }

  const scoringFormula = program?.scoring_formula as ScoringFormula | undefined;
  const subjectRules = Array.isArray(scoringFormula?.subject_rules) ? scoringFormula.subject_rules : [];
  const bonusRules = Array.isArray(scoringFormula?.bonus_subjects) ? scoringFormula.bonus_subjects : [];
  const ruleCandidates = [...subjectRules, ...bonusRules];
  const allowsCategoryBByRule = ruleCandidates.some(rule => {
    const categories = Array.isArray(rule?.categories) ? rule.categories : undefined;
    return Boolean(categories?.length) && matchesRuleCategoryList(subject, categories);
  });
  if (allowsCategoryBByRule) {
    return true;
  }

  if (getUniversityKey(program.jupas_id) === '3') {
    return hasExplicitProgrammeSubjectWeight(subject, program);
  }

  return true;
};

const MATH_EXTENDED_MODULE_1 = 'Mathematics Extended Part Module 1';
const MATH_EXTENDED_MODULE_2 = 'Mathematics Extended Part Module 2';

const isJS4238MathematicsRequirementMet = (program: JupasProgram, results: DSEResults): boolean => {
  if (program.jupas_id !== '4238') return false;

  const mathematicsGrade = results['Mathematics'];
  const mathematicsLevel = mathematicsGrade
    ? getGradeScore(String(mathematicsGrade), program, 'Mathematics')
    : 0;

  if (mathematicsLevel >= 5) return true;
  if (mathematicsLevel < 3 || mathematicsLevel > 4) return false;

  const extendedMathLevels = [MATH_EXTENDED_MODULE_1, MATH_EXTENDED_MODULE_2].map(subject => {
    const grade = results[subject as keyof DSEResults];
    return grade ? getGradeScore(String(grade), program, subject) : 0;
  });

  return Math.max(...extendedMathLevels) >= 5;
};

const applyPostSelectionReplacementRules = (
  program: JupasProgram,
  scoringFormula: ScoringFormula,
  results: DSEResults,
  breakdown: ScoreBreakdownItem[],
): { total: number; usedSourceSubjects: string[] } => {
  const replacementRules = scoringFormula.replacement_rules;
  if (!Array.isArray(replacementRules) || replacementRules.length === 0) {
    return {
      total: breakdown.reduce((sum, item) => sum + (item.counted === false ? 0 : item.weightedScore), 0),
      usedSourceSubjects: [],
    };
  }

  const usedSourceSubjects = new Set<string>();

  for (const rule of replacementRules) {
    if (!rule || rule.type !== 'extended_math_half_replace_worst') {
      continue;
    }

    const sourceSubjects = Array.isArray(rule.source_subjects)
      ? Array.from(new Set(rule.source_subjects.map(subject => canonicalizeSubjectName(subject)).filter(Boolean)))
      : [];
    if (sourceSubjects.length === 0) {
      continue;
    }

    let bestSource: { subject: string; grade: string; rawScore: number } | null = null;
    for (const subject of sourceSubjects) {
      const grade = results[subject];
      if (!grade) {
        continue;
      }

      const rawScore = getGradeScore(grade, program, subject);
      if (rawScore <= 0) {
        continue;
      }

      if (
        !bestSource
        || rawScore > bestSource.rawScore
        || (rawScore === bestSource.rawScore && subject.localeCompare(bestSource.subject) < 0)
      ) {
        bestSource = { subject, grade, rawScore };
      }
    }

    if (!bestSource) {
      continue;
    }

    const worstCountedItem = breakdown
      .filter(item => (
        item.counted !== false
        && item.slotType !== 'BONUS'
        && item.slotType !== 'MISSING'
        && item.slotType !== 'MISSING_PICK_ONE'
        && !sourceSubjects.includes(canonicalizeSubjectName(item.subject))
      ))
      .reduce<ScoreBreakdownItem | null>((worst, item) => {
        if (!worst) {
          return item;
        }
        if (item.rawScore < worst.rawScore) {
          return item;
        }
        if (item.rawScore > worst.rawScore) {
          return worst;
        }
        if (item.weightedScore < worst.weightedScore) {
          return item;
        }
        return worst;
      }, null);

    if (!worstCountedItem) {
      continue;
    }

    const blendTargetRatio = rule.blend_target_ratio ?? 0.5;
    const blendSourceRatio = rule.blend_source_ratio ?? 0.5;
    const countedMainItems = breakdown.filter(item => (
      item.counted !== false
      && item.slotType !== 'BONUS'
      && item.slotType !== 'MISSING'
      && item.slotType !== 'MISSING_PICK_ONE'
      && item.slotType !== 'REPLACEMENT'
    ));
    const mainSlotTarget = scoringFormula.num_of_subjects || 5;

    if (countedMainItems.length < mainSlotTarget) {
      const sourceContributionWeight = worstCountedItem.weight * blendSourceRatio;
      const sourceContributionScore = bestSource.rawScore * sourceContributionWeight;
      if (sourceContributionScore <= 0) {
        continue;
      }

      breakdown.push({
        subject: bestSource.subject,
        grade: bestSource.grade,
        rawScore: bestSource.rawScore,
        weight: sourceContributionWeight,
        weightedScore: sourceContributionScore,
        description: `${bestSource.subject} replacement contribution`,
        category: getSubjectCategory(bestSource.subject),
        mandatory: false,
        slotType: 'REPLACEMENT',
        counted: true,
      });
      usedSourceSubjects.add(bestSource.subject);
      continue;
    }

    const minimumSourceScoreGap = rule.minimum_source_score_gap ?? 0;
    if (bestSource.rawScore <= worstCountedItem.rawScore + minimumSourceScoreGap) {
      continue;
    }

    const originalTargetRawScore = worstCountedItem.rawScore;
    const sourceContributionWeight = worstCountedItem.weight * blendSourceRatio;
    const sourceContributionScore = bestSource.rawScore * sourceContributionWeight;
    const targetContributionScore = worstCountedItem.rawScore * worstCountedItem.weight * blendTargetRatio;
    const blendedWeightedScore = targetContributionScore + sourceContributionScore;

    if (blendedWeightedScore <= worstCountedItem.weightedScore || sourceContributionScore <= 0) {
      continue;
    }

    worstCountedItem.weightedScore = blendedWeightedScore;
    if (worstCountedItem.rawScore > 0) {
      worstCountedItem.weight = blendedWeightedScore / worstCountedItem.rawScore;
    }
    worstCountedItem.description = `${worstCountedItem.description} + ${bestSource.subject} replacement`;
    worstCountedItem.replacementSourceSubject = bestSource.subject;
    worstCountedItem.replacementSourceGrade = bestSource.grade;
    worstCountedItem.replacementSourceRawScore = bestSource.rawScore;
    worstCountedItem.replacementTargetOriginalRawScore = originalTargetRawScore;
    worstCountedItem.replacementRuleNote = rule.note;
    worstCountedItem.replacementBlendTargetRatio = blendTargetRatio;
    worstCountedItem.replacementBlendSourceRatio = blendSourceRatio;

    const countedSourceItem = breakdown.find(item => (
      item.counted !== false
      && canonicalizeSubjectName(item.subject) === bestSource!.subject
    ));
    if (countedSourceItem) {
      countedSourceItem.counted = false;
      countedSourceItem.description = `${countedSourceItem.description} used for ${worstCountedItem.subject} replacement`;

      const countedSubjects = new Set(
        breakdown
          .filter(item => item.counted !== false)
          .map(item => canonicalizeSubjectName(item.subject))
      );
      const replacementCandidate = Object.entries(results)
        .map(([subject, grade]) => {
          const canonicalSubject = canonicalizeSubjectName(subject);
          const rawScore = getGradeScore(grade, program, canonicalSubject);
          const weight = estimateBestRemainingSubjectWeight(scoringFormula, canonicalSubject);
          return {
            subject: canonicalSubject,
            grade,
            rawScore,
            weight,
            weightedScore: rawScore * weight,
          };
        })
        .filter(candidate => (
          candidate.rawScore > 0
          && !countedSubjects.has(candidate.subject)
          && !sourceSubjects.includes(candidate.subject)
        ))
        .sort((left, right) => {
          if (left.weightedScore !== right.weightedScore) {
            return right.weightedScore - left.weightedScore;
          }
          if (left.rawScore !== right.rawScore) {
            return right.rawScore - left.rawScore;
          }
          return left.subject.localeCompare(right.subject);
        })[0];

      if (replacementCandidate) {
        breakdown.push({
          subject: replacementCandidate.subject,
          grade: replacementCandidate.grade,
          rawScore: replacementCandidate.rawScore,
          weight: replacementCandidate.weight,
          weightedScore: replacementCandidate.weightedScore,
          description: 'Replacement backfill subject',
          category: getSubjectCategory(replacementCandidate.subject),
          mandatory: false,
          slotType: 'REMAINING',
          counted: true,
        });
      }
    }

    usedSourceSubjects.add(bestSource.subject);
  }

  return {
    total: breakdown.reduce((sum, item) => sum + (item.counted === false ? 0 : item.weightedScore), 0),
    usedSourceSubjects: Array.from(usedSourceSubjects),
  };
};

export const getEffectiveForbiddenGroups = (
  program: JupasProgram | undefined,
  institutionData: any,
): string[][] | undefined => {
  if (program?.forbidden_subject_groups && program.forbidden_subject_groups.length > 0) {
    return program.forbidden_subject_groups;
  }

  if (institutionData?.forbidden_subject_groups && institutionData.forbidden_subject_groups.length > 0) {
    return institutionData.forbidden_subject_groups;
  }

  return (INSTITUTIONS_DATA as any)?.common?.forbidden_subject_groups;
};

const getScoreSelectionForbiddenGroups = (
  program: JupasProgram | undefined,
  institutionData: any,
): string[][] | undefined => {
  return getEffectiveForbiddenGroups(program, institutionData);
};

const countSubjectsAfterForbiddenGroupDedup = (
  subjects: string[],
  forbiddenGroups: string[][] | undefined,
): number => {
  if (!forbiddenGroups || forbiddenGroups.length === 0) {
    return subjects.length;
  }

  const remainingSubjects = new Set(subjects.map(subject => canonicalizeSubjectName(subject)));
  let count = 0;

  for (const group of forbiddenGroups) {
    const matchingSubjects = group.filter(subject =>
      remainingSubjects.has(canonicalizeSubjectName(subject))
    );

    if (matchingSubjects.length === 0) {
      continue;
    }

    count += 1;
    for (const subject of matchingSubjects) {
      remainingSubjects.delete(canonicalizeSubjectName(subject));
    }
  }

  count += remainingSubjects.size;
  return count;
};

const subjectMatchesElectiveRequirement = (
  subject: string,
  grade: string,
  requirement: NormalizedElectiveRequirement,
  defaultMinLevel: number,
  program: JupasProgram,
  respectProgramCategoryBAllowlist: boolean,
  includeSubjectsMode: 'restrict' | 'extend' = 'restrict',
): boolean => {
  const allowedCoreSubject = isCoreSubject(subject)
    && subjectListIncludes(requirement.allowed_core_subjects, subject);
  const coreSubjectIsExplicitlyAllowed =
    allowedCoreSubject ||
    requirement.categories?.includes('core') ||
    (requirement.include_subjects?.some(includedSubject => subjectNamesMatch(includedSubject, subject)) ?? false);
  if (isCoreSubject(subject) && !coreSubjectIsExplicitlyAllowed) {
    return false;
  }

  if (requirement.exclude_subjects && subjectListIncludes(requirement.exclude_subjects, subject)) {
    return false;
  }

  if (allowedCoreSubject) {
    const minLevel = requirement.min_level ?? defaultMinLevel;
    return getGradeScore(String(grade), program, subject) >= minLevel;
  }

  if (respectProgramCategoryBAllowlist && !isProgramAllowedCategoryBSubject(subject, program)) {
    return false;
  }

  const hasCategories = Boolean(requirement.categories && requirement.categories.length > 0);
  const hasIncludedSubjects = Boolean(requirement.include_subjects && requirement.include_subjects.length > 0);
  const matchesCategories = hasCategories
    ? matchesRuleCategoryList(subject, requirement.categories as string[] | undefined)
    : false;
  const matchesIncludedSubjects = hasIncludedSubjects
    ? subjectListIncludes(requirement.include_subjects, subject)
    : false;

  if (includeSubjectsMode === 'extend' && hasCategories && hasIncludedSubjects) {
    if (!matchesCategories && !matchesIncludedSubjects) {
      return false;
    }
  } else {
    if (hasCategories && !matchesCategories) {
      return false;
    }

    if (hasIncludedSubjects && !matchesIncludedSubjects) {
      return false;
    }
  }

  const subjectMinimum = Object.entries(requirement.subject_minimum_levels || {})
    .find(([requiredSubject]) => subjectNamesMatch(requiredSubject, subject))?.[1];
  const subjectCategory = getSubjectCategory(subject);
  const categoryMinimum = requirement.category_minimum_levels?.[subjectCategory];
  const minLevel = subjectMinimum ?? categoryMinimum ?? requirement.min_level ?? defaultMinLevel;
  if (
    subjectCategory === 'B'
    && requirement.category_b_requires_distinction
    && !isCategoryBDistinctionGrade(String(grade).trim())
  ) {
    return false;
  }
  return getGradeScore(String(grade), program, subject) >= minLevel;
};

const hasForbiddenSubjectGroupDuplicate = (
  subjects: string[],
  forbiddenGroups: string[][] | undefined,
): boolean => (forbiddenGroups || []).some(group => {
  let matchingCount = 0;
  for (const subject of subjects) {
    if (group.some(forbiddenSubject => subjectNamesMatch(forbiddenSubject, subject))) {
      matchingCount += 1;
      if (matchingCount > 1) return true;
    }
  }
  return false;
});

const chooseSubjectCombinations = <T,>(items: T[], count: number): T[][] => {
  if (count <= 0) return [[]];
  if (items.length < count) return [];

  const combinations: T[][] = [];

  const walk = (start: number, remaining: number, current: T[]) => {
    if (remaining === 0) {
      combinations.push([...current]);
      return;
    }

    for (let index = start; index <= items.length - remaining; index += 1) {
      current.push(items[index]);
      walk(index + 1, remaining - 1, current);
      current.pop();
    }
  };

  walk(0, count, []);
  return combinations;
};

const getWeightedGroupMatchCount = (
  weightedSubjects: Set<string>,
  groupLimit: WeightedSubjectGroupLimit,
): number => {
  return groupLimit.subjects.reduce((count, subject) => {
    return count + (weightedSubjects.has(canonicalizeSubjectName(subject)) ? 1 : 0);
  }, 0);
};

/**
 * Keyed on the formula object itself, which is bundled data and never mutated, so a WeakMap entry
 * stays valid for as long as the formula does and is collected with it. The three callers below sit
 * inside the subject-assignment search and rebuild this same array at every node of it.
 */
const SUBJECT_GROUP_LIMITS_CACHE = new WeakMap<ScoringFormula, SubjectGroupLimit[]>();

const getSubjectGroupLimits = (
  scoringFormula: ScoringFormula | undefined,
): SubjectGroupLimit[] => {
  if (!scoringFormula) return [];

  const cached = SUBJECT_GROUP_LIMITS_CACHE.get(scoringFormula);
  if (cached !== undefined) return cached;

  const formulaLimits = Array.isArray(scoringFormula.subject_group_limits)
    ? scoringFormula.subject_group_limits
    : [];
  const ruleLimits = Array.isArray(scoringFormula.subject_rules)
    ? scoringFormula.subject_rules.flatMap(rule => (
      Array.isArray(rule.subject_group_limits) ? rule.subject_group_limits : []
    ))
    : [];

  const limits = [...formulaLimits, ...ruleLimits];
  SUBJECT_GROUP_LIMITS_CACHE.set(scoringFormula, limits);
  return limits;
};

const violatesSubjectGroupLimits = (
  subject: string,
  usedSubjects: Set<string>,
  scoringFormula: ScoringFormula | undefined,
): boolean => {
  const groupLimits = getSubjectGroupLimits(scoringFormula);
  if (groupLimits.length === 0) return false;

  const candidateSubject = canonicalizeSubjectName(subject);
  return groupLimits.some(groupLimit => {
    if (!Array.isArray(groupLimit.subjects) || groupLimit.subjects.length === 0 || groupLimit.max_count < 0) {
      return false;
    }

    const groupSubjects = new Set(groupLimit.subjects.map(canonicalizeSubjectName));
    if (!groupSubjects.has(candidateSubject)) return false;

    const usedGroupCount = Array.from(usedSubjects)
      .filter(usedSubject => groupSubjects.has(canonicalizeSubjectName(usedSubject)))
      .length;
    return usedGroupCount >= groupLimit.max_count;
  });
};

const violatesSubjectGroupLimitsForBonus = (
  subject: string,
  mainSubjects: Set<string>,
  scoringFormula: ScoringFormula | undefined,
): boolean => {
  const groupLimits = getSubjectGroupLimits(scoringFormula);
  if (groupLimits.length === 0) return false;

  const candidateSubject = canonicalizeSubjectName(subject);
  return groupLimits.some(groupLimit => {
    if (!Array.isArray(groupLimit.subjects) || groupLimit.subjects.length === 0) {
      return false;
    }

    const groupSubjects = new Set(groupLimit.subjects.map(canonicalizeSubjectName));
    if (!groupSubjects.has(candidateSubject)) return false;

    const mainGroupSubjects = Array.from(mainSubjects)
      .filter(usedSubject => groupSubjects.has(canonicalizeSubjectName(usedSubject)));
    const mainGroupCount = mainGroupSubjects.length;

    const effectiveMaxCount = typeof groupLimit.max_count_bonus === 'number'
      ? groupLimit.max_count + groupLimit.max_count_bonus
      : groupLimit.max_count;
    if (mainGroupCount + 1 > effectiveMaxCount) return true;

    if (groupLimit.no_duplicate_category_in_bonus && mainGroupCount > 0) {
      const candidateCategory = getSubjectCategory(candidateSubject);
      const mainGroupCategories = new Set(mainGroupSubjects.map(s => getSubjectCategory(s)));
      if (mainGroupCategories.has(candidateCategory)) return true;
    }

    return false;
  });
};

const violatesSubjectGroupLimitsForSelection = (
  selectedSubjects: Set<string>,
  scoringFormula: ScoringFormula | undefined,
): boolean => {
  const groupLimits = getSubjectGroupLimits(scoringFormula);
  if (groupLimits.length === 0) return false;

  return groupLimits.some(groupLimit => {
    if (!Array.isArray(groupLimit.subjects) || groupLimit.subjects.length === 0 || groupLimit.max_count < 0) {
      return false;
    }

    const groupSubjects = new Set(groupLimit.subjects.map(canonicalizeSubjectName));
    const matchedCount = Array.from(selectedSubjects)
      .filter(subject => groupSubjects.has(canonicalizeSubjectName(subject)))
      .length;
    return matchedCount > groupLimit.max_count;
  });
};

const calculateCappedWeightedPoolSelection = (
  program: JupasProgram,
  scoringFormula: ScoringFormula,
  rule: SubjectRule,
  results: DSEResults,
  availableSubjects: Set<string>,
  forbiddenGroups: string[][] | undefined,
): { total: number; breakdown: ScoreBreakdownItem[] } | null => {
  const numSubjects = scoringFormula.num_of_subjects || rule.count || 0;
  const maxWeightedSubjects = scoringFormula.max_weighted_subjects;
  const baseWeight = rule.subject_weights?.any_one_subject;

  if (
    numSubjects <= 0
    || typeof maxWeightedSubjects !== 'number'
    || maxWeightedSubjects < 0
    || baseWeight === undefined
  ) {
    return null;
  }

  const defaultCategories = getProgrammeDefaultRuleCategories(program, true);
  const allowedCategories = rule.categories || defaultCategories;
  const weightedGroupLimits = Array.isArray(scoringFormula.weighted_subject_group_limits)
    ? scoringFormula.weighted_subject_group_limits.filter(group => Array.isArray(group.subjects) && group.subjects.length > 0 && (group.max_count || 0) >= 0)
    : [];

  const candidates = Array.from(availableSubjects)
    .filter(subject => !subjectExcludedByRule(rule, subject) && matchesRuleCategoryList(subject, allowedCategories))
    .map(subject => {
      const grade = results[subject];
      const rawScore = getGradeScore(grade, program, subject);
      const boostedWeight = getResolvedSubjectWeight(rule.subject_weights || {}, subject) ?? baseWeight;
      return {
        subject,
        grade,
        rawScore,
        category: getSubjectCategory(subject),
        boostedWeight,
      };
    });

  if (candidates.length < numSubjects) {
    return null;
  }

  const validSubjectSelections = chooseSubjectCombinations(candidates, numSubjects).filter(selection => {
    const selectedSubjects = new Set(selection.map(candidate => candidate.subject));
    if (violatesSubjectGroupLimitsForSelection(selectedSubjects, scoringFormula)) {
      return false;
    }
    if (!forbiddenGroups || forbiddenGroups.length === 0) {
      return true;
    }

    const usedSubjects = new Set<string>();
    for (const candidate of selection) {
      if (hasForbiddenConflictForSubjectGroups(candidate.subject, usedSubjects, forbiddenGroups)) {
        return false;
      }
      usedSubjects.add(candidate.subject);
    }

    return true;
  });

  if (validSubjectSelections.length === 0) {
    return null;
  }

  let bestSelection: {
    subjects: typeof candidates;
    weightedSubjects: Set<string>;
    total: number;
    rawTotal: number;
  } | null = null;

  for (const selection of validSubjectSelections) {
    const baseTotal = selection.reduce((sum, candidate) => sum + candidate.rawScore * baseWeight, 0);
    const rawTotal = selection.reduce((sum, candidate) => sum + candidate.rawScore, 0);
    const weightedEligible = selection.filter(candidate => candidate.boostedWeight > baseWeight + 1e-9);
    const weightedCombos = chooseSubjectCombinations(weightedEligible, 0)
      .concat(
        ...Array.from({ length: Math.min(maxWeightedSubjects, weightedEligible.length) }, (_, index) =>
          chooseSubjectCombinations(weightedEligible, index + 1),
        ),
      );

    for (const weightedCombo of weightedCombos) {
      const weightedSubjectSet = new Set(weightedCombo.map(candidate => canonicalizeSubjectName(candidate.subject)));
      let violatesGroupLimit = false;
      for (const groupLimit of weightedGroupLimits) {
        if (getWeightedGroupMatchCount(weightedSubjectSet, groupLimit) > groupLimit.max_count) {
          violatesGroupLimit = true;
          break;
        }
      }
      if (violatesGroupLimit) continue;

      const weightedDelta = weightedCombo.reduce((sum, candidate) => {
        return sum + candidate.rawScore * (candidate.boostedWeight - baseWeight);
      }, 0);
      const total = baseTotal + weightedDelta;

      if (
        bestSelection === null
        || total > bestSelection.total
        || (total === bestSelection.total && rawTotal > bestSelection.rawTotal)
      ) {
        bestSelection = {
          subjects: selection,
          weightedSubjects: weightedSubjectSet,
          total,
          rawTotal,
        };
      }
    }
  }

  if (!bestSelection) {
    return null;
  }

  const breakdown = bestSelection.subjects
    .map(candidate => {
      const canonicalSubject = canonicalizeSubjectName(candidate.subject);
      const weight = bestSelection.weightedSubjects.has(canonicalSubject) ? candidate.boostedWeight : baseWeight;
      return {
        subject: candidate.subject,
        grade: candidate.grade,
        rawScore: candidate.rawScore,
        weight,
        weightedScore: candidate.rawScore * weight,
        description: `Best weighted pool (${numSubjects})`,
        category: candidate.category,
        mandatory: false,
        slotType: 'REMAINING' as const,
      };
    })
    .sort((a, b) => {
      if (a.weightedScore !== b.weightedScore) {
        return b.weightedScore - a.weightedScore;
      }
      if (a.rawScore !== b.rawScore) {
        return b.rawScore - a.rawScore;
      }
      return a.subject.localeCompare(b.subject);
    });

  return {
    total: breakdown.reduce((sum, item) => sum + item.weightedScore, 0),
    breakdown,
  };
};

const applyWeightedElectiveCapToBreakdown = (
  breakdown: ScoreBreakdownItem[],
  scoringFormula: ScoringFormula,
): number => {
  const maxWeightedSubjects = scoringFormula.max_weighted_subjects;
  if (typeof maxWeightedSubjects !== 'number' || maxWeightedSubjects < 0) {
    return breakdown.reduce((sum, item) => sum + (item.counted === false ? 0 : (item.weightedScore || 0)), 0);
  }

  const weightedGroupLimits = Array.isArray(scoringFormula.weighted_subject_group_limits)
    ? scoringFormula.weighted_subject_group_limits.filter(group => Array.isArray(group.subjects) && group.subjects.length > 0 && (group.max_count || 0) >= 0)
    : [];

  const weightedElectives = breakdown
    .filter(item => item.slotType !== 'BONUS')
    .filter(item => item.counted !== false)
    .filter(item => !isCoreSubject(item.subject))
    .map(item => ({
      item,
      boostedWeight: (scoringFormula.subject_rules || []).reduce((bestWeight, rule) => {
        const resolvedWeight = getResolvedSubjectWeight(rule.subject_weights || {}, item.subject);
        if (resolvedWeight === undefined) {
          return bestWeight;
        }
        return Math.max(bestWeight, resolvedWeight);
      }, 1),
    }))
    .filter(entry => entry.boostedWeight > 1)
    .map(entry => ({
      ...entry,
      weightedDelta: (entry.item.rawScore || 0) * (entry.boostedWeight - 1),
    }));

  if (weightedElectives.length === 0) {
    return breakdown.reduce((sum, item) => sum + (item.counted === false ? 0 : (item.weightedScore || 0)), 0);
  }

  const weightedCombos = chooseSubjectCombinations(
    weightedElectives,
    0,
  ).concat(
    ...Array.from({ length: Math.min(maxWeightedSubjects, weightedElectives.length) }, (_, index) =>
      chooseSubjectCombinations(weightedElectives, index + 1),
    ),
  );

  let bestWeightedSubjects = new Set<ScoreBreakdownItem>();
  let bestWeightedDelta = Number.NEGATIVE_INFINITY;

  for (const weightedCombo of weightedCombos) {
    const weightedSubjectSet = new Set(weightedCombo.map(entry => canonicalizeSubjectName(entry.item.subject)));
    let violatesGroupLimit = false;
    for (const groupLimit of weightedGroupLimits) {
      if (getWeightedGroupMatchCount(weightedSubjectSet, groupLimit) > groupLimit.max_count) {
        violatesGroupLimit = true;
        break;
      }
    }
    if (violatesGroupLimit) {
      continue;
    }

    const weightedDelta = weightedCombo.reduce((sum, entry) => {
      return sum + entry.weightedDelta;
    }, 0);

    if (weightedDelta > bestWeightedDelta) {
      bestWeightedDelta = weightedDelta;
      bestWeightedSubjects = new Set(weightedCombo.map(entry => entry.item));
      continue;
    }

    if (weightedDelta === bestWeightedDelta) {
      const bestSubjects = Array.from(bestWeightedSubjects).map(item => item.subject).sort();
      const currentSubjects = weightedCombo.map(entry => entry.item.subject).sort();
      if (currentSubjects.join('|') > bestSubjects.join('|')) {
        bestWeightedSubjects = new Set(weightedCombo.map(entry => entry.item));
      }
    }
  }

  for (const entry of weightedElectives) {
    if (bestWeightedSubjects.has(entry.item)) {
      entry.item.weight = entry.boostedWeight;
      entry.item.weightedScore = (entry.item.rawScore || 0) * entry.boostedWeight;
    } else {
      entry.item.weight = 1.0;
      entry.item.weightedScore = entry.item.rawScore || 0;
    }
  }

  return breakdown.reduce((sum, item) => sum + (item.counted === false ? 0 : (item.weightedScore || 0)), 0);
};

const hasForbiddenConflictForSubjectGroups = (
  subject: string,
  usedSubjects: Set<string>,
  forbiddenGroups: string[][],
): boolean => {
  for (const group of forbiddenGroups) {
    if (!group.some(item => subjectNamesMatch(item, subject))) {
      continue;
    }

    for (const usedSubject of usedSubjects) {
      if (group.some(item => subjectNamesMatch(item, usedSubject))) {
        return true;
      }
    }
  }

  return false;
};

const evaluateElectiveRequirements = (
  program: JupasProgram,
  results: DSEResults,
  requirements: NormalizedElectiveRequirement[] | undefined,
  defaultMinLevel: number,
  respectProgramCategoryBAllowlist: boolean,
  includeSubjectsMode: 'restrict' | 'extend' = 'restrict',
): MERResult['elective_requirements'] => {
  if (!requirements || requirements.length === 0) {
    return undefined;
  }

  const institutionData = (INSTITUTIONS_DATA as any)[getUniversityKey(program.jupas_id)];
  const forbiddenSubjectGroups = getEffectiveForbiddenGroups(program, institutionData);
  const candidateEntries: Array<[string, Grade]> = [];
  const sortedCandidates = Object.entries(results)
    .filter((entry): entry is [string, Grade] => Boolean(entry[1]))
    .sort(([subjectA, gradeA], [subjectB, gradeB]) =>
      getGradeScore(String(gradeB), program, subjectB) - getGradeScore(String(gradeA), program, subjectA),
    );
  for (const candidate of sortedCandidates) {
    if (!candidateEntries.some(([selectedSubject]) =>
      (forbiddenSubjectGroups || []).some(group =>
        group.some(subject => subjectNamesMatch(subject, candidate[0]))
        && group.some(subject => subjectNamesMatch(subject, selectedSubject)),
      ),
    )) {
      candidateEntries.push(candidate);
    }
  }

  // Sort requirements so include_subjects blocks (more constrained) are assigned first
  const sortedIndices = requirements.map((_, i) => i).sort((a, b) => {
    const aHasInc = requirements[a].include_subjects?.length ? 1 : 0;
    const bHasInc = requirements[b].include_subjects?.length ? 1 : 0;
    return bHasInc - aHasInc;
  });
  const sortedRequirements = sortedIndices.map(i => requirements[i]);

  const assignRequirements = (
    requirementIndex: number,
    usedSubjects: Set<string>,
  ): string[][] | null => {
    if (requirementIndex >= sortedRequirements.length) {
      return [];
    }

    const requirement = sortedRequirements[requirementIndex];
    const matchingSubjects = candidateEntries
      .filter(([subject, grade]) => !usedSubjects.has(subject) && subjectMatchesElectiveRequirement(subject, String(grade), requirement, defaultMinLevel, program, respectProgramCategoryBAllowlist, includeSubjectsMode))
      .map(([subject]) => subject);

    const combinations = chooseSubjectCombinations(matchingSubjects, requirement.requireCount || 1)
      .filter(combination => !hasForbiddenSubjectGroupDuplicate(
        [...usedSubjects, ...combination],
        forbiddenSubjectGroups,
      ))
      .filter(combination => requirement.max_category_b === undefined
        || [...usedSubjects, ...combination].filter(subject =>
          getSubjectCategory(subject) === 'B'
          && !subjectListIncludes(requirement.category_b_exempt_subjects, subject),
        ).length <= requirement.max_category_b);
    for (const combination of combinations) {
      const nextUsedSubjects = new Set(usedSubjects);
      combination.forEach(subject => nextUsedSubjects.add(subject));
      const remainder = assignRequirements(requirementIndex + 1, nextUsedSubjects);
      if (remainder) {
        return [combination, ...remainder];
      }
    }

    return null;
  };

  const sortedAssignments = assignRequirements(0, new Set<string>());
  // Map sorted assignments back to original requirement order
  let assignments: string[][] | null;
  if (sortedAssignments) {
    assignments = sortedIndices.map((_, sortedIdx) => {
      const originalIdx = sortedIndices[sortedIdx];
      return { originalIdx, subjects: sortedAssignments[sortedIdx] };
    }).sort((a, b) => a.originalIdx - b.originalIdx).map(a => a.subjects);
  } else {
    // Full assignment failed; try greedy per-block assignment so met blocks still get ✓
    const usedSubjects = new Set<string>();
    const partialByOriginal = new Array<string[]>(requirements.length).fill([]);
    for (const sortedIdx of sortedIndices.keys()) {
      const req = sortedRequirements[sortedIdx];
      const matching = candidateEntries
        .filter(([subject, grade]) => !usedSubjects.has(subject) && subjectMatchesElectiveRequirement(subject, String(grade), req, defaultMinLevel, program, respectProgramCategoryBAllowlist, includeSubjectsMode))
        .map(([subject]) => subject);
      const needed = req.requireCount || 1;
      const picked = chooseSubjectCombinations(matching, needed).find(combination =>
        !hasForbiddenSubjectGroupDuplicate([...usedSubjects, ...combination], forbiddenSubjectGroups)
        && (req.max_category_b === undefined
          || [...usedSubjects, ...combination].filter(subject =>
            getSubjectCategory(subject) === 'B'
            && !subjectListIncludes(req.category_b_exempt_subjects, subject),
          ).length <= req.max_category_b),
      ) || [];
      const originalIdx = sortedIndices[sortedIdx];
      partialByOriginal[originalIdx] = picked;
      picked.forEach(s => usedSubjects.add(s));
    }
    assignments = partialByOriginal;
  }

  return requirements.map((requirement, index) => ({
    label: requirement.label,
    footnote: requirement.footnote,
    categories: requirement.categories,
    include_subjects: requirement.include_subjects,
    exclude_subjects: requirement.exclude_subjects,
    allowed_core_subjects: requirement.allowed_core_subjects,
    max_category_b: requirement.max_category_b,
    category_b_exempt_subjects: requirement.category_b_exempt_subjects,
    subject_minimum_levels: requirement.subject_minimum_levels,
    category_minimum_levels: requirement.category_minimum_levels,
    category_b_requires_distinction: requirement.category_b_requires_distinction,
    requireCount: requirement.requireCount,
    min_level: requirement.min_level ?? defaultMinLevel,
    matched_subjects: assignments?.[index] || [],
    met: Boolean(assignments?.[index] && assignments[index].length >= (requirement.requireCount || 1)),
  }));
};

const findInvalidElectiveRequirementSubjects = (
  program: JupasProgram,
  results: DSEResults,
  requirements: NormalizedElectiveRequirement[] | undefined,
  defaultMinLevel: number,
  respectProgramCategoryBAllowlist: boolean,
  includeSubjectsMode: 'restrict' | 'extend' = 'restrict',
): string[] => {
  if (!requirements || requirements.length === 0) {
    return [];
  }

  return Object.entries(results)
    .filter(([subject, grade]) => {
      if (!grade || isCoreSubject(subject)) {
        return false;
      }

      return getGradeScore(String(grade), program) >= defaultMinLevel;
    })
    .map(([subject]) => subject)
    .filter(subject => !requirements.some(requirement => {
      const grade = String(results[subject as keyof DSEResults] || '');
      return subjectMatchesElectiveRequirement(subject, grade, requirement, defaultMinLevel, program, respectProgramCategoryBAllowlist, includeSubjectsMode);
    }));
};

/**
 * Check if a subject is allowed based on category limits
 */
export const isSubjectAllowedByCategory = (
  subject: string,
  categoryLimits: CategoryLimits | undefined,
  currentCategoryCounts: { A: number; B: number; C: number }
): boolean => {
  if (!categoryLimits) return true;

  const category = getSubjectCategory(subject);

  // Check allowed_categories
  if (categoryLimits.allowed_categories && !categoryLimits.allowed_categories.includes(category)) {
    return false;
  }

  // Check max_category_b
  if (category === 'B' && categoryLimits.max_category_b !== undefined) {
    if (currentCategoryCounts.B >= categoryLimits.max_category_b) {
      return false;
    }
  }

  // Check max_category_c
  if (category === 'C' && categoryLimits.max_category_c !== undefined) {
    if (currentCategoryCounts.C >= categoryLimits.max_category_c) {
      return false;
    }
  }

  // Check max_combined_bc
  if ((category === 'B' || category === 'C') && categoryLimits.max_combined_bc !== undefined) {
    if (currentCategoryCounts.B + currentCategoryCounts.C >= categoryLimits.max_combined_bc) {
      return false;
    }
  }

  return true;
};

/**
 * Filter subjects based on forbidden groups
 * 1. Removes subjects that conflict with already used subjects
 * 2. Resolves conflicts within the available subjects (keeps highest score)
 */
export const filterForbiddenSubjects = <T extends { subject: string; score: number }>(
  subjects: T[],
  usedSubjects: Set<string>,
  forbiddenGroups: string[][] | undefined
): T[] => {
  if (!forbiddenGroups || forbiddenGroups.length === 0) {
    return subjects;
  }

  // 1. Remove subjects that conflict with used subjects
  let validSubjects = subjects.filter(item => {
    for (const group of forbiddenGroups) {
      if (group.includes(item.subject)) {
        // If any *other* subject in this group is already used, filter this one out
        const otherInGroup = group.find(s => s !== item.subject && usedSubjects.has(s));
        if (otherInGroup) return false;
      }
    }
    return true;
  });

  // 2. Resolve internal conflicts (greedy: keep highest score)
  // Sort by score descending first
  validSubjects.sort((a, b) => b.score - a.score);

  const finalSubjects: T[] = [];
  const processed = new Set<string>(); // Keep track of subjects we've decided on (kept or discarded via group)

  // Create a tracking set for groups to know if a group has been "claimed"
  // Actually, simplified: iterate through sorted list. If subject is not forbidden by a "processed" subject, keep it.

  for (const item of validSubjects) {
    let forbidden = false;

    for (const group of forbiddenGroups) {
      if (group.includes(item.subject)) {
        // Check if any *other* member of this group has already been accepted into finalSubjects
        const conflict = group.find(s => s !== item.subject && finalSubjects.some(fs => fs.subject === s));
        if (conflict) {
          forbidden = true;
          break;
        }
      }
    }

    if (!forbidden) {
      finalSubjects.push(item);
    }
  }

  return finalSubjects;
};

// ============================================================================
// New Data Structure Helpers
// ============================================================================

/**
 * Check if scoring_formula exists and is valid
 */
const isValidScoringFormat = (scoringFormula: ScoringFormula | undefined): scoringFormula is ScoringFormula => {
  return scoringFormula !== undefined &&
    scoringFormula !== null &&
    'subject_rules' in scoringFormula;
};

/**
 * Get allowed categories from scoring formula
 */
const getAllowedCategories = (program: JupasProgram): ('A' | 'B' | 'C')[] | undefined => {
  if (isCuhkMedicineProgramme(program)) {
    return ['A'];
  }

  if (!isValidScoringFormat(program.scoring_formula)) {
    return undefined;
  }

  const rulesWithCategories = getActiveSubjectRules(program.scoring_formula)
    .filter(rule => Array.isArray(rule.categories) && rule.categories.length > 0);
  if (rulesWithCategories.length > 0) {
    const allCategories = new Set<'A' | 'B' | 'C'>();
    rulesWithCategories.forEach(rule => {
      (rule.categories || []).forEach(cat => {
        if (cat === 'core') {
          allCategories.add('A');
        } else if (cat === 'A' || cat === 'B' || cat === 'C') {
          allCategories.add(cat);
        }
      });
    });
    return Array.from(allCategories);
  }
  
  // Get from flexible (required: 0) rules
  const flexibleRules = getActiveSubjectRules(program.scoring_formula).filter(rule => rule.required === 0 || rule.mandatory === 0);
  if (flexibleRules.length > 0) {
    // Combine all categories from flexible rules
    const allCategories = new Set<'A' | 'B' | 'C'>();
    flexibleRules.forEach(rule => {
      (rule.categories || getProgrammeDefaultRuleCategories(program, false)).forEach(cat => {
        if (cat === 'core') {
          allCategories.add('A'); // Core subjects are Category A
        } else if (cat === 'A' || cat === 'B' || cat === 'C') {
          allCategories.add(cat);
        }
      });
    });
    return Array.from(allCategories);
  }
  return undefined;
};

const getProgrammeMerAllowedCategories = (program: JupasProgram): string[] | undefined => {
  const additionalCategories =
    program.program_mer.additional_any_one_subject?.categories
    || program.program_mer.additional_any_two_subjects?.categories;
  if (additionalCategories?.length) {
    return additionalCategories;
  }

  const electiveMinima = program.program_mer.elective_minima as { categories?: string[] } | undefined;
  if (Array.isArray(electiveMinima?.categories) && electiveMinima.categories.length > 0) {
    return electiveMinima.categories;
  }

  const maxPerCategory = (program as JupasProgram & { max_subjects_per_category?: { A?: number; B?: number; C?: number } })
    .max_subjects_per_category;
  const programmeCategoryLimits = getProgrammeCategoryLimits(program);
  if (programmeCategoryLimits?.allowed_categories?.length) {
    const merCategories: string[] = [...programmeCategoryLimits.allowed_categories];
    if (maxPerCategory?.B !== 0 || maxPerCategory?.C !== 0) {
      merCategories.push('core');
    }
    return Array.from(new Set(merCategories));
  }

  if (maxPerCategory?.B === 0 && maxPerCategory?.C === 0) {
    return ['A', 'core'];
  }
  if (maxPerCategory?.C === 0) {
    return ['A', 'B', 'core'];
  }
  if (maxPerCategory?.B === 0) {
    return ['A', 'C', 'core'];
  }

  if (isValidScoringFormat(program.scoring_formula)) {
    const explicitCategories = new Set<string>();
    getActiveSubjectRules(program.scoring_formula).forEach(rule => {
      (rule.categories || []).forEach(category => explicitCategories.add(category));
    });
    if (explicitCategories.size > 0) {
      return Array.from(explicitCategories);
    }
  }

  const allowedCategories = getAllowedCategories(program);
  if (allowedCategories) {
    return allowedCategories;
  }

  return undefined;
};

const subjectMeetsMerCategoryRequirement = (
  subject: string,
  allowedCategories: string[] | undefined,
): boolean => {
  if (!allowedCategories || allowedCategories.length === 0) {
    return true;
  }

  return matchesRuleCategoryList(subject, allowedCategories);
};

const getConsumedMerElectiveSubjects = (
  oneOfResult: MERResult['one_of_electives'] | undefined,
  oneOfGroupsResult: MERResult['one_of_groups'] | undefined,
  specificElectivesResult: MERResult['specific_electives'] | undefined,
): Set<string> => {
  const consumed = new Set<string>();

  if (oneOfResult?.matched_subjects?.length) {
    const requireCount = oneOfResult.requireCount || 1;
    oneOfResult.matched_subjects.slice(0, requireCount).forEach(subject => consumed.add(subject));
  }

  if (oneOfGroupsResult) {
    for (const group of oneOfGroupsResult) {
      const requireCount = group.requireCount || 1;
      (group.matched_subjects || []).slice(0, requireCount).forEach(subject => consumed.add(subject));
    }
  }

  if (specificElectivesResult) {
    for (const [subject, req] of Object.entries(specificElectivesResult)) {
      if (req.met) {
        consumed.add(subject);
      }
    }
  }

  return consumed;
};

const countAdditionalMerSubjectsMet = (
  program: JupasProgram,
  results: DSEResults,
  minLevel: number,
  allowedCategories: string[] | undefined,
  consumedSubjects: Set<string>,
): number => {
  return Object.entries(results).filter(([subj, grade]) => {
    if (subj === 'Citizenship and Social Development') {
      // CSD may count toward additional MER/level requirements for HKSYU.
      if (!doesInstitutionScoreCsd(getUniversityKey(program.jupas_id))) return false;
      if (!isCoreSubject(subj)) return false;
    }
    if (isCoreSubject(subj) && subj !== 'Citizenship and Social Development') return false;
    if (consumedSubjects.has(subj)) return false;
    if (!isProgramAllowedCategoryBSubject(subj, program)) return false;
    if (!subjectMeetsMerCategoryRequirement(subj, allowedCategories)) return false;
    return getGradeScore(String(grade), program) >= minLevel;
  }).length;
};

/**
 * Filter and sort subjects based on category limits
 * Returns subjects sorted by score, filtered by category limits
 */
export const filterSubjectsByCategory = <T extends { subject: string; score: number }>(
  subjects: T[],
  categoryLimits: CategoryLimits | undefined,
  maxCount: number
): T[] => {
  if (!categoryLimits) {
    // No limits, just return top N by score
    return [...subjects]
      .sort((a, b) => b.score - a.score)
      .slice(0, maxCount);
  }

  // Sort by score descending
  const sorted = [...subjects].sort((a, b) => b.score - a.score);

  const result: T[] = [];
  const categoryCounts = { A: 0, B: 0, C: 0 };

  for (const item of sorted) {
    if (result.length >= maxCount) break;

    if (isSubjectAllowedByCategory(item.subject, categoryLimits, categoryCounts)) {
      result.push(item);
      const category = getSubjectCategory(item.subject);
      categoryCounts[category]++;
    }
  }

  return result;
};

/**
 * Get default category limits based on university (from JUPAS ID prefix)
 */
export const getDefaultCategoryLimits = (jupasId: string): CategoryLimits | undefined => {
  if (!jupasId) return undefined;

  const prefix = jupasId.charAt(0);

  // Check for special programs first (Medicine, Dentistry, etc.)
  if (['4501', '4502'].includes(jupasId) ||
    ['6456', '6107'].includes(jupasId) ||
    jupasId.startsWith('6') && jupasId.includes('MBBS')) {
    return {
      max_category_b: 0,
      max_category_c: 0,
      allowed_categories: ['A']
    };
  }

  switch (prefix) {
    case '6': // HKU
      return { max_category_b: 0, max_category_c: 1, allowed_categories: ['A', 'C'] };
    case '4': // CUHK
      return { max_category_b: 0, max_category_c: 1, allowed_categories: ['A', 'C'] };
    case '5': // HKUST
      return { max_category_b: 0, max_category_c: 1, allowed_categories: ['A', 'C'] };
    case '3': // PolyU
      return { max_category_b: 1, max_category_c: 1, allowed_categories: ['A', 'B', 'C'] };
    case '1': // CityU
      return { max_category_b: 0, max_category_c: 1, allowed_categories: ['A', 'C'] };
    case '2': // HKBU
      return { max_category_b: 1, max_category_c: 1, allowed_categories: ['A', 'B', 'C'] };
    case '7': // Lingnan
      return { max_category_b: 0, max_category_c: 2, allowed_categories: ['A', 'C'] };
    case '8': // EdUHK
      return { max_category_b: 1, max_category_c: 1, allowed_categories: ['A', 'B', 'C'] };
    case '9':
    case 'SU': // HKMU
      return { max_category_b: 2, max_category_c: 5, allowed_categories: ['A', 'B', 'C'] };
    case 'S': // SSSDP
      return { max_category_b: 1, max_category_c: 1, allowed_categories: ['A', 'B', 'C'] };
    default:
      return undefined;
  }
};

export const getProgrammeCategoryLimits = (program: JupasProgram | undefined): CategoryLimits | undefined => {
  if (!program) return undefined;

  const jupasId = String(program.jupas_id || '').replace(/^JS/i, '');
  const maxPerCategory = (program as JupasProgram & {
    max_subjects_per_category?: { A?: number; B?: number; C?: number };
  }).max_subjects_per_category;

  const limits: CategoryLimits = {
    ...(getDefaultCategoryLimits(jupasId) || { allowed_categories: ['A', 'B', 'C'] }),
  };

  if (maxPerCategory?.B !== undefined) {
    limits.max_category_b = maxPerCategory.B;
  }
  if (maxPerCategory?.C !== undefined) {
    limits.max_category_c = maxPerCategory.C;
  }
  if (program.max_combined_bc !== undefined) {
    limits.max_combined_bc = program.max_combined_bc;
    if (maxPerCategory?.B === undefined) {
      limits.max_category_b = program.max_combined_bc;
    }
    if (maxPerCategory?.C === undefined) {
      limits.max_category_c = program.max_combined_bc;
    }
  }

  const allowed = new Set<'A' | 'B' | 'C'>(limits.allowed_categories || ['A', 'B', 'C']);
  if (limits.max_category_b === 0) {
    allowed.delete('B');
  }
  if (limits.max_category_c === 0) {
    allowed.delete('C');
  }
  limits.allowed_categories = Array.from(allowed);

  return limits;
};

// ============================================================================
// Types
// ============================================================================

export interface ScoreTriple {
  current: string;
  min: string;
  max: string;
}

export interface ElectiveScore {
  subject: string;
  grade: string;
  score: number;
  baseScore: number;
  weighting: number;
}

export interface SubjectBreakdownItem extends ElectiveScore {
  mandatory?: boolean;
  slotType?: string;
  category?: 'A' | 'B' | 'C';
  counted?: boolean;
  description?: string;
  replacementSourceSubject?: string;
  replacementSourceGrade?: string;
  replacementSourceRawScore?: number;
  replacementTargetOriginalRawScore?: number;
  replacementRuleNote?: string;
  replacementBlendTargetRatio?: number;
  replacementBlendSourceRatio?: number;
}

function resolveInstitutionGradeValues(instKey?: string): Record<string, any> {
  if (!instKey) {
    return {};
  }

  let instData = (INSTITUTIONS_DATA as any)[instKey];

  if (!instData) {
    const instKeyUpper = instKey.toUpperCase();
    for (const [, data] of Object.entries(INSTITUTIONS_DATA as any)) {
      if ((data as any).inst_code?.toUpperCase() === instKeyUpper) {
        instData = data;
        break;
      }
    }
  }

  if (!instData && instKey.startsWith('S')) {
    instData = (INSTITUTIONS_DATA as any)['S'];
  }

  return instData?.grade_values || {};
}

function mergeGradeValues(
  baseGradeValues: Record<string, any>,
  programmeGradeValues?: Record<string, any>,
): Record<string, any> {
  if (!programmeGradeValues || Object.keys(programmeGradeValues).length === 0) {
    return baseGradeValues;
  }

  const merged: Record<string, any> = { ...baseGradeValues };
  for (const [key, value] of Object.entries(programmeGradeValues)) {
    if (
      value
      && typeof value === 'object'
      && !Array.isArray(value)
      && merged[key]
      && typeof merged[key] === 'object'
      && !Array.isArray(merged[key])
    ) {
      merged[key] = { ...merged[key], ...value };
    } else {
      merged[key] = value;
    }
  }

  return merged;
}

// ============================================================================
// Grade Values & Conversions
// ============================================================================

/**
 * Get grade values based on university key and program ID
 */
export function getGradeValues(instKey?: string, jupasId?: string, programmeGradeValues?: Record<string, number>): Record<string, number> {
  const programmeId = jupasId?.replace(/^JS/i, '').toUpperCase();
  const providerKey = programmeId?.match(/^S[A-Z]/)?.[0];
  const institutionGradeValues = resolveInstitutionGradeValues(providerKey || instKey);
  const mergedGradeValues = mergeGradeValues(institutionGradeValues, programmeGradeValues as Record<string, any> | undefined);
  if (Object.keys(mergedGradeValues).length > 0) {
    return mergedGradeValues as Record<string, number>;
  }

  throw new Error(`No grade_values found for programme: ${jupasId || 'unknown'} (institution: ${providerKey || instKey})`);
}

/**
 * Get grade score based on grade and programme's grade_values
 * Priority: Programme grade_values > Institution grade_values > Default scale
 */
export const getGradeScore = (grade: string, program?: JupasProgram, subject?: string): number => {
  // Get programme-specific grade_values if available
  let gradeValues: any;

  if (program) {
    const instKey = getUniversityKey(String(program.jupas_id)) || undefined;

    const programmeGradeValues = program.scoring_formula?.grade_values || program.grade_values;
    gradeValues = getGradeValues(instKey || undefined, String(program.jupas_id), programmeGradeValues as Record<string, number> | undefined);
  }

  const normalizeGradeForSubject = (rawGrade: string, subjectName?: string): string => {
    const g = String(rawGrade ?? '').trim();
    if (!subjectName) return g;
    const s = String(subjectName).trim().toUpperCase();

    // Category C: Korean, convert all variants to 'Grade X' only
    if (s === 'KOREAN') {
      const match = g.match(/^(G|LEVEL)?\s*([3-6])$/i);
      if (match?.[2]) {
        return `Grade ${match[2]}`;
      }
    }

    return g;
  };

  const resolveGradeValue = (targetGrade: string): number => {
    if (!gradeValues) return 0;

    const normalizedGrade = normalizeGradeForSubject(targetGrade, subject);

    if (typeof gradeValues[normalizedGrade] === 'number') {
      return gradeValues[normalizedGrade] as number;
    }

    if (subject && gradeValues[subject] && typeof gradeValues[subject] === 'object') {
      const subjectMap = gradeValues[subject] as Record<string, number>;
      if (typeof subjectMap[normalizedGrade] === 'number') {
        return subjectMap[normalizedGrade];
      }
    }

    if (gradeValues.default && typeof gradeValues.default === 'object') {
      const defaultMap = gradeValues.default as Record<string, number>;
      if (typeof defaultMap[normalizedGrade] === 'number') {
        return defaultMap[normalizedGrade];
      }
    }

    return 0;
  };

  const subjectCategory = subject ? getSubjectCategory(subject) : undefined;

  if (subjectCategory === 'B') {
    if (grade === 'A') {
      return resolveGradeValue('Att') || resolveGradeValue('A') || 0;
    }

    if (grade === 'U') {
      return resolveGradeValue('Unatt') || resolveGradeValue('U') || 0;
    }
  }

  // Specific handling for CSD (Citizenship)
  if (grade === 'Attained') {
    return resolveGradeValue('Attained') || 0;
  }
  if (grade === 'A') {
    return resolveGradeValue('A') || 0;
  }

  return resolveGradeValue(grade);
};

const meetsJS4502AdditionalRequirements = (program: JupasProgram, results: DSEResults): boolean => {
  const bestSixSubjects = Object.entries(results)
    .filter(([subject, grade]) => subject !== 'Citizenship and Social Development' && Boolean(grade))
    .map(([subject, grade]) => ({
      grade: String(grade),
      score: getGradeScore(String(grade), program, subject),
    }))
    .sort((left, right) => right.score - left.score)
    .slice(0, 6);

  const fiveStarStarCount = bestSixSubjects.filter(({ grade }) => grade === '5**').length;
  const totalGradePoints = bestSixSubjects.reduce((total, subject) => total + subject.score, 0);
  return bestSixSubjects.length === 6 && totalGradePoints >= 40 && fiveStarStarCount >= 4;
};

// ==============================================================================
// Note: getCategoryCLanguageScore, getMaxCategoryCLanguageScore, and gradeToLevel  
// have been removed. All grade values (including Category C languages) should now
// be provided in the grade_values property of institutions and programmes.
// Use getGradeValues() or getGradeScore() with direct gradeValues[grade] lookups.
// ==============================================================================



// ============================================================================
// Maximum Subject Score Calculation
// ============================================================================

/**
 * Calculate the maximum possible score for a subject based on its category
 * @param subjectName - Name of the subject
 * @param instKey - University key (e.g., 'hku', 'cuhk')
 * @param jupasId - Programme JUPAS ID (without JS prefix)
 * @param programmeGradeValues - Optional programme-specific grade values
 * @param weighting - Optional subject weighting (default: 1.0)
 * @returns Maximum weighted score for the subject
 */
export function calcSubjectMaxScore(
  subjectName: string,
  instKey: string,
  jupasId: string,
  programmeGradeValues?: Record<string, number>,
  weighting: number = 1.0
): number {
  // Get grade values with priority: programme > institution > default
  const gradeValues = getGradeValues(instKey, jupasId, programmeGradeValues);

  const category = getSubjectCategory(subjectName);
  const canonicalSubject = canonicalizeSubjectName(subjectName);
  let maxBaseScore = 0;

  // Determine max grade based on category and subject-specific overrides
  if (canonicalSubject === 'Citizenship and Social Development') {
    // Use gradeValues for Attained and U for CSD universally, regardless of institution
    maxBaseScore = gradeValues['Attained'] ?? 2;
  } else if (category === 'A') {
    // Category A (HKDSE) - max grade is "5**"
    maxBaseScore = gradeValues['5**'] || 0;
  } else if (category === 'B') {
    // Category B (Applied Learning) - max grade is "Dist II"
    maxBaseScore = gradeValues['Dist II'] || 0;
  } else if (category === 'C') {
    // Category C (Languages) - determine max grade by subject
    const subjectUpper = subjectName.toUpperCase();
    if (["FRENCH", "GERMAN", "SPANISH"].includes(subjectUpper)) {
      maxBaseScore = gradeValues['C2'] || 0;
    } else if (subjectUpper === "KOREAN") {
      maxBaseScore = gradeValues['Grade 6'] || 0;
    } else if (subjectUpper === "JAPANESE") {
      maxBaseScore = gradeValues['N1'] || 0;
    } else if (subjectUpper === "URDU") {
      maxBaseScore = gradeValues['A++'] || gradeValues['A+'] || gradeValues['A'] || 0;
    }
  }

  return maxBaseScore * weighting;
}

/**
 * Calculate the maximum possible score for a programme based on its scoring_formula
 * Considers subject weights and limitations defined in scoring_formula
 * 
 * @param jupasProgram - The JUPAS programme
 * @param instKey - University identifier
 * @returns Maximum possible score for the programme
 */
export function calcProgMaxScore(
  jupasProgram: JupasProgram | undefined,
  instKey: string
): number {
  if (!jupasProgram) return 0;

  // Get programme-specific grade_values
  let programmeGradeValues = jupasProgram?.grade_values;
  if (!programmeGradeValues && jupasProgram?.scoring_formula?.grade_values) {
    programmeGradeValues = jupasProgram.scoring_formula.grade_values;
  }

  const jupasId = jupasProgram?.jupas_id?.replace(/^JS/i, '') || '';
  const gradeValues = getGradeValues(instKey, jupasId, programmeGradeValues);

  // Calculate based on subject_rules
  if (!isValidScoringFormat(jupasProgram.scoring_formula)) {
    return 0;
  }

  const scoringFormula = jupasProgram.scoring_formula;
  const activeSubjectRules = getActiveSubjectRules(scoringFormula);
  let total = 0;

  // Track subjects used in required rules - they can't be used again in flexible rules
  const usedSubjects = new Set<string>();

  // First pass: process required rules and track used subjects
  const requiredRules = activeSubjectRules.filter(rule => rule.required === 1 || rule.mandatory === 1);
  requiredRules.forEach(rule => {
    const subjectWeights = rule.subject_weights || {};
    const subjectNames = Object.keys(subjectWeights);
    const baseScore = gradeValues["5**"] || 0;

    for (let i = 0; i < rule.count; i++) {
      // For required rules, use the first subject's weight (there's typically just one)
      const weight = subjectNames.length > 0 ? subjectWeights[subjectNames[0]] : 1.0;
      total += baseScore * weight;

      // Mark subjects as used
      subjectNames.forEach(subj => usedSubjects.add(subj));
    }
  });

  // Second pass: process flexible rules, excluding weights for used subjects
  const flexibleRules = activeSubjectRules.filter(rule => rule.required === 0 || rule.mandatory === 0);
  flexibleRules.forEach(rule => {
    const subjectWeights = rule.subject_weights || {};
    const baseScore = gradeValues["5**"] || 0;

    // Determine the base weight (default or any_one_subject)
    let baseWeight = 1.0;
    if (subjectWeights['any_one_subject'] !== undefined) {
      baseWeight = subjectWeights['any_one_subject'];
    }

    // Get weights for subjects that haven't been used yet
    const availableWeights = Object.entries(subjectWeights)
      .filter(([key]) => key !== 'any_one_subject' && !usedSubjects.has(key))
      .map(([, value]) => value);

    // The max weight is the higher of baseWeight and any available specific weights
    const maxWeight = availableWeights.length > 0
      ? Math.max(baseWeight, ...availableWeights)
      : baseWeight;

    for (let i = 0; i < rule.count; i++) {
      total += baseScore * maxWeight;
    }
  });

  return total;
}

/**
 * Calculate the maximum possible BASE score for a programme (without bonus subjects).
 * This is used internally by calcHighestAttainableScore and calculateScore (for HKUST bonus basis).
 */
export function calcHighestAttainableScoreBase(
  jupasProgram: JupasProgram | undefined,
  instKey: string
): number {
  if (!jupasProgram) return 0;

  // Get programme-specific grade_values
  let programmeGradeValues = jupasProgram?.grade_values;
  if (!programmeGradeValues && jupasProgram?.scoring_formula?.grade_values) {
    programmeGradeValues = jupasProgram.scoring_formula.grade_values;
  }

  const jupasId = jupasProgram?.jupas_id?.replace(/^JS/i, '') || '';
  const gradeValues = getGradeValues(instKey, jupasId, programmeGradeValues);
  const baseScore = gradeValues["5**"] || 8.5;

  // Handle old array format scoring_formula (type: "CORE", "REMAINING", etc.)
  if (Array.isArray(jupasProgram.scoring_formula)) {
    let total = 0;
    for (const slot of jupasProgram.scoring_formula) {
      if (slot.type === "CORE") {
        const weight = slot.weight || 1.0;
        total += baseScore * weight;
      } else if (slot.type === "REMAINING" || slot.type === "ELECTIVE" || slot.type === "BEST") {
        const count = slot.count || 1;
        const defaultWeight = slot.weight_rules?.default_weight || slot.weight || 1.0;
        total += baseScore * defaultWeight * count;
      }
    }
    return total > 0 ? total : baseScore * (jupasProgram.max_num_of_subjects || 5);
  }

  // Calculate based on new subject_rules format
  if (!isValidScoringFormat(jupasProgram.scoring_formula)) {
    return baseScore * (jupasProgram.max_num_of_subjects || 5);
  }

  const scoringFormula = jupasProgram.scoring_formula;
  const activeSubjectRules = getActiveSubjectRules(scoringFormula);
  let total = 0;
  const usedSubjects = new Set<string>();

  const requiredRules = activeSubjectRules.filter(rule => rule.required === 1 || rule.required === true || rule.mandatory === 1);
  requiredRules.forEach(rule => {
    const subjectWeights = rule.subject_weights || {};
    const anyOneSubjectWeight = subjectWeights['any_one_subject'] ?? 1.0;
    const specificWeights = Object.entries(subjectWeights)
      .filter(([key]) => key !== 'any_one_subject' && key !== 'any_category_a_elective')
      .map(([key, val]) => ({ key, val }))
      .filter(({ val }) => val > anyOneSubjectWeight)
      .sort((a, b) => b.val - a.val);

    const count = rule.count ?? 1;
    for (let i = 0; i < count; i++) {
      const weight = i < specificWeights.length ? specificWeights[i].val : anyOneSubjectWeight;
      total += baseScore * weight;
    }
    specificWeights.forEach(({ key }) => usedSubjects.add(key));
  });

  const numOfSubjects = scoringFormula.num_of_subjects || 5;
  let allocatedSlots = requiredRules.reduce((sum: number, rule: any) => {
    const subjectCount = Object.keys(rule.subject_weights || {}).filter((k: string) => k !== 'any_one_subject').length;
    return sum + (rule.count ?? (subjectCount || 1));
  }, 0);

  const flexibleRules = activeSubjectRules.filter(rule => rule.required !== 1 && rule.required !== true && rule.mandatory !== 1);
  flexibleRules.forEach(rule => {
    const remainingSlots = Math.max(0, numOfSubjects - allocatedSlots);
    if (remainingSlots === 0) {
      return;
    }

    const subjectWeights = rule.subject_weights || {};
    const anyOneSubjectWeight = subjectWeights['any_one_subject'] ?? 1.0;
    const specificWeights = Object.entries(subjectWeights)
      .filter(([key]) => key !== 'any_one_subject' && key !== 'any_category_a_elective' && !usedSubjects.has(key))
      .map(([, val]) => val)
      .filter(val => val > anyOneSubjectWeight)
      .sort((a, b) => b - a);

    const requestedCount = rule.count || remainingSlots;
    const count = Math.min(requestedCount, remainingSlots);
    for (let i = 0; i < count; i++) {
      const weight = i < specificWeights.length ? specificWeights[i] : anyOneSubjectWeight;
      total += baseScore * weight;
    }
    allocatedSlots += count;
  });

  return total;
}

/**
 * Get the bonus_subjects rules for a programme.
 * Checks programme-level first (scoring_formula.bonus_subjects), then institution-level.
 */
export function getBonusSubjectRules(
  jupasProgram: JupasProgram | undefined,
  instKey: string
): any[] | undefined {
  // Programme-level override takes priority
  if (jupasProgram?.scoring_formula?.bonus_subjects?.length) {
    return jupasProgram.scoring_formula.bonus_subjects;
  }
  // Fall back to institution-level default
  let institutionData = (INSTITUTIONS_DATA as any)[instKey];
  if (!institutionData) {
    const instKeyUpper = instKey.toUpperCase();
    for (const instEntry of Object.values(INSTITUTIONS_DATA as any) as any[]) {
      if (instEntry.inst_code?.toUpperCase() === instKeyUpper) {
        institutionData = instEntry;
        break;
      }
    }
  }
  if (!institutionData && jupasProgram?.jupas_id) {
    const prefix = getUniversityKey(jupasProgram.jupas_id);
    if (prefix) {
      institutionData = (INSTITUTIONS_DATA as any)[prefix];
    }
  }
  return institutionData?.bonus_subjects;
}

const HKUST_BONUS_GER_PROGRAMMES = new Set(['5101', '5102']);

type ScoringBreakdownSubject = {
  subject: string;
  slotType?: string;
  counted?: boolean;
};

const getScoringBreakdownSubjectsByRole = (
  breakdown: ScoringBreakdownSubject[],
  role: 'main' | 'bonus',
): string[] => {
  return breakdown
    .filter(item => {
      const isBonus = item.slotType === 'BONUS';
      if (role === 'bonus') {
        return isBonus && item.counted !== false;
      }
      return !isBonus && item.counted !== false;
    })
    .map(item => item.subject);
};

/**
 * JS5101/JS5102 use best-5 Category A scoring with a 6th bonus subject (Cat A/B/C).
 * When that pattern applies, GER 332A33 should treat the bonus subject as satisfying
 * elective requirements instead of invalidating them.
 */
const getHkustBonusGerReliefContext = (
  program: JupasProgram,
  breakdown: ScoringBreakdownSubject[],
  results: DSEResults,
  instKey: string,
): {
  bonusSubjects: string[];
} | null => {
  if (!HKUST_BONUS_GER_PROGRAMMES.has(program.jupas_id)) {
    return null;
  }

  const mainSubjects = getScoringBreakdownSubjectsByRole(breakdown, 'main');
  if (mainSubjects.length === 0 || !mainSubjects.every(subject => getSubjectCategory(subject) === 'A')) {
    return null;
  }

  const bonusRules = getBonusSubjectRules(program, instKey);
  if (!bonusRules?.length) {
    return null;
  }

  const bonusSubjects = getScoringBreakdownSubjectsByRole(breakdown, 'bonus').filter(subject => {
    const grade = results[subject as keyof DSEResults];
    if (!grade) return false;
    const rawScore = getGradeScore(String(grade), program);
    return bonusRules.some(rule => {
      const allowedCategories = rule.categories || ['A', 'B', 'C', 'core'];
      if (!matchesRuleCategoryList(subject, allowedCategories)) return false;
      return rawScore >= (rule.min_level || 0);
    });
  });

  if (bonusSubjects.length === 0) {
    return null;
  }

  return {
    bonusSubjects,
  };
};

/**
 * Calculate the maximum possible score for a programme based on its scoring_formula.
 * Includes bonus subject scoring (6th/7th subject bonus) if configured.
 */
export function calcHighestAttainableScore(
  jupasProgram: JupasProgram | undefined,
  instKey: string
): number {
  const baseMax = calcHighestAttainableScoreBase(jupasProgram, instKey);
  if (!jupasProgram || baseMax === 0) return baseMax;

  const bonusRules = getBonusSubjectRules(jupasProgram, instKey);
  if (!bonusRules?.length) return baseMax;

  let programmeGradeValues = jupasProgram?.grade_values;
  if (!programmeGradeValues && jupasProgram?.scoring_formula?.grade_values) {
    programmeGradeValues = jupasProgram.scoring_formula.grade_values;
  }
  const jupasId = jupasProgram?.jupas_id?.replace(/^JS/i, '') || '';
  const gradeValues = getGradeValues(instKey, jupasId, programmeGradeValues);

  let bonusMax = 0;
  for (const bonus of bonusRules) {
    if (bonus.formula === 'percentage_of_max') {
      const percentageBase = bonus.base_score ?? baseMax;
      bonusMax += bonus.rate * percentageBase * bonus.count;
    } else if (bonus.formula === 'percentage_of_weighted_score') {
      const maxGradeScore = gradeValues['5**'] || 8.5;
      const allWeights: number[] = [];
      const scoringFormula = jupasProgram.scoring_formula;
      if (bonus.bonus_subject_weights && typeof bonus.bonus_subject_weights === 'object') {
        for (const val of Object.values(bonus.bonus_subject_weights)) {
          if (typeof val === 'number') {
            allWeights.push(val);
          }
        }
      } else if (scoringFormula?.subject_rules) {
        for (const rule of scoringFormula.subject_rules) {
          for (const [key, val] of Object.entries(rule.subject_weights || {})) {
            if (key !== 'any_one_subject' && typeof val === 'number') {
              allWeights.push(val);
            }
          }
        }
      }
      allWeights.sort((a, b) => b - a);
      const numMain = scoringFormula?.num_of_subjects || 5;
      for (let i = 0; i < bonus.count; i++) {
        const bonusWeight = allWeights[numMain + i] ?? allWeights[allWeights.length - 1] ?? 1.0;
        bonusMax += bonus.rate * maxGradeScore * bonusWeight;
      }
    } else {
      // Default: treat as simple additive bonus
      const maxGradeScore = gradeValues['5**'] || 8.5;
      bonusMax += maxGradeScore * bonus.count;
    }
  }

  return baseMax + bonusMax;
}

/**
 * Select and prepare the best subjects for display with all necessary metadata
 * Handles required subjects, calculates max scores, and sorts appropriately
 * 
 * @param program - The JUPAS programme
 * @param jupasId - The JUPAS programme ID
 * @param breakdownDetails - Array of subject breakdown items with scores
 * @returns Array of subjects with complete display information, sorted with required first
 */
export function selectBestSubjects(
  program: JupasProgram | undefined,
  jupasId: string,
  breakdownDetails: SubjectBreakdownItem[]
): Array<{
  subject: string;
  grade: string;
  baseScore: number;
  score: number;
  weighting: number;
  category?: string;
  mandatory: boolean;
  slotType?: string;
  maxScore: number;
  counted?: boolean;
  description?: string;
  replacementSourceSubject?: string;
  replacementSourceGrade?: string;
  replacementSourceRawScore?: number;
  replacementTargetOriginalRawScore?: number;
  replacementRuleNote?: string;
  replacementBlendTargetRatio?: number;
  replacementBlendSourceRatio?: number;
}> {
  // Get the correct grade values (programme-specific or institution-specific)
  const instKey = getUniversityKey(jupasId);
  const cleanJupasId = jupasId.replace(/^JS/, '');
  // Separate required and non-required subjects (for scoring, not just eligibility)
  // Only treat as required if the subject is required for scoring (not just for eligibility)
  // For generality, requiredSubjects is built from subject_rules with required=true/1 or mandatory=1
  const institutionData = (INSTITUTIONS_DATA as any)[instKey];
  const forbiddenGroups = getScoreSelectionForbiddenGroups(program, institutionData);
  const conflictsWithSelectedSubjects = (subject: string, selectedSubjects: string[]): boolean => {
    if (!forbiddenGroups || forbiddenGroups.length === 0) {
      return false;
    }

    return hasForbiddenConflictForSubjectGroups(subject, new Set(selectedSubjects), forbiddenGroups);
  };
  const scoringRequiredSubjects = new Set<string>();
  if (program?.scoring_formula?.subject_rules) {
    program.scoring_formula.subject_rules
      .filter(rule => rule.required === true || rule.required === 1 || rule.mandatory === 1)
      .forEach(rule => {
        if (rule.subject_weights) {
          Object.keys(rule.subject_weights).forEach((subject: string) => {
            if (subject !== 'any_one_subject') {
              scoringRequiredSubjects.add(canonicalizeSubjectName(subject));
            }
          });
        }
      });
  }

  // Build perRequiredSubjects: subjects required by one_of_electives, one_of_groups, specific_electives (for PER/MER display)
  const perRequiredSubjects = new Set<string>();
  if (program?.program_mer) {
    // one_of_electives: all subjects in the list
    if (program.program_mer.one_of_electives?.subjects) {
      for (const subj of program.program_mer.one_of_electives.subjects) {
        perRequiredSubjects.add(canonicalizeSubjectName(subj));
      }
    }
    // one_of_groups: all subjects in all groups
    if (Array.isArray(program.program_mer.one_of_groups)) {
      for (const group of program.program_mer.one_of_groups) {
        if (Array.isArray(group.subjects)) {
          for (const subj of group.subjects) {
            perRequiredSubjects.add(canonicalizeSubjectName(subj));
          }
        }
      }
    }
    // specific_electives: all keys
    if (program.program_mer.specific_electives) {
      for (const subj of Object.keys(program.program_mer.specific_electives)) {
        perRequiredSubjects.add(canonicalizeSubjectName(subj));
      }
    }
  }


  // For scoring, only force subjects required by the scoring formula (scoringRequiredSubjects)
  // All other subjects (including those only required for eligibility) compete for top N by score
  const numSubjects = program?.scoring_formula?.num_of_subjects || 5;
  // Remove duplicates and ensure all items have a score
  const allItems = breakdownDetails
    .filter(item => item.counted !== false || item.slotType === 'BONUS')
    .map(item => ({ ...item, score: item.score || 0 }));
  const bonusItems = allItems.filter(item => item.slotType === 'BONUS');
  const mainItems = allItems.filter(item => item.slotType !== 'BONUS');

  const hasSubjectRules = Array.isArray(program?.scoring_formula?.subject_rules) && program?.scoring_formula?.subject_rules.length > 0;
  let combinedItems: typeof allItems = [];

  if (hasSubjectRules) {
    // Preserve the programme-specific subject selection produced by calculateScore.
    // The breakdown already reflects scoring_formula-based choices, so do not reselect.
    combinedItems = [...mainItems];
  } else {
    // Default: force all scoringRequiredSubjects
    const forcedSubjects = mainItems.filter(item => scoringRequiredSubjects.has(item.subject));
    const flexibleSubjects = mainItems.filter(item => !scoringRequiredSubjects.has(item.subject));
    flexibleSubjects.sort((a, b) => b.score - a.score);
    combinedItems.push(...forcedSubjects);
    const selectedSubjects = combinedItems.map(item => item.subject);
    for (const item of flexibleSubjects) {
      if (combinedItems.length < numSubjects) {
        if (!combinedItems.some(i => i.subject === item.subject) && !conflictsWithSelectedSubjects(item.subject, selectedSubjects)) {
          combinedItems.push(item);
          selectedSubjects.push(item.subject);
        }
      }
    }
    if (combinedItems.length > numSubjects) {
      combinedItems = combinedItems.slice(0, numSubjects);
    }
  }

  combinedItems.push(...bonusItems);
  // For HKU, only mark subjects from the first required rule as mandatory for scoring
  // For HKU, only mark English and Mathematics as mandatory for display
  let mandatorySet = new Set<string>();
  if (instKey === 'hku' && program?.scoring_formula?.subject_rules?.length) {
    const firstRequiredRule = program.scoring_formula.subject_rules.find(rule => rule.required === true || rule.required === 1 || rule.mandatory === 1);
    if (firstRequiredRule && firstRequiredRule.subject_weights) {
      mandatorySet = new Set(Object.keys(firstRequiredRule.subject_weights).map(subject => canonicalizeSubjectName(subject)));
    }
  } else {
    mandatorySet = scoringRequiredSubjects;
  }
  // Sort for display: required first, then by score
  combinedItems = combinedItems.sort((a, b) => {
    if (a.slotType === 'BONUS' && b.slotType !== 'BONUS') return 1;
    if (a.slotType !== 'BONUS' && b.slotType === 'BONUS') return -1;
    const aMandatory = mandatorySet.has(a.subject);
    const bMandatory = mandatorySet.has(b.subject);
    if (aMandatory && !bMandatory) return -1;
    if (!aMandatory && bMandatory) return 1;
    return b.score - a.score;
  });

  // Prepare display list with metadata
  // Get programmeGradeValues for maxScore calculation
  const programmeGradeValues = program?.scoring_formula?.grade_values || program?.grade_values;
  const displayList = combinedItems.map(item => {
    let weighting = item.weighting || 1;
    // For HKU, if the subject is a Category A elective not explicitly listed in subject_weights, set weighting to 1.0 for the 5th slot
    if (
      instKey === 'hku' &&
      program?.scoring_formula?.subject_rules?.length &&
      getSubjectCategory(item.subject) === 'A' &&
      !['English Language', 'Mathematics'].includes(item.subject)
    ) {
      // Check if subject is not in any subject_weights of required or flexible rules (other than 'any_category_a_elective')
      const allExplicitSubjects = program.scoring_formula.subject_rules
        .flatMap(rule => Object.keys(rule.subject_weights || {}))
        .filter(s => !s.startsWith('any_'));
      if (!allExplicitSubjects.includes(item.subject)) {
        weighting = 1.0;
      }
    }
    return {
      subject: item.subject,
      grade: item.grade,
      baseScore: item.baseScore || 0,
      score: item.score || 0,
      weighting,
      category: item.category,
      mandatory: mandatorySet.has(item.subject),
      slotType: item.slotType,
      description: item.description,
      replacementSourceSubject: item.replacementSourceSubject,
      replacementSourceGrade: item.replacementSourceGrade,
      replacementSourceRawScore: item.replacementSourceRawScore,
      replacementTargetOriginalRawScore: item.replacementTargetOriginalRawScore,
      replacementRuleNote: item.replacementRuleNote,
      replacementBlendTargetRatio: item.replacementBlendTargetRatio,
      replacementBlendSourceRatio: item.replacementBlendSourceRatio,
      maxScore: item.slotType === 'BONUS'
        ? item.score || 0
        : calcSubjectMaxScore(
          item.subject,
          instKey || '',
          cleanJupasId,
          programmeGradeValues,
          weighting
        )
    };
  });

  return displayList;
}

// ============================================================================
// Minimum Requirements Checking
// ============================================================================
export function meetsMinimumRequirements(
  userScores: Record<string, ScoreTriple>,
  selectedElectives: string[],
  programmeRequirements: {
    "English Language": string;
    "Chinese Language": string;
    "Mathematics": string;
    "Citizenship and Social Development": string;
    elective1: string;
    elective2: string;
  }
): boolean {
  const gradeValues: Record<string, number> = {
    "5**": 7, "5*": 6, "5": 5, "4": 4, "3": 3, "2": 2, "1": 1, "U": 0
  };

  // Parse requirement string (e.g., "3" or "2" or "Attained" or "-")
  const parseRequirement = (req: string): number | string => {
    if (!req || req === "" || req === "-") return 0; // Missing data - assume no requirement
    if (req === "Attained" || req === "Unattained") return req;
    if (req === "N/A") return 0; // No requirement
    return gradeValues[req] || 0;
  };

  // Check English
  const engReq = parseRequirement(programmeRequirements["English Language"]);
  if (typeof engReq === 'number' && engReq > 0) {
    const engGrade = userScores["English Language"]?.max || "";
    if ((gradeValues[engGrade] || 0) < engReq) return false;
  }

  // Check Chinese
  const chinReq = parseRequirement(programmeRequirements["Chinese Language"]);
  if (typeof chinReq === 'number' && chinReq > 0) {
    const chinGrade = userScores["Chinese Language"]?.max || "";
    if ((gradeValues[chinGrade] || 0) < chinReq) return false;
  }

  // Check Math
  const mathReq = parseRequirement(programmeRequirements["Mathematics"]);
  if (typeof mathReq === 'number' && mathReq > 0) {
    const mathGrade = userScores["Mathematics"]?.max || "";
    if ((gradeValues[mathGrade] || 0) < mathReq) return false;
  }

  // Check CSD
  const csdReq = programmeRequirements["Citizenship and Social Development"];
  if (csdReq === "Attained") {
    const csdGrade = userScores["Citizenship and Social Development"]?.max || "";
    if (csdGrade !== "Attained") return false;
  }

  // Check electives - get the minimum required grade for each elective
  const elective1Req = parseRequirement(programmeRequirements.elective1);
  const elective2Req = parseRequirement(programmeRequirements.elective2);

  // Get user's elective grades sorted descending (best first)
  const electiveGrades = selectedElectives
    .map(subjectName => {
      const grade = userScores[subjectName]?.max || "";
      return gradeValues[grade] || 0;
    })
    .sort((a, b) => b - a);

  // Check if best 2 electives meet requirements
  if (typeof elective1Req === 'number' && elective1Req > 0) {
    if (!electiveGrades[0] || electiveGrades[0] < elective1Req) return false;
  }
  if (typeof elective2Req === 'number' && elective2Req > 0) {
    if (!electiveGrades[1] || electiveGrades[1] < elective2Req) return false;
  }

  return true;
}

/**
 * Check basic JUPAS eligibility (3322+2) using MAX expected grades
 */
export function checkBasicEligibility(
  userScores: Record<string, ScoreTriple>,
  selectedElectives: string[]
): boolean {
  const gradeValues: Record<string, number> = {
    "5**": 7, "5*": 6, "5": 5, "4": 4, "3": 3, "2": 2, "1": 1, "U": 0
  };

  // Check 3322 requirement using MAX expected grades
  const chin = userScores["Chinese Language"]?.max;
  const eng = userScores["English Language"]?.max;
  const math = userScores["Mathematics"]?.max;
  const csd = userScores["Citizenship and Social Development"]?.max;

  if (!chin || !eng || !math || csd !== "Attained") return false;

  const chinGrade = gradeValues[chin] || 0;
  const engGrade = gradeValues[eng] || 0;
  const mathGrade = gradeValues[math] || 0;

  if (chinGrade < 3 || engGrade < 3 || mathGrade < 2) return false;

  // Check at least 2 electives with grade 2 or above using MAX expected
  const electiveGrades = selectedElectives
    .map(subjectName => {
      const score = userScores[subjectName];
      const grade = score?.max || "";

      // For Category C languages, use minimum threshold of 2 equivalent
      if (["French", "German", "Spanish", "Korean", "Japanese", "Urdu"].includes(subjectName)) {
        return grade && grade !== "N/A" ? 2 : 0;
      }

      return grade ? gradeValues[grade] || 0 : 0;
    })
    .filter(grade => grade >= 2);

  return electiveGrades.length >= 2;
}

// ============================================================================
// User Score Calculation
// ============================================================================

/**
 * Calculate user's total score for a programme
 * 
 * @param userScores - User's scores for each subject (with min/current/max)
 * @param selectedElectives - List of selected elective subjects
 * @param jupasProgram - The JUPAS programme data
 * @param instKey - The university key (e.g., 'hku', 'cuhk')
 * @param scoreType - Which score type to use ('min', 'current', or 'max')
 * @returns Total calculated score
 */
export function calculateUserScore(
  userScores: Record<string, ScoreTriple>,
  selectedElectives: string[],
  jupasProgram: JupasProgram | undefined,
  instKey: string,
  scoreType: 'min' | 'current' | 'max' = 'current'
): number {
  if (!jupasProgram) return 0;

  // Use the main calculateScore function with new format
  const dseResults: DSEResults = {};
  Object.keys(userScores).forEach(subject => {
    const score = userScores[subject];
    if (score?.[scoreType]) {
      dseResults[subject] = score[scoreType] as Grade;
    }
  });
  const result = calculateScore(jupasProgram, dseResults);
  return result.total;
}

// ============================================================================
// Maximum Possible Score Calculation
// ============================================================================

/**
 * Calculate maximum possible score for a programme (all highest possible subjects with weightings)
 */
export function calcMaxProgScore(
  jupasProgram: JupasProgram | undefined,
  instKey?: string
): number {
  if (!jupasProgram) return 0;

  // Get programme-specific grade_values
  let programmeGradeValues = jupasProgram?.grade_values;
  if (!programmeGradeValues && jupasProgram?.scoring_formula?.grade_values) {
    programmeGradeValues = jupasProgram.scoring_formula.grade_values;
  }

  const jupasId = jupasProgram?.jupas_id?.replace(/^JS/i, '') || '';
  let total = 0;
  const gradeValues = getGradeValues(instKey, jupasId, programmeGradeValues);

  // Calculate based on subject_rules
  if (jupasProgram?.scoring_formula?.subject_rules) {
    const scoringFormula = jupasProgram.scoring_formula;
    const baseScore = gradeValues["5**"]; // Max possible grade

    getActiveSubjectRules(scoringFormula).forEach((rule: SubjectRule) => {
      const weights = Object.values(rule.subject_weights) as number[];
      const maxWeight = Math.max(...weights);

      // For each subject slot in this rule, use the max weight
      for (let i = 0; i < rule.count; i++) {
        total += baseScore * maxWeight;
      }
    });
    return total;
  }

  // Fallback: simple 5 subjects with 5** (should not happen with current format)
  const baseScore = gradeValues["5**"] || 0;
  return baseScore * 5;
}

export function formatScore(score: number): string {
  // Round to 1 decimal place and always display it
  const rounded = roundToDecimals(score, 1);
  return rounded.toFixed(1);
}

function roundToDecimals(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * Calculate the best score for a user based on their selected subjects
 * This function uses the existing calculateScore logic to find the optimal subject combination
 * 
 * @param jupasId - The JUPAS programme ID (with or without 'JS' prefix)
 * @param userScores - User's scores for each subject (with min/current/max)
 * @param programs - Array of all JUPAS programmes
 * @param scoreType - Which score type to use ('min', 'current', or 'max')
 * @returns The calculated best score for the user
 */
export function calcBestScore(
  jupasId: string | number | null | undefined,
  userScores: Record<string, ScoreTriple>,
  programs: JupasProgram[],
  scoreType: 'min' | 'current' | 'max' = 'current'
): number {
  // Handle undefined/null jupasId
  if (!jupasId) {
    console.warn('calcBestScore called with undefined/null jupasId');
    return 0;
  }

  // Find the programme
  const cleanJupasId = String(jupasId).replace(/^JS/, '');
  const program = programs.find(p => p.jupas_id === cleanJupasId);

  if (!program) {
    console.warn(`Programme ${jupasId} not found`);
    return 0;
  }

  // Convert ScoreTriple to DSEResults based on scoreType
  const dseResults: DSEResults = {};
  Object.entries(userScores).forEach(([subject, triple]) => {
    if (triple?.[scoreType]) {
      dseResults[subject] = triple[scoreType] as Grade;
    }
  });

  // Use the existing calculateScore function to get the best combination
  const result = calculateScore(program, dseResults, scoreType === 'max');
  return result.total;
}

// ============================================================================
// Score Breakdown Calculation
// ============================================================================

/**
 * Calculate all subjects ranked for display (with breakdown details)
 */
export function calculateDetailedScore(
  userScores: Record<string, ScoreTriple>,
  selectedElectives: string[],
  jupasProgram: JupasProgram | undefined,
  instKey: string,
  scoreType: 'min' | 'current' | 'max' = 'current'
): SubjectBreakdownItem[] {
  if (!jupasProgram?.scoring_formula) {
    return [];
  }

  const jupasId = jupasProgram?.jupas_id?.replace(/^JS/i, '') || '';

  // Get programme-specific grade_values
  let programmeGradeValues = jupasProgram?.grade_values;
  if (!programmeGradeValues && jupasProgram?.scoring_formula?.grade_values) {
    programmeGradeValues = jupasProgram.scoring_formula.grade_values;
  }

  // Use calculateScore to get breakdown
  const dseResults: DSEResults = {};
  Object.keys(userScores).forEach(subject => {
    const score = userScores[subject];
    if (score?.[scoreType]) {
      dseResults[subject] = score[scoreType] as Grade;
    }
  });

  const result = calculateScore(jupasProgram, dseResults, scoreType === 'max');

  // Convert ScoreBreakdownItem[] to SubjectBreakdownItem[]
  return result.breakdown.map(item => ({
    subject: item.subject,
    grade: item.grade || '',
    score: item.weightedScore || 0,
    baseScore: item.rawScore || 0,
    weighting: item.weight || 1,
    mandatory: item.mandatory,
    slotType: item.slotType,
    category: item.category,
    counted: item.counted,
    description: item.description,
    replacementSourceSubject: item.replacementSourceSubject,
    replacementSourceGrade: item.replacementSourceGrade,
    replacementSourceRawScore: item.replacementSourceRawScore,
    replacementTargetOriginalRawScore: item.replacementTargetOriginalRawScore,
    replacementRuleNote: item.replacementRuleNote,
    replacementBlendTargetRatio: item.replacementBlendTargetRatio,
    replacementBlendSourceRatio: item.replacementBlendSourceRatio,
  }));
}

// ============================================================================
// MER (Minimum Entry Requirements) Checking
// ============================================================================

// Helper type for ScoreTriple input
export interface ScoreTriple {
  min: string;
  current: string;
  max: string;
}

/**
 * Check MER (Minimum Entry Requirements) for a programme with ScoreTriple grades
 * This is useful when you have min/current/max grades for each subject
 */
export const checkMERWithScoreTriple = (
  program: JupasProgram,
  scores: Record<string, ScoreTriple>,
  scoreType: 'min' | 'current' | 'max' = 'current'
): MERResult => {
  // Convert ScoreTriple to simple DSEResults based on scoreType
  const results: DSEResults = {};
  for (const [subject, triple] of Object.entries(scores)) {
    if (triple && triple[scoreType]) {
      results[subject] = triple[scoreType] as Grade;
    }
  }
  return checkMER(program, results);
};

/**
 * Special handler for JS1202 conditional PER requirements
 * Condition 1: Math Level 2 AND 2nd elective from {Bio/BAFS/DAT/ICT/Physics}
 * Condition 2: Math, Eng, Chin, Chem all Level 3+, then any elective at Level 3
 */
const checkJS1202ConditionalPER = (
  program: JupasProgram,
  results: DSEResults,
  allCoresMet: boolean,
  merResult: Omit<MERResult, 'eligible'>
): boolean => {
  // If cores aren't met, PER is not satisfied
  if (!allCoresMet) return false;

  // MUST verify Chemistry (specific required elective) is at Level 3+
  const chemGrade = results["Chemistry"];
  const chemLevel = chemGrade ? getGradeScore(String(chemGrade), program) : 0;
  if (chemLevel < 3) {
    return false; // Chemistry is required at Level 3
  }

  const mathGrade = results["Mathematics"];
  const engGrade = results["English Language"];
  const chinGrade = results["Chinese Language"];

  const mathLevel = mathGrade ? getGradeScore(String(mathGrade), program) : 0;
  const engLevel = engGrade ? getGradeScore(String(engGrade), program) : 0;
  const chinLevel = chinGrade ? getGradeScore(String(chinGrade), program) : 0;

  // Condition 1: Math is Level 2 → MUST have 2nd elective from restricted list
  if (mathLevel === 2) {
    const restrictedElectives = [
      'Biology',
      'Business, Accounting and Financial Studies (Accounting)',
      'Business, Accounting and Financial Studies (Business Management)',
      'Design and Applied Technology',
      'Information and Communication Technology',
      'Physics'
    ];

    // Check if student has at least one elective from the restricted list at Level 3+
    for (const subject of restrictedElectives) {
      const grade = results[subject as keyof DSEResults];
      if (grade && getGradeScore(String(grade), program) >= 3) {
        return true; // Condition 1 satisfied
      }
    }
    // Math is Level 2 but NO elective from restricted list → PER NOT satisfied
    return false;
  }

  const coreSubjects = ["English Language", "Chinese Language", "Mathematics", "Citizenship and Social Development"];
  const specificElectiveSubjects = program.program_mer?.specific_electives ? Object.keys(program.program_mer.specific_electives) : [];

  // Condition 2: Math >= 3 AND all four cores (Eng, Chin, Chem also >= 3)
  if (mathLevel >= 3 && engLevel >= 3 && chinLevel >= 3 && chemLevel >= 3) {
    // Then any additional elective subject at Level 3 is acceptable → PER satisfied
    const hasAdditionalLevel3Elective = Object.entries(results).some(([subject, grade]) => {
      if (coreSubjects.includes(subject)) return false;
      if (specificElectiveSubjects.includes(subject)) return false;
      if (!isProgramAllowedCategoryBSubject(subject, program)) return false;
      return grade && getGradeScore(String(grade), program) >= 3;
    });

    return hasAdditionalLevel3Elective;
  }

  // No other conditions satisfy PER
  return false;
};

/**
 * Check MER (Minimum Entry Requirements) for a programme
 */
export const checkMER = (program: JupasProgram, results: DSEResults): MERResult => {
  const coreMap = {
    "English Language": false,
    "Chinese Language": false,
    "Mathematics": false,
    "Citizenship and Social Development": false
  };

  // Determine core requirements source: program_mer or scoring_formula or institution general_mer
  const hasProgramMerCores = Object.keys(program.program_mer?.core_minima || {}).length > 0;

  // Check if scoring_formula has old format with mandatory rules
  const hasMandatoryRules = program.scoring_formula?.subject_rules?.some(
    (rule: any) => rule.mandatory === 1 && rule.categories?.includes('core')
  );

  if (hasProgramMerCores && program.program_mer.core_minima) {
    // Use program_mer.core_minima
    for (const subject of Object.keys(coreMap)) {
      coreMap[subject as keyof typeof coreMap] = true;
    }

    for (const [subject, req] of Object.entries(program.program_mer.core_minima)) {
      if (req === undefined) continue;

      // Normalize CSD key variations (e.g., "CITIZENSHIP AND SOCIAL DEVELOPMENT 1" -> "Citizenship and Social Development")
      const normalizedSubject = subject.toUpperCase().startsWith('CITIZENSHIP')
        ? 'Citizenship and Social Development'
        : subject;

      const userGrade = results[normalizedSubject as keyof DSEResults];
      if (!userGrade) {
        if (coreMap.hasOwnProperty(normalizedSubject)) {
          coreMap[normalizedSubject as keyof typeof coreMap] = false;
        }
        continue;
      }

      const userLevel = getGradeScore(String(userGrade), program);

      let met = false;
      if (req === 'A' || req === 'Attained') {
        met = isAttainedGrade(userGrade);
      } else {
        met = userLevel >= (typeof req === 'number' ? req : parseInt(String(req)));
      }

      if (normalizedSubject === 'Mathematics' && program.jupas_id === '4238') {
        met = isJS4238MathematicsRequirementMet(program, results);
      }

      // @ts-ignore
      if (coreMap.hasOwnProperty(normalizedSubject)) coreMap[normalizedSubject] = met;
    }
  } else if (hasMandatoryRules && program.scoring_formula?.subject_rules) {
    // Use scoring_formula.subject_rules for core requirements (old format with mandatory field)
    const csdProcessed = false;
    for (const subject of Object.keys(coreMap)) {
      coreMap[subject as keyof typeof coreMap] = true;
    }

    for (const rule of program.scoring_formula.subject_rules) {
      // @ts-ignore
      if (rule.categories?.includes('core') && rule.mandatory) {
        const minLevel = rule.min_level || 2;
        // @ts-ignore
        for (const subject of Object.keys(rule.subject_weights || {})) {
          if (coreMap.hasOwnProperty(subject)) {
            const userGrade = results[subject as keyof DSEResults];
            if (!userGrade) {
              coreMap[subject as keyof typeof coreMap] = false;
              continue;
            }

            const userLevel = getGradeScore(String(userGrade), program);
            let met = false;

            if (subject === 'Citizenship and Social Development') {
              met = isAttainedGrade(userGrade);
            } else {
              met = userLevel >= minLevel;
            }

            // @ts-ignore
            coreMap[subject] = met;
          }
        }
      }
    }

    // Handle CSD separately if it wasn't in any rule but user has a grade for it
    const csdGrade = results['Citizenship and Social Development'];
    if (csdGrade && coreMap['Citizenship and Social Development'] === false) {
      // CSD is not required by this programme, but if user has it, check if it's valid
      coreMap['Citizenship and Social Development'] =
        isAttainedGrade(csdGrade);
    }
  } else {
    // New simplified format: use institution's general_mer
    const instKey = getUniversityKey(program.jupas_id);
    const institutionData = (INSTITUTIONS_DATA as any)[instKey];
    const generalMer = institutionData?.general_mer || {};

    // Default core requirements (common across most institutions)
    const coreRequirements: Record<string, number | string> = {
      "English Language": generalMer["English Language"] || 3,
      "Chinese Language": generalMer["Chinese Language"] || 3,
      "Mathematics": generalMer["Mathematics"] || 2,
      "Citizenship and Social Development": generalMer["Citizenship and Social Development"] || 'A'
    };

    for (const [subject, req] of Object.entries(coreRequirements)) {
      const userGrade = results[subject as keyof DSEResults];
      if (!userGrade) continue;

      const userLevel = getGradeScore(String(userGrade), program);

      let met = false;
      if (req === 'A' || req === 'Attained') {
        met = isAttainedGrade(userGrade);
      } else {
        met = userLevel >= (typeof req === 'number' ? req : parseInt(String(req)));
      }

      // @ts-ignore
      if (coreMap.hasOwnProperty(subject)) coreMap[subject] = met;
    }
  }

  // 2. Check "One of" or "Two of" requirement (e.g., "One of: Biology / Chemistry" or "Two of: ...")
  let oneOfResult: { subjects: string[]; met: boolean; matched_subjects?: string[]; requireCount?: number } | undefined;
  if (program.program_mer.one_of_electives) {
    const oneOfSubjects = program.program_mer.one_of_electives.subjects;
    const minLevel = program.program_mer.one_of_electives.min_level;
    const requireCount = program.program_mer.one_of_electives.requireCount || 1;
    const matched_subjects: string[] = [];

    for (const subject of oneOfSubjects) {
      const userGrade = results[subject as keyof DSEResults];
      if (userGrade && isProgramAllowedCategoryBSubject(subject, program) && getGradeScore(String(userGrade), program) >= minLevel) {
        matched_subjects.push(subject);
      }
    }

    oneOfResult = {
      subjects: oneOfSubjects,
      met: matched_subjects.length >= requireCount,
      matched_subjects,
      requireCount
    };
  }

  // 2b. Check "One of Groups" or "Two of Groups" requirement (multiple restricted elective groups)
  let oneOfGroupsResult: Array<{
    name?: string;
    subjects: string[];
    met: boolean;
    matched_subjects?: string[];
    min_level: number;
    requireCount?: number;
  }> | undefined;

  if (program.program_mer.one_of_groups) {
    oneOfGroupsResult = program.program_mer.one_of_groups.map(group => {
      const requireCount = group.requireCount || 1;
      const matched_subjects: string[] = [];

      for (const subject of group.subjects) {
        const userGrade = results[subject as keyof DSEResults];
        if (userGrade && isProgramAllowedCategoryBSubject(subject, program) && getGradeScore(String(userGrade), program) >= group.min_level) {
          matched_subjects.push(subject);
        }
      }

      return {
        name: group.name,
        subjects: group.subjects,
        met: matched_subjects.length >= requireCount,
        matched_subjects,
        min_level: group.min_level,
        requireCount
      };
    });
  }

  // 3. Check "Other Subjects" or "Electives" requirement
  // If include_cores is true, count all subjects except the specific mandatory ones (English, Math for scoring)
  // If include_cores is false (default), only count elective subjects
  const includeCores = program.program_mer?.electives?.include_cores || false;
  const coreSubjects = ["English Language", "Chinese Language", "Mathematics", "Citizenship and Social Development"];

  // Get institution's electives_min_level from general_mer (default 2, but CUHK requires 3)
  const instKey = getUniversityKey(program.jupas_id);
  const institutionData = (INSTITUTIONS_DATA as any)[instKey];
  const gerElectivesMinLevel = institutionData?.general_mer?.electives_min_level || 2;
  const programElectiveRequirements = normalizeElectiveRequirements(program.program_mer.elective_requirements);
  const programElectiveAlternatives = Array.isArray(program.program_mer.elective_alternatives)
    ? program.program_mer.elective_alternatives
      .map(path => normalizeElectiveRequirements(path))
      .filter((path): path is NormalizedElectiveRequirement[] => Boolean(path))
    : [];

  // Check if specific subjects are required for electives (conditionally based on Math level)
  const specificSubjects = (program.program_mer?.electives as any)?.specific_subjects;
  const mathGrade = results["Mathematics"];
  const mathLevel = mathGrade ? getGradeScore(String(mathGrade), program) : 0;
  const specificElectiveSubjects = program.program_mer?.specific_electives ? Object.keys(program.program_mer.specific_electives) : [];

  // For JS1202: only apply specific_subjects restriction when Math < 3
  const applySpecificSubjects = Array.isArray(specificSubjects) && specificSubjects.length > 0 && mathLevel < 3;

  // For "other subjects" mode, get mandatory subjects from subject_rules
  const mandatorySubjects = includeCores && program.scoring_formula?.subject_rules
    ? program.scoring_formula.subject_rules
      .filter(rule => rule.mandatory === 1)
      .flatMap(rule => Object.keys(rule.subject_weights || {}))
      .filter(subj => subj !== 'any_one_subject')
    : [];

  // Get allowed categories from programme MER / scoring configuration
  const merAllowedCategories = getProgrammeMerAllowedCategories(program);

  // Note: For programs with one_of requirement, the matched subject from one_of 
  // SHOULD count toward the electives requirement. The one_of specifies that 
  // at least one of the required electives must be from that specific list.
  // It is NOT an additional requirement on top of electives.

  // Also collect matched subjects from one_of_groups if they should be excluded
  const matchedOneOfGroupSubjects = oneOfGroupsResult
    ? oneOfGroupsResult.flatMap(g => g.matched_subjects || [])
    : [];

  const eligibleSubjects = Object.entries(results).filter(([subj, grade]) => {
    // Always exclude CSD from counting
    if (subj === "Citizenship and Social Development") return false;

    if (specificElectiveSubjects.includes(subj)) return false;

    if (!isProgramAllowedCategoryBSubject(subj, program)) return false;

    // If Math < 3 and specific_subjects is defined, only count those subjects
    if (applySpecificSubjects) {
      if (!specificSubjects.includes(subj)) return false;
    }

    // If include_cores is false, exclude all core subjects
    if (!includeCores && coreSubjects.includes(subj)) return false;

    // If include_cores is true, only exclude mandatory subjects from scoring
    if (includeCores && mandatorySubjects.includes(subj)) return false;

    // DO NOT exclude the matched subject from one_of - it should count toward electives
    // The one_of requirement means "one of your electives must be from this list"
    // not "pick one from this list IN ADDITION TO your electives"

    // Exclude matched subjects from one_of_groups (these are truly separate requirements)
    if (matchedOneOfGroupSubjects.includes(subj)) return false;

    // Check if subject is in allowed categories (if specified)
    if (!subjectMeetsMerCategoryRequirement(subj, merAllowedCategories)) return false;

    return getGradeScore(String(grade), program) >= gerElectivesMinLevel;
  });

  const effectiveForbiddenGroups = getEffectiveForbiddenGroups(program, institutionData);
  const electivesFound = countSubjectsAfterForbiddenGroupDedup(
    eligibleSubjects.map(([subj]) => subj),
    effectiveForbiddenGroups,
  );

  const electivesMet = electivesFound >= (program.program_mer?.electives?.count || 2);

  const electiveRequirementsResult = evaluateElectiveRequirements(
    program,
    results,
    programElectiveRequirements,
    program.program_mer?.electives?.min_level || 3,
    true,
  );
  const electiveAlternativesResult = programElectiveAlternatives.length > 0
    ? programElectiveAlternatives.map(path => evaluateElectiveRequirements(
      program,
      results,
      path,
      program.program_mer?.electives?.min_level || 3,
      true,
    ) || [])
    : undefined;
  // Elective requirements mean "must be able to satisfy the required pattern", not
  // "every elective entered must match". Otherwise a valid extra 6th (bonus) subject
  // (e.g. HKUST Cat B/C bonus) can incorrectly invalidate MER/PER and hide bonus labels.
  const electiveRequirementsMet = electiveRequirementsResult
    ? electiveRequirementsResult.every(requirement => requirement.met)
    : electiveAlternativesResult
      ? electiveAlternativesResult.some(path => path.every(requirement => requirement.met))
      : true;


  const allCoresMet = Object.values(coreMap).every(v => v);
  const oneOfMet = oneOfResult ? oneOfResult.met : true;
  const oneOfGroupsMet = oneOfGroupsResult ? oneOfGroupsResult.every(g => g.met) : true;

  // 4. Check specific electives (e.g., Physics for JS4690)
  let specificElectivesResult: { [subject: string]: { required_level: number; met: boolean; actual_level?: number } } | undefined;
  if (program.program_mer.specific_electives) {
    specificElectivesResult = {};
    for (const [subject, requiredLevel] of Object.entries(program.program_mer.specific_electives)) {
      const userGrade = results[subject as keyof DSEResults];
      const actualLevel = userGrade && isProgramAllowedCategoryBSubject(subject, program)
        ? getGradeScore(String(userGrade), program)
        : 0;
      specificElectivesResult[subject] = {
        required_level: requiredLevel,
        met: actualLevel >= requiredLevel,
        actual_level: actualLevel > 0 ? actualLevel : undefined
      };
    }
  }
  const specificElectivesMet = specificElectivesResult ? Object.values(specificElectivesResult).every(req => req.met) : true;

  const consumedMerElectiveSubjects = getConsumedMerElectiveSubjects(
    oneOfResult,
    oneOfGroupsResult,
    specificElectivesResult,
  );

  // 5. Check additional "any one subject" requirement (e.g., for JS4412)
  let additionalAnyOneSubjectResult: { min_level: number; met: boolean } | undefined;
  if (program.program_mer.additional_any_one_subject) {
    const minLevel = program.program_mer.additional_any_one_subject.min_level;
    const allowedCategories =
      program.program_mer.additional_any_one_subject.categories
      || merAllowedCategories;

    const additionalSubjectsMet = countAdditionalMerSubjectsMet(
      program,
      results,
      minLevel,
      allowedCategories,
      consumedMerElectiveSubjects,
    );

    additionalAnyOneSubjectResult = {
      min_level: minLevel,
      met: additionalSubjectsMet >= 1,
    };
  }
  const additionalAnyOneSubjectMet = additionalAnyOneSubjectResult ? additionalAnyOneSubjectResult.met : true;

  // 6. Check additional "any two subjects" requirement
  let additionalAnyTwoSubjectsResult: { min_level: number; met: boolean } | undefined;
  if (program.program_mer.additional_any_two_subjects) {
    const minLevel = program.program_mer.additional_any_two_subjects.min_level;
    const allowedCategories =
      program.program_mer.additional_any_two_subjects.categories
      || merAllowedCategories;

    const additionalSubjectsMet = countAdditionalMerSubjectsMet(
      program,
      results,
      minLevel,
      allowedCategories,
      consumedMerElectiveSubjects,
    );

    additionalAnyTwoSubjectsResult = {
      min_level: minLevel,
      met: additionalSubjectsMet >= 2,
    };
  }
  const additionalAnyTwoSubjectsMet = additionalAnyTwoSubjectsResult ? additionalAnyTwoSubjectsResult.met : true;

  // Build the base MER result
  const baseMERResult = {
    core_minima: coreMap,
    electives: {
      required: program.program_mer?.electives?.count || 2,
      found: electivesFound,
      met: electivesMet,
      include_cores: includeCores,
      min_level: gerElectivesMinLevel
    },
    elective_requirements: electiveRequirementsResult,
    elective_alternatives: electiveAlternativesResult,
    one_of_electives: oneOfResult ? {
      ...oneOfResult,
      min_level: program.program_mer.one_of_electives?.min_level,
      requireCount: program.program_mer.one_of_electives?.requireCount || 1
    } : undefined,
    one_of_groups: oneOfGroupsResult,
    specific_electives: specificElectivesResult,
    additional_any_one_subject: additionalAnyOneSubjectResult,
    additional_any_two_subjects: additionalAnyTwoSubjectsResult
  };

  // Special handling for JS1202 conditional PER requirements
  let finalEligible = allCoresMet && electivesMet && electiveRequirementsMet && oneOfMet && oneOfGroupsMet && specificElectivesMet && additionalAnyOneSubjectMet && additionalAnyTwoSubjectsMet;
  if (program.jupas_id === '1202') {
    const mathGrade = results["Mathematics"];
    const mathLevel = mathGrade ? getGradeScore(String(mathGrade), program) : 0;
    if (mathLevel === 2) {
      const restrictedElectives = [
        'Biology',
        'Business, Accounting and Financial Studies (Accounting)',
        'Business, Accounting and Financial Studies (Business Management)',
        'Design and Applied Technology',
        'Information and Communication Technology',
        'Physics'
      ];
      const restrictedFound = restrictedElectives.filter(subject => {
        const grade = results[subject as keyof DSEResults];
        return grade && getGradeScore(String(grade), program) >= 3;
      }).length;

      baseMERResult.electives = {
        ...baseMERResult.electives,
        found: restrictedFound,
        met: restrictedFound >= 1
      };
    }

    finalEligible = checkJS1202ConditionalPER(program, results, allCoresMet, baseMERResult);
  }

  // JS4502: At least 40 points in the best six graded subjects, with 5** in any four.
  if (program.jupas_id === '4502' && !meetsJS4502AdditionalRequirements(program, results)) {
    finalEligible = false;
  }

  return {
    ...baseMERResult,
    eligible: finalEligible
  };
};

// ============================================================================
// Full Score Calculation (using scoring_formula)
// ============================================================================

/**
 * Calculate score using programme's scoring_formula configuration
 */
const calculateScore = (
  program: JupasProgram,
  results: DSEResults,
  isMaxCalculation: boolean = false
): { total: number; breakdown: ScoreBreakdownItem[] } => {
  results = filterInstitutionExcludedResults(program, results);

  const breakdown: ScoreBreakdownItem[] = [];
  let totalScore = 0;

  const scoringFormula = program.scoring_formula;
  const activeSubjectRules = getActiveSubjectRules(scoringFormula);

  const instKey = getUniversityKey(String(program.jupas_id)) || (program as any).university?.toLowerCase() || 'other';
  const effectiveBonusRules = getBonusSubjectRules(program, instKey);
  const effectiveBonusBaseScore = calcHighestAttainableScoreBase(program, instKey);
  const shouldHideBonus = !isMaxCalculation && (() => {
    try {
      const merResult = checkMER(program, results);
      return !merResult?.eligible;
    } catch {
      return false;
    }
  })();

  // Helper to get raw score for subjects
  const getRawScore = (subject: string, grade: string): number => {
    return getGradeScore(grade, program, subject);
  };

  // IMPORTANT: Exclude CSD from scoring unless the institution explicitly scores CSD.
  const availableSubjects = new Set(
    Object.keys(results).filter(subj => {
      if (subj !== 'Citizenship and Social Development') return true;
      return doesInstitutionScoreCsd(instKey);
    })
  );

  const instData = (INSTITUTIONS_DATA as any)[instKey];
  const selectionForbiddenGroups = getScoreSelectionForbiddenGroups(program, instData);
  const catBScoringMinScore = instData?.cat_b_scoring_min_score;
  if (catBScoringMinScore !== undefined) {
    for (const subj of Array.from(availableSubjects)) {
      if (getSubjectCategory(subj) === 'B') {
        const grade = results[subj];
        if (getRawScore(subj, grade) < catBScoringMinScore) {
          availableSubjects.delete(subj);
        }
      }
    }
  }

  // Exclude unsupported Cat B subjects for this institution
  const unsupportedCatBSubjects = new Set<string>(instData?.unsupported_cat_b_subjects || []);
  if (unsupportedCatBSubjects.size > 0) {
    for (const subj of Array.from(availableSubjects)) {
      if (getSubjectCategory(subj) === 'B' && unsupportedCatBSubjects.has(subj)) {
        availableSubjects.delete(subj);
      }
    }
  }

  for (const subj of Array.from(availableSubjects)) {
    if (!isProgramAllowedCategoryBSubject(subj, program)) {
      availableSubjects.delete(subj);
    }
  }

  // Track category counts for category limits
  const categoryCounts = { A: 0, B: 0, C: 0 };
  const programmeCategoryLimits = getProgrammeCategoryLimits(program);
  const getProjectedCategoryCounts = (
    extraAssign: Array<{ subject: string; category?: 'A' | 'B' | 'C' }> = [],
  ): { A: number; B: number; C: number } => {
    const counts = { A: 0, B: 0, C: 0 };
    for (const item of breakdown) {
      const category = item.category || getSubjectCategory(item.subject);
      counts[category]++;
    }
    for (const item of extraAssign) {
      const category = item.category || getSubjectCategory(item.subject);
      counts[category]++;
    }
    return counts;
  };
  const canSelectSubjectForScore = (
    subject: string,
    projectedCounts: { A: number; B: number; C: number },
    projectedSubjects: string[] = [],
    options?: { ignoreProgrammeCategoryLimits?: boolean; isBonus?: boolean },
  ): boolean => {
    const usedSubjects = new Set<string>(breakdown.map(item => item.subject));
    projectedSubjects.forEach(projectedSubject => usedSubjects.add(projectedSubject));
    if (options?.isBonus) {
      const mainSubjects = new Set<string>(breakdown.filter(item => item.slotType !== 'BONUS').map(item => item.subject));
      projectedSubjects.forEach(projectedSubject => mainSubjects.add(projectedSubject));
      if (violatesSubjectGroupLimitsForBonus(subject, mainSubjects, scoringFormula)) return false;
    } else {
      if (violatesSubjectGroupLimits(subject, usedSubjects, scoringFormula)) return false;
    }
    if (!programmeCategoryLimits || options?.ignoreProgrammeCategoryLimits) return true;
    const category = getSubjectCategory(subject);
    if (
      programmeCategoryLimits.allowed_categories
      && !programmeCategoryLimits.allowed_categories.includes(category)
    ) {
      return false;
    }
    return isSubjectAllowedByCategory(subject, programmeCategoryLimits, projectedCounts);
  };

  const totalEligibleSubjectCount = Object.keys(results).length;
  const canActivateBonusRule = (rule: { min_total_subjects?: number } | undefined): boolean => {
    if (!rule) return false;
    const minTotalSubjects = typeof rule.min_total_subjects === 'number' ? rule.min_total_subjects : 0;
    return totalEligibleSubjectCount >= minTotalSubjects;
  };

  const isPotentialBonusSubject = (subject: string): boolean => {
    if (!Array.isArray(effectiveBonusRules) || effectiveBonusRules.length === 0) {
      return false;
    }

    return effectiveBonusRules.some(rule => {
      if (!canActivateBonusRule(rule)) return false;
      const allowedCategories = rule.categories || getProgrammeDefaultRuleCategories(program, false);
      return matchesRuleCategoryList(subject, allowedCategories);
    });
  };

  if (programmeCategoryLimits) {
    for (const subj of Array.from(availableSubjects)) {
      if (!canSelectSubjectForScore(subj, { A: 0, B: 0, C: 0 }) && !isPotentialBonusSubject(subj)) {
        availableSubjects.delete(subj);
      }
    }
  }
  const buildRuleSubjectWeightMap = (): Record<string, number> => {
    if (!Array.isArray(scoringFormula?.subject_rules) || scoringFormula.subject_rules.length === 0) {
      return {};
    }

    return activeSubjectRules.reduce<Record<string, number>>((acc, rule) => {
      for (const [subj, w] of Object.entries(rule.subject_weights || {})) {
        if (typeof w === 'number') {
          const canonicalSubject = subj === 'any_one_subject' ? subj : canonicalizeSubjectName(subj);
          acc[canonicalSubject] = Math.max(acc[canonicalSubject] ?? Number.NEGATIVE_INFINITY, w);
        }
      }
      return acc;
    }, {});
  };
  const getEstimatedBonusPotential = (usedSubjects: Set<string>): number => {
    if (!Array.isArray(effectiveBonusRules) || effectiveBonusRules.length === 0) {
      return 0;
    }

    const subjectWeights = buildRuleSubjectWeightMap();
    let totalBonus = 0;
    const remainingSubjects = new Set<string>(usedSubjects);

    for (const rule of effectiveBonusRules) {
      if (!canActivateBonusRule(rule)) continue;

      for (let i = 0; i < (rule.count || 1); i += 1) {
        let bestBonusSubject: string | null = null;
        let bestBonusScore = -1;

        for (const subject of Array.from(availableSubjects)) {
          if (remainingSubjects.has(subject)) continue;
          if (hasForbiddenConflictForSelection(subject, remainingSubjects)) continue;

          const allowedCategories = rule.categories || getProgrammeDefaultRuleCategories(program, false);
          if (!matchesRuleCategoryList(subject, allowedCategories)) continue;

          const bonusCategoryCounts = { A: 0, B: 0, C: 0 };
          remainingSubjects.forEach(usedSubject => {
            bonusCategoryCounts[getSubjectCategory(usedSubject)]++;
          });
          if (!canSelectSubjectForScore(subject, bonusCategoryCounts, Array.from(usedSubjects), { isBonus: true })) continue;

          const rawScore = getRawScore(subject, results[subject]);
          if (rawScore < (rule.min_level || 0)) continue;

          let bonusScore = 0;
          if (rule.formula === 'percentage_of_weighted_score') {
            const effectiveWeights = rule.bonus_subject_weights || subjectWeights;
            const weight = getResolvedSubjectWeight(effectiveWeights, subject);
            bonusScore = rawScore * (weight ?? 1) * (rule.rate || 0);
          } else if (rule.formula === 'percentage_of_max') {
            let baselineMax = 8.5;
            if (getSubjectCategory(subject) === 'C') {
              const baselineGrade = rule.category_c_bonus_baseline || 'C2';
              baselineMax = getGradeScore(baselineGrade, program, subject);
            }
            const percentageBase = rule.base_score ?? effectiveBonusBaseScore;
            if (baselineMax > 0) {
              bonusScore = (rawScore / baselineMax) * percentageBase * (rule.rate || 0);
            }
          }

          if (
            rule.formula === 'percentage_of_max'
              ? isBetterSixthSubjectBonusCandidate(
                subject,
                rawScore,
                bestBonusSubject,
                bestBonusSubject ? getRawScore(bestBonusSubject, results[bestBonusSubject]) : -1,
              )
              : (
            bonusScore > bestBonusScore
            || (
              bonusScore === bestBonusScore
              && bestBonusSubject !== null
              && getRuleCategoryPriority(subject, allowedCategories) < getRuleCategoryPriority(bestBonusSubject, allowedCategories)
            )
            || (bonusScore === bestBonusScore && bestBonusSubject === null)
              )
          ) {
            bestBonusScore = bonusScore;
            bestBonusSubject = subject;
          }
        }

        if (!bestBonusSubject || bestBonusScore <= 0) {
          continue;
        }

        totalBonus += bestBonusScore;
        remainingSubjects.add(bestBonusSubject);
      }
    }

    return totalBonus;
  };

  // If no subject_rules, use default "Best 5 subjects" formula
  if (!scoringFormula?.subject_rules || !Array.isArray(scoringFormula.subject_rules) || scoringFormula.subject_rules.length === 0) {
    // Default formula: Best 5 subjects with weight 1.0 each
    const numSubjects = scoringFormula?.num_of_subjects || 5;
    const institutionData = (INSTITUTIONS_DATA as any)[instKey];
    const acceptedCategories = isCuhkMedicineProgramme(program)
      ? ['A']
      : institutionData?.accepted_categories || ['A', 'B', 'C'];
    const forbiddenGroups = getScoreSelectionForbiddenGroups(program, institutionData);

    // Build list of all available subjects with their scores
    const potentialSubjects: {
      subject: string,
      score: number,
      category: 'A' | 'B' | 'C',
      grade: string
    }[] = [];

    availableSubjects.forEach(subj => {
      const subjCategory = getSubjectCategory(subj);
      // Check if subject category is accepted
      if (!acceptedCategories.includes(subjCategory)) return;

      const grade = results[subj];
      const score = getRawScore(subj, grade);
      potentialSubjects.push({
        subject: subj,
        score,
        category: subjCategory,
        grade
      });
    });

    // Sort by score descending
    potentialSubjects.sort((a, b) => b.score - a.score);

    // Filter by forbidden groups
    const filteredSubjects = filterForbiddenSubjects(
      potentialSubjects,
      new Set<string>(),
      forbiddenGroups
    );

    // Select best N subjects
    const selected: typeof filteredSubjects = [];
    const selectedSubjects = new Set<string>();
    for (const subject of filteredSubjects) {
      if (selected.length >= numSubjects) break;
      if (violatesSubjectGroupLimits(subject.subject, selectedSubjects, scoringFormula)) continue;
      selected.push(subject);
      selectedSubjects.add(subject.subject);
    }
    selected.forEach((p, idx) => {
      breakdown.push({
        subject: p.subject,
        grade: p.grade,
        rawScore: p.score,
        weight: 1.0,
        weightedScore: p.score,
        description: `Best ${idx + 1} of ${numSubjects}`,
        category: p.category,
        mandatory: false,
        slotType: 'REMAINING'
      });
      totalScore += p.score;
    });

    return { total: totalScore, breakdown };
  }

  if (
    scoringFormula.max_weighted_subjects !== undefined
    && Array.isArray(scoringFormula.subject_rules)
    && scoringFormula.subject_rules.length === 1
  ) {
    const pooledResult = calculateCappedWeightedPoolSelection(
      program,
      scoringFormula,
      scoringFormula.subject_rules[0],
      results,
      availableSubjects,
      selectionForbiddenGroups,
    );
    if (pooledResult) {
      return pooledResult;
    }
  }

  const mandatoryRules = activeSubjectRules.filter(
    rule => rule.mandatory === 1 || rule.required === true || rule.required === 1,
  );
  const hasFlexibleRules = activeSubjectRules.some(
    rule => !(rule.mandatory === 1 || rule.required === true || rule.required === 1),
  );
  const shouldOptimizeMandatoryWithBonus = !hasFlexibleRules
    && Array.isArray(effectiveBonusRules)
    && effectiveBonusRules.some(rule => canActivateBonusRule(rule));
  const selectByRaw = scoringFormula.select_by_raw === true;
  const mandatoryFallbackSlots: { allowedCategories: string[]; weight: number; excludeSubjects?: string[] }[] = [];
  const hasForbiddenConflictForSelection = (subject: string, usedSet: Set<string>): boolean => {
    if (!selectionForbiddenGroups) return false;
    for (const group of selectionForbiddenGroups) {
      if (group.includes(subject)) {
        for (const other of group) {
          if (other !== subject && usedSet.has(other)) return true;
        }
      }
    }
    return false;
  };
  const upsertMandatoryCandidate = (
    candidateMap: Map<string, { subject: string; grade: string; rawScore: number; weight: number; weightedScore: number; category: 'A' | 'B' | 'C'; description: string; isExplicitMatch: boolean }>,
    candidate: { subject: string; grade: string; rawScore: number; weight: number; weightedScore: number; category: 'A' | 'B' | 'C'; description: string; isExplicitMatch: boolean }
  ) => {
    const existing = candidateMap.get(candidate.subject);
    if (!existing) {
      candidateMap.set(candidate.subject, candidate);
      return;
    }

    if (candidate.weightedScore > existing.weightedScore) {
      candidateMap.set(candidate.subject, candidate);
      return;
    }

    if (candidate.weightedScore === existing.weightedScore && candidate.rawScore > existing.rawScore) {
      candidateMap.set(candidate.subject, candidate);
    }
  };
  const mandatoryUsedSubjects = new Set<string>();
  const mandatorySlots: Array<{
    rule: SubjectRule;
    slotIdx: number;
    candidates: Array<{ subject: string; grade: string; rawScore: number; weight: number; weightedScore: number; category: 'A' | 'B' | 'C'; description: string; isExplicitMatch: boolean }>;
    fallbackSlot?: { allowedCategories: string[]; weight: number; excludeSubjects?: string[] };
    missingIfEmpty: boolean;
  }> = [];
  mandatoryRules.forEach(rule => {
    const subjectWeights = rule.subject_weights;
    const subjectNames = Object.keys(subjectWeights);

    const matchesRuleCategories = (subject: string): boolean => {
      if (subjectExcludedByRule(rule, subject)) {
        return false;
      }
      const defaultCats = subjectWeights['any_one_subject'] !== undefined
        ? getProgrammeDefaultRuleCategories(program, true)
        : getProgrammeDefaultRuleCategories(program, false);
      return matchesRuleCategoryList(subject, rule.categories || defaultCats);
    };

    for (let i = 0; i < rule.count; i++) {
      const explicitCandidateMap = new Map<string, { subject: string; grade: string; rawScore: number; weight: number; weightedScore: number; category: 'A' | 'B' | 'C'; description: string; isExplicitMatch: boolean }>();
      const wildcardCandidateMap = new Map<string, { subject: string; grade: string; rawScore: number; weight: number; weightedScore: number; category: 'A' | 'B' | 'C'; description: string; isExplicitMatch: boolean }>();

      for (const subjectName of subjectNames) {
        if (subjectName === 'any_one_subject') continue;

        const canonicalSubjectName = canonicalizeSubjectName(subjectName);
        if (subjectExcludedByRule(rule, canonicalSubjectName)) {
          continue;
        }
        if (availableSubjects.has(canonicalSubjectName)) {
          const grade = results[canonicalSubjectName];
          const rawScore = getRawScore(canonicalSubjectName, grade);
          const weight = subjectWeights[subjectName];
          upsertMandatoryCandidate(explicitCandidateMap, {
            subject: canonicalSubjectName,
            grade,
            rawScore,
            weight,
            weightedScore: rawScore * weight,
            category: getSubjectCategory(canonicalSubjectName),
            description: `Mandatory: ${canonicalSubjectName}`,
            isExplicitMatch: true,
          });
        } else {
          for (const availSubj of Array.from(availableSubjects)) {
            if (SUBJECT_STRAND_MAP[availSubj] === subjectName || SUBJECT_STRAND_MAP[availSubj] === canonicalSubjectName) {
              const grade = results[availSubj];
              const rawScore = getRawScore(availSubj, grade);
              const weight = subjectWeights[subjectName];
              upsertMandatoryCandidate(explicitCandidateMap, {
                subject: availSubj,
                grade,
                rawScore,
                weight,
                weightedScore: rawScore * weight,
                category: getSubjectCategory(availSubj),
                description: `Mandatory: ${availSubj} (${subjectName})`,
                isExplicitMatch: true,
              });
              break;
            }
          }
        }
      }

      if (subjectWeights['any_one_subject'] !== undefined) {
        const wildcardWeight = subjectWeights['any_one_subject'];
        for (const availSubj of Array.from(availableSubjects)) {
          if (!matchesRuleCategories(availSubj)) continue;
          const grade = results[availSubj];
          const rawScore = getRawScore(availSubj, grade);
          upsertMandatoryCandidate(wildcardCandidateMap, {
            subject: availSubj,
            grade,
            rawScore,
            weight: wildcardWeight,
            weightedScore: rawScore * wildcardWeight,
            category: getSubjectCategory(availSubj),
            description: `Mandatory: ${availSubj} (any_one_subject)`,
            isExplicitMatch: false,
          });
        }
      }

      const combinedCandidateMap = new Map<string, { subject: string; grade: string; rawScore: number; weight: number; weightedScore: number; category: 'A' | 'B' | 'C'; description: string; isExplicitMatch: boolean }>();
      const preferExplicitSubjects = rule.prefer_explicit_subjects === true
        && subjectWeights['any_one_subject'] !== undefined
        && explicitCandidateMap.size > 0;

      Array.from(explicitCandidateMap.values()).forEach(candidate => {
        upsertMandatoryCandidate(combinedCandidateMap, candidate);
      });
      Array.from(wildcardCandidateMap.values()).forEach(candidate => {
        upsertMandatoryCandidate(combinedCandidateMap, candidate);
      });

      const candidates = Array.from(combinedCandidateMap.values());
      candidates.sort((a, b) => {
        const ruleCategories = rule.categories || (subjectWeights['any_one_subject'] !== undefined
          ? getProgrammeDefaultRuleCategories(program, true)
          : getProgrammeDefaultRuleCategories(program, false));
        const shouldPreserveCoreForBonus = rule.preserve_core_for_bonus === true
          && Array.isArray(effectiveBonusRules)
          && effectiveBonusRules.some(bonusRule => canActivateBonusRule(bonusRule));
        if (selectByRaw && a.rawScore !== b.rawScore) {
          return b.rawScore - a.rawScore;
        }
        if (selectByRaw) {
          const aSubjectPriority = getRuleSubjectPriority(a.subject, rule.subject_priority);
          const bSubjectPriority = getRuleSubjectPriority(b.subject, rule.subject_priority);
          if (aSubjectPriority !== bSubjectPriority) {
            return aSubjectPriority - bSubjectPriority;
          }
        }
        if (a.weightedScore !== b.weightedScore) {
          return b.weightedScore - a.weightedScore;
        }
        if (a.rawScore !== b.rawScore) {
          return b.rawScore - a.rawScore;
        }

        if (preferExplicitSubjects && a.isExplicitMatch !== b.isExplicitMatch) {
          return a.isExplicitMatch ? -1 : 1;
        }

        if (shouldPreserveCoreForBonus) {
          const aIsCore = isCoreSubject(a.subject);
          const bIsCore = isCoreSubject(b.subject);
          if (aIsCore !== bIsCore) {
            return aIsCore ? 1 : -1;
          }
        }

        const aPriority = getRuleCategoryPriority(a.subject, ruleCategories);
        const bPriority = getRuleCategoryPriority(b.subject, ruleCategories);
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        const aSubjectPriority = getRuleSubjectPriority(a.subject, rule.subject_priority);
        const bSubjectPriority = getRuleSubjectPriority(b.subject, rule.subject_priority);
        if (aSubjectPriority !== bSubjectPriority) {
          return aSubjectPriority - bSubjectPriority;
        }

        return a.subject.localeCompare(b.subject);
      });

      mandatorySlots.push({
        rule,
        slotIdx: mandatorySlots.length,
        candidates,
        fallbackSlot: candidates.length === 0 && subjectWeights['any_one_subject'] !== undefined
          ? {
            allowedCategories: rule.fallback_categories || rule.categories || getProgrammeDefaultRuleCategories(program, true),
            weight: subjectWeights['any_one_subject'],
            excludeSubjects: rule.exclude_subjects,
          }
          : undefined,
        missingIfEmpty: candidates.length === 0 && subjectWeights['any_one_subject'] === undefined,
      });
    }
  });

  const flexibleRules = activeSubjectRules.filter(
    rule => !(rule.mandatory === 1 || rule.required === true || rule.required === 1),
  );

  const normalizedFlexibleRules = (() => {
    if (instKey !== 'hku' || flexibleRules.length < 2) {
      return flexibleRules;
    }

    const merged = [...flexibleRules];
    for (let explicitIdx = 0; explicitIdx < merged.length; explicitIdx += 1) {
      const explicitRule = merged[explicitIdx];
      const explicitWeights = explicitRule.subject_weights || {};
      const explicitSubjects = Object.keys(explicitWeights).filter(subject => subject !== 'any_one_subject');
      if (explicitSubjects.length === 0 || explicitWeights['any_one_subject'] !== undefined) {
        continue;
      }

      const wildcardIdx = merged.findIndex((candidateRule, candidateIdx) => {
        if (candidateIdx === explicitIdx) {
          return false;
        }

        const candidateWeights = candidateRule.subject_weights || {};
        if (candidateWeights['any_one_subject'] === undefined) {
          return false;
        }

        return explicitSubjects.every(subject => candidateWeights[subject] === explicitWeights[subject]);
      });

      if (wildcardIdx === -1) {
        continue;
      }

      const wildcardRule = merged[wildcardIdx];
      merged[wildcardIdx] = {
        ...wildcardRule,
        count: (wildcardRule.count || 1) + (explicitRule.count || 1),
      };
      merged.splice(explicitIdx, 1);
      explicitIdx -= 1;
    }

    return merged;
  })();

  const hasRequiredOrMandatoryRules = Array.isArray(scoringFormula?.subject_rules)
    && activeSubjectRules.some(rule => rule.required === true || rule.mandatory === 1);

  const preferredCoreSubjects = getPolyUPreferredCoreSubjects(program);
  const effectiveFlexibleRules = (
    getUniversityKey(program.jupas_id) === '3'
    && !hasRequiredOrMandatoryRules
    && normalizedFlexibleRules.length === 1
    && preferredCoreSubjects.length === 2
    && (normalizedFlexibleRules[0].count || 1) > preferredCoreSubjects.length
    && preferredCoreSubjects.length > 0
  )
    ? (() => {
      const [baseRule] = normalizedFlexibleRules;
      const baseSubjectWeights = baseRule.subject_weights || {};
      const preferredSubjectWeights = preferredCoreSubjects.reduce<Record<string, number>>((acc, subject) => {
        const weight = getResolvedSubjectWeight(baseSubjectWeights, subject);
        if (weight !== undefined) {
          acc[subject] = weight;
        }
        return acc;
      }, {});

      if (Object.keys(preferredSubjectWeights).length === 0) {
        return normalizedFlexibleRules;
      }

      return [
        {
          ...baseRule,
          subject_weights: preferredSubjectWeights,
          categories: ['core'],
          count: Math.min(preferredCoreSubjects.length, baseRule.count || 1),
        },
        {
          ...baseRule,
          count: Math.max((baseRule.count || 1) - preferredCoreSubjects.length, 0),
        },
      ].filter(rule => (rule.count || 0) > 0);
    })()
    : normalizedFlexibleRules;

  const mainSlotTarget = scoringFormula.num_of_subjects || 5;
  const projectedFlexSlots: { subjectWeights: Record<string, number>; allowedCategories: string[]; excludeSubjects?: string[] }[] = [];
  effectiveFlexibleRules.forEach(rule => {
    const count = rule.count || 1;
    const sw = rule.subject_weights || {};
    const defaultCategories = sw['any_one_subject'] !== undefined
      ? getProgrammeDefaultRuleCategories(program, true)
      : getProgrammeDefaultRuleCategories(program, false);
    for (let i = 0; i < count; i += 1) {
      projectedFlexSlots.push({
        subjectWeights: sw,
        allowedCategories: rule.categories || defaultCategories,
        excludeSubjects: rule.exclude_subjects,
      });
    }
  });

  const projectedFlexCandidates = Array.from(availableSubjects).map(subject => ({
    subject,
    rawScore: getRawScore(subject, results[subject]),
  }));

  const getProjectedFlexSlotWeight = (
    slot: typeof projectedFlexSlots[number],
    subject: string,
  ): number | null => {
    if (subjectListIncludes(slot.excludeSubjects, subject)) {
      return null;
    }

    const subjectWeights = slot.subjectWeights || {};
    let weight = getExplicitSubjectWeight(subjectWeights, subject);

    if (weight === undefined && SUBJECT_STRAND_MAP[subject] !== undefined) {
      weight = getExplicitSubjectWeight(subjectWeights, SUBJECT_STRAND_MAP[subject]);
    }

    if (weight !== undefined) {
      return weight;
    }

    if (!matchesRuleCategoryList(subject, slot.allowedCategories)) {
      return null;
    }

    if (getSubjectCategory(subject) === 'A' && subjectWeights['any_category_a_elective'] !== undefined) {
      weight = subjectWeights['any_category_a_elective'];
    }

    if (weight === undefined && subjectWeights['any_one_subject'] !== undefined) {
      weight = subjectWeights['any_one_subject'];
    }

    // Default weight 1 when rule has categories but no subject_weights
    if (weight === undefined && Object.keys(subjectWeights).length === 0) {
      weight = 1;
    }

    return weight ?? null;
  };

  const isCoreOnlyRule = (rule: SubjectRule): boolean => {
    const subjectWeights = rule.subject_weights || {};
    if (subjectWeights['any_one_subject'] !== undefined) {
      return false;
    }

    const explicitSubjects = Object.keys(subjectWeights).filter(subject => subject !== 'any_one_subject');
    if (explicitSubjects.length === 0) {
      return false;
    }

    return explicitSubjects.every(subject => isCoreSubject(subject));
  };

  const isWildcardOnlyRule = (rule: SubjectRule): boolean => {
    const subjectWeights = rule.subject_weights || {};
    const explicitSubjects = Object.keys(subjectWeights).filter(subject => subject !== 'any_one_subject');
    return explicitSubjects.length === 0 && subjectWeights['any_one_subject'] !== undefined;
  };

  const getRuleSlotWeight = (rule: SubjectRule, subject: string): number | null => {
    if (subjectExcludedByRule(rule, subject)) {
      return null;
    }

    const subjectWeights = rule.subject_weights || {};
    let weight = getExplicitSubjectWeight(subjectWeights, subject);

    if (weight === undefined && SUBJECT_STRAND_MAP[subject] !== undefined) {
      weight = getExplicitSubjectWeight(subjectWeights, SUBJECT_STRAND_MAP[subject]);
    }

    if (weight !== undefined) {
      return weight;
    }

    const defaultCategories = subjectWeights['any_one_subject'] !== undefined
      ? getProgrammeDefaultRuleCategories(program, true)
      : getProgrammeDefaultRuleCategories(program, false);

    if (!matchesRuleCategoryList(subject, rule.categories || defaultCategories)) {
      return null;
    }

    if (getSubjectCategory(subject) === 'A' && subjectWeights['any_category_a_elective'] !== undefined) {
      return subjectWeights['any_category_a_elective'];
    }

    if (subjectWeights['any_one_subject'] !== undefined) {
      return subjectWeights['any_one_subject'];
    }

    // Default weight 1 when rule has categories but no subject_weights
    if (Object.keys(subjectWeights).length === 0) {
      return 1;
    }

    return null;
  };

  const buildCityUPositionalElectiveOrder = (): string[] => {
    const rawElectives = Array.from(availableSubjects).filter(subject => !isCoreSubject(subject));
    if (rawElectives.length <= 1) {
      return rawElectives;
    }
    const ordered = [...rawElectives];

    const electiveMinima = (program as JupasProgram & { program_mer?: { elective_minima?: Record<string, Record<string, number>> } })?.program_mer?.elective_minima;
    if (!electiveMinima || typeof electiveMinima !== 'object') {
      return ordered;
    }

    const genericLabels = new Set([
      'ANY 1 SUBJECT', 'ANY CATEGORY A SUBJECT', 'ANY ONE SUBJECT',
      'ANY 2 SUBJECTS', 'ANY 2 CATEGORY A SUBJECTS',
    ]);

    const slotRequirements: Array<{ slotIdx: number; requiredSubjects: string[]; isSpecific: boolean }> = [];
    Object.entries(electiveMinima).forEach(([slotKey, requirement]) => {
      if (!requirement || typeof requirement !== 'object' || !slotKey.startsWith('elective_')) {
        return;
      }

      const baseKey = slotKey.split(':', 1)[0];
      const slotIdx = Number.parseInt(baseKey.split('_')[1] || '', 10) - 1;
      if (!Number.isFinite(slotIdx) || slotIdx < 0) {
        return;
      }

      const requiredSubjects = Object.keys(requirement)
        .filter(subject => !genericLabels.has(subject.toUpperCase()))
        .map(subject => canonicalizeSubjectName(subject));
      const isSpecific = requiredSubjects.length > 0;
      if (!isSpecific && slotKey.toLowerCase().includes(':one_of')) {
        return;
      }

      slotRequirements.push({ slotIdx, requiredSubjects, isSpecific });
    });

    if (slotRequirements.length === 0) {
      return ordered;
    }

    const normalizedRequirements = [...slotRequirements].sort((left, right) => {
      if (left.isSpecific !== right.isSpecific) {
        return left.isSpecific ? -1 : 1;
      }
      return left.slotIdx - right.slotIdx;
    });

    const normalizedOrder = [...ordered];
    normalizedRequirements.forEach((requirement, normalizedIdx) => {
      if (!requirement.isSpecific || normalizedIdx >= normalizedOrder.length) {
        return;
      }

      const currentIdx = normalizedOrder.findIndex(subject => requirement.requiredSubjects.includes(subject));
      if (currentIdx === -1 || currentIdx === normalizedIdx) {
        return;
      }

      const tmp = normalizedOrder[normalizedIdx];
      normalizedOrder[normalizedIdx] = normalizedOrder[currentIdx];
      normalizedOrder[currentIdx] = tmp;
    });

    return normalizedOrder;
  };

  const useCityUPositionalAssignment = getUniversityKey(program.jupas_id) === '1'
    && activeSubjectRules.length > 0
    && projectedFlexSlots.length > 0
    && mandatoryRules.length > 0
    && (() => {
      const nonCoreRules = activeSubjectRules.filter(rule => !isCoreOnlyRule(rule));
      const hasRequiredNonCoreRule = nonCoreRules.some(
        rule => rule.mandatory === 1 || rule.required === true || rule.required === 1,
      );
      return hasRequiredNonCoreRule || nonCoreRules.length > 1;
    })();

  if (useCityUPositionalAssignment) {
    const orderedElectives = buildCityUPositionalElectiveOrder();
    let flexibleSlotNumber = 0;

    activeSubjectRules.forEach(rule => {
      const count = rule.count || 1;
      for (let ruleSlotIdx = 0; ruleSlotIdx < count; ruleSlotIdx += 1) {
        const requiredLike = rule.mandatory === 1 || rule.required === true || rule.required === 1;
        const wildcardOnlyRule = isWildcardOnlyRule(rule);
        const hasWildcardWeight = (rule.subject_weights || {}).any_one_subject !== undefined;
        const ruleHasExplicitCoreSubject = Object.keys(rule.subject_weights || {})
          .filter(sw => sw !== 'any_one_subject')
          .some(sw => isCoreSubject(sw));
        const sourceSubjects = isCoreOnlyRule(rule)
          ? Array.from(availableSubjects)
          : (wildcardOnlyRule || (requiredLike && hasWildcardWeight) || ruleHasExplicitCoreSubject)
            ? Array.from(availableSubjects)
            : orderedElectives.filter(subject => availableSubjects.has(subject));

        let pickedSubject: string | null = null;
        let pickedWeight: number | null = null;
        let pickedExplicit = false;

        const rankSubjects = (
          subjects: string[],
          selectionMode: 'explicit-only' | 'wildcard-only' | 'any',
        ): string[] => {
          const ranked = [...subjects];
          ranked.sort((left, right) => {
            const leftWeight = getRuleSlotWeight(rule, left) ?? 0;
            const rightWeight = getRuleSlotWeight(rule, right) ?? 0;
            const leftRaw = getRawScore(left, results[left]);
            const rightRaw = getRawScore(right, results[right]);
            const leftWeighted = leftRaw * leftWeight;
            const rightWeighted = rightRaw * rightWeight;

            if (selectByRaw && leftRaw !== rightRaw) {
              return rightRaw - leftRaw;
            }
            if (leftWeighted !== rightWeighted) {
              return rightWeighted - leftWeighted;
            }
            if (leftRaw !== rightRaw) {
              return rightRaw - leftRaw;
            }
            return left.localeCompare(right);
          });

          return ranked;
        };

        const tryPick = (selectionMode: 'explicit-only' | 'wildcard-only' | 'any'): boolean => {
          for (const subject of rankSubjects(sourceSubjects, selectionMode)) {
            if (!isCoreOnlyRule(rule) && isCoreSubject(subject)) {
              const isExplicitlyWeightedCore = getExplicitSubjectWeight(rule.subject_weights || {}, subject) !== undefined
                || (SUBJECT_STRAND_MAP[subject] !== undefined
                  ? getExplicitSubjectWeight(rule.subject_weights || {}, SUBJECT_STRAND_MAP[subject]) !== undefined
                  : false);
              if (!wildcardOnlyRule && !isExplicitlyWeightedCore && !hasWildcardWeight) {
                continue;
              }
            }
            if (hasForbiddenConflictForSelection(subject, mandatoryUsedSubjects)) {
              continue;
            }

            const explicitWeight = getExplicitSubjectWeight(rule.subject_weights || {}, subject)
              ?? (SUBJECT_STRAND_MAP[subject] !== undefined
                ? getExplicitSubjectWeight(rule.subject_weights || {}, SUBJECT_STRAND_MAP[subject])
                : undefined);

            if (selectionMode === 'explicit-only' && explicitWeight === undefined) {
              continue;
            }
            if (selectionMode === 'wildcard-only' && explicitWeight !== undefined) {
              continue;
            }

            const weight = explicitWeight ?? getRuleSlotWeight(rule, subject);
            if (weight === null || weight === undefined || weight <= 0) {
              continue;
            }

            pickedSubject = subject;
            pickedWeight = weight;
            pickedExplicit = explicitWeight !== undefined;
            return true;
          }

          return false;
        };

        if (requiredLike) {
          if (hasWildcardWeight) {
            tryPick('any');
          } else {
            tryPick('explicit-only');
          }
        } else {
          tryPick('any');
        }

        if (!pickedSubject || pickedWeight === null) {
          if (requiredLike) {
            breakdown.push({
              subject: `Missing required slot ${breakdown.length + 1}`,
              grade: '',
              rawScore: 0,
              weight: 0,
              weightedScore: 0,
              description: 'Missing required subject',
              category: 'A',
              mandatory: true,
              slotType: 'MISSING',
              counted: false,
            });
          }
          continue;
        }

        const rawScore = getRawScore(pickedSubject, results[pickedSubject]);
        const description = requiredLike
          ? (pickedExplicit ? `Mandatory: ${pickedSubject}` : `Mandatory: ${pickedSubject} (any_one_subject)`)
          : `Flexible slot ${++flexibleSlotNumber}`;

        breakdown.push({
          subject: pickedSubject,
          grade: results[pickedSubject],
          rawScore,
          weight: pickedWeight,
          weightedScore: rawScore * pickedWeight,
          description,
          category: getSubjectCategory(pickedSubject),
          mandatory: requiredLike,
          slotType: requiredLike ? 'SPECIFIC' : 'REMAINING',
        });
        totalScore += rawScore * pickedWeight;
        availableSubjects.delete(pickedSubject);
        mandatoryUsedSubjects.add(pickedSubject);
        categoryCounts[getSubjectCategory(pickedSubject)] += 1;
      }
    });

    return { total: totalScore, breakdown };
  }

  const compareProjectedFlexOutcome = (
    candidate: { score: number; rawScore: number; subjects: string[] },
    incumbent: { score: number; rawScore: number; subjects: string[] } | null,
  ): boolean => {
    if (!incumbent) return true;
    if (selectByRaw && candidate.rawScore !== incumbent.rawScore) {
      return candidate.rawScore > incumbent.rawScore;
    }
    if (candidate.score !== incumbent.score) {
      return candidate.score > incumbent.score;
    }
    if (!selectByRaw && candidate.rawScore !== incumbent.rawScore) {
      return candidate.rawScore > incumbent.rawScore;
    }
    return candidate.subjects.join('|') < incumbent.subjects.join('|');
  };

  const estimateFlexiblePotential = (
    baseUsedSubjects: Set<string>,
    filledMainSlotCount: number,
  ): { score: number; rawScore: number; subjects: string[] } => {
    const remainingSlots = Math.max(0, mainSlotTarget - filledMainSlotCount);
    if (remainingSlots <= 0 || projectedFlexSlots.length === 0) {
      return { score: 0, rawScore: 0, subjects: [] };
    }

    const memo = new Map<string, { score: number; rawScore: number; subjects: string[] }>();

    const searchProjectedFlex = (
      slotIdx: number,
      usedSubjects: Set<string>,
      chosenCount: number,
    ): { score: number; rawScore: number; subjects: string[] } => {
      if (slotIdx >= projectedFlexSlots.length || chosenCount >= remainingSlots) {
        return { score: 0, rawScore: 0, subjects: [] };
      }

      const memoKey = `${slotIdx}|${chosenCount}|${Array.from(usedSubjects).sort().join('|')}`;
      const memoized = memo.get(memoKey);
      if (memoized) {
        return memoized;
      }

      let bestOutcome = searchProjectedFlex(slotIdx + 1, usedSubjects, chosenCount);
      const slot = projectedFlexSlots[slotIdx];

      for (const candidate of projectedFlexCandidates) {
        if (usedSubjects.has(candidate.subject)) continue;
        if (hasForbiddenConflictForSelection(candidate.subject, usedSubjects)) continue;
        if (violatesSubjectGroupLimits(candidate.subject, usedSubjects, scoringFormula)) continue;

        const weight = getProjectedFlexSlotWeight(slot, candidate.subject);
        if (weight === null) continue;

        const nextUsedSubjects = new Set(usedSubjects);
        nextUsedSubjects.add(candidate.subject);
        const nextOutcome = searchProjectedFlex(slotIdx + 1, nextUsedSubjects, chosenCount + 1);
        const currentOutcome = {
          score: candidate.rawScore * weight + nextOutcome.score,
          rawScore: candidate.rawScore + nextOutcome.rawScore,
          subjects: [candidate.subject, ...nextOutcome.subjects],
        };

        if (compareProjectedFlexOutcome(currentOutcome, bestOutcome)) {
          bestOutcome = currentOutcome;
        }
      }

      memo.set(memoKey, bestOutcome);
      return bestOutcome;
    };

    return searchProjectedFlex(0, new Set(baseUsedSubjects), 0);
  };

  let bestMandatoryScore = Number.NEGATIVE_INFINITY;
  let bestMandatoryProjectedScore = Number.NEGATIVE_INFINITY;
  let bestMandatoryProjectedRaw = Number.NEGATIVE_INFINITY;
  let bestMandatoryBonusPotential = Number.NEGATIVE_INFINITY;
  let bestMandatoryRaw = Number.NEGATIVE_INFINITY;
  let bestMandatoryFilled = Number.NEGATIVE_INFINITY;
  let bestMandatoryAssign: Array<{ slotIdx: number; subject: string; grade: string; rawScore: number; weight: number; weightedScore: number; category: 'A' | 'B' | 'C'; description: string }> = [];

  const orderedMandatorySlots = [...mandatorySlots].sort((a, b) => {
    if (a.candidates.length !== b.candidates.length) {
      return a.candidates.length - b.candidates.length;
    }
    return a.slotIdx - b.slotIdx;
  });

  const updateBestMandatoryAssignment = (
    assign: typeof bestMandatoryAssign,
    score: number,
    rawScore: number,
    projectedFlexScore: number,
    projectedFlexRawScore: number,
    bonusPotential: number,
  ) => {
    const projectedScore = score + projectedFlexScore + bonusPotential;
    const projectedRaw = rawScore + projectedFlexRawScore;
    if (
      (selectByRaw && projectedRaw > bestMandatoryProjectedRaw)
      || (selectByRaw && projectedRaw === bestMandatoryProjectedRaw && projectedScore > bestMandatoryProjectedScore)
      || (selectByRaw && projectedRaw === bestMandatoryProjectedRaw && projectedScore === bestMandatoryProjectedScore && score > bestMandatoryScore)
      || (selectByRaw && projectedRaw === bestMandatoryProjectedRaw && projectedScore === bestMandatoryProjectedScore && score === bestMandatoryScore && rawScore > bestMandatoryRaw)
      || (selectByRaw && projectedRaw === bestMandatoryProjectedRaw && projectedScore === bestMandatoryProjectedScore && score === bestMandatoryScore && rawScore === bestMandatoryRaw && assign.length > bestMandatoryFilled)
      || (!selectByRaw && projectedScore > bestMandatoryProjectedScore)
      || (!selectByRaw && projectedScore === bestMandatoryProjectedScore && projectedRaw > bestMandatoryProjectedRaw)
      || (!selectByRaw && projectedScore === bestMandatoryProjectedScore && projectedRaw === bestMandatoryProjectedRaw && score > bestMandatoryScore)
      || (!selectByRaw && projectedScore === bestMandatoryProjectedScore && projectedRaw === bestMandatoryProjectedRaw && score === bestMandatoryScore && rawScore > bestMandatoryRaw)
      || (!selectByRaw && projectedScore === bestMandatoryProjectedScore && projectedRaw === bestMandatoryProjectedRaw && score === bestMandatoryScore && rawScore === bestMandatoryRaw && bonusPotential > bestMandatoryBonusPotential)
      || (!selectByRaw && projectedScore === bestMandatoryProjectedScore && projectedRaw === bestMandatoryProjectedRaw && score === bestMandatoryScore && rawScore === bestMandatoryRaw && bonusPotential === bestMandatoryBonusPotential && assign.length > bestMandatoryFilled)
    ) {
      bestMandatoryProjectedScore = projectedScore;
      bestMandatoryProjectedRaw = projectedRaw;
      bestMandatoryScore = score;
      bestMandatoryBonusPotential = bonusPotential;
      bestMandatoryRaw = rawScore;
      bestMandatoryFilled = assign.length;
      bestMandatoryAssign = assign.map(item => ({ ...item }));
    }
  };

  const searchMandatoryAssignments = (
    slotOrderIdx: number,
    assign: typeof bestMandatoryAssign,
    usedSubjects: Set<string>,
    score: number,
    rawScore: number,
  ) => {
    if (slotOrderIdx === orderedMandatorySlots.length) {
      const projectedFlex = estimateFlexiblePotential(usedSubjects, assign.length);
      const projectedUsedSubjects = new Set<string>(usedSubjects);
      projectedFlex.subjects.forEach(subject => {
        projectedUsedSubjects.add(subject);
      });
      updateBestMandatoryAssignment(
        assign,
        score,
        rawScore,
        projectedFlex.score,
        projectedFlex.rawScore,
        getEstimatedBonusPotential(projectedUsedSubjects),
      );
      return;
    }

    const slot = orderedMandatorySlots[slotOrderIdx];
    let assignedAny = false;

    for (const candidate of slot.candidates) {
      if (usedSubjects.has(candidate.subject)) continue;
      if (hasForbiddenConflictForSelection(candidate.subject, usedSubjects)) continue;
      if (!canSelectSubjectForScore(candidate.subject, getProjectedCategoryCounts(assign), assign.map(item => item.subject))) continue;

      assignedAny = true;
      usedSubjects.add(candidate.subject);
      assign.push({
        slotIdx: slot.slotIdx,
        ...candidate,
      });

      searchMandatoryAssignments(
        slotOrderIdx + 1,
        assign,
        usedSubjects,
        score + candidate.weightedScore,
        rawScore + candidate.rawScore,
      );

      assign.pop();
      usedSubjects.delete(candidate.subject);
    }

    if (!assignedAny) {
      searchMandatoryAssignments(slotOrderIdx + 1, assign, usedSubjects, score, rawScore);
    }
  };

  searchMandatoryAssignments(0, [], new Set<string>(), 0, 0);

  const mandatoryAssignmentBySlot = new Map<number, typeof bestMandatoryAssign[number]>();
  bestMandatoryAssign.forEach(item => {
    mandatoryAssignmentBySlot.set(item.slotIdx, item);
  });
  let unresolvedRequiredSlots = Math.max(0, mandatorySlots.length - bestMandatoryAssign.length);

  mandatorySlots.forEach(slot => {
    const best = mandatoryAssignmentBySlot.get(slot.slotIdx);
    if (best) {
      breakdown.push({
        subject: best.subject,
        grade: best.grade,
        rawScore: best.rawScore,
        weight: best.weight,
        weightedScore: best.weightedScore,
        description: best.description,
        category: best.category,
        mandatory: true,
        slotType: 'SPECIFIC'
      });
      totalScore += best.weightedScore;
      availableSubjects.delete(best.subject);
      mandatoryUsedSubjects.add(best.subject);
      categoryCounts[best.category]++;
      return;
    }

    if (slot.fallbackSlot) {
      mandatoryFallbackSlots.push(slot.fallbackSlot);
      return;
    }

    if (slot.missingIfEmpty) {
      breakdown.push({
        subject: `Missing required slot ${breakdown.length + 1}`,
        grade: '',
        rawScore: 0,
        weight: 0,
        weightedScore: 0,
        description: 'Missing required subject',
        category: 'A',
        mandatory: true,
        slotType: 'MISSING',
        counted: false,
      });
    }
  });

  const scoringCandidateSubjects = Array.from(availableSubjects);

  const flexSlots: { ruleIndex: number; subjectWeights: Record<string, number>; allowedCategories: string[]; excludeSubjects?: string[]; subjectPriority?: string[] }[] = [];
  effectiveFlexibleRules.forEach((rule, ruleIndex) => {
    const count = rule.count || 1;
    const sw = rule.subject_weights || {};
    // When a rule has any_one_subject wildcard, core subjects should be eligible
    const defaultCategories = sw['any_one_subject'] !== undefined
      ? getProgrammeDefaultRuleCategories(program, true)
      : getProgrammeDefaultRuleCategories(program, false);
    for (let i = 0; i < count; i++) {
      flexSlots.push({
        ruleIndex,
        subjectWeights: sw,
        allowedCategories: rule.categories || defaultCategories,
        excludeSubjects: rule.exclude_subjects,
        subjectPriority: rule.subject_priority,
      });
    }
  });

  const flexForbiddenGroups = getScoreSelectionForbiddenGroups(program, instData);
  const baseFlexUsedSubjects = new Set(breakdown.map(item => item.subject));

  const flexAvail: { subject: string; category: 'A' | 'B' | 'C'; grade: string; raw: number }[] = [];
  availableSubjects.forEach(subj => {
    const grade = results[subj];
    const raw = getRawScore(subj, grade);
    flexAvail.push({ subject: subj, category: getSubjectCategory(subj), grade, raw });
  });

  const getSlotWeight = (slot: typeof flexSlots[0], subj: string): number | null => {
    if (subjectListIncludes(slot.excludeSubjects, subj)) {
      return null;
    }

    if (!matchesRuleCategoryList(subj, slot.allowedCategories)) return null;

    const sw = slot.subjectWeights;
    let weight: number | undefined = undefined;

    const explicitWeight = getExplicitSubjectWeight(sw, subj);
    if (explicitWeight !== undefined) {
      weight = explicitWeight;
    } else if (SUBJECT_STRAND_MAP[subj] !== undefined) {
      const strand = SUBJECT_STRAND_MAP[subj];
      const strandWeight = getExplicitSubjectWeight(sw, strand);
      if (strandWeight !== undefined) {
        weight = strandWeight;
      }
    }

    if (weight !== undefined) {
      return weight;
    }

    // Support wildcards like 'any_category_a_elective' for HKU and similar
    if (getSubjectCategory(subj) === 'A' && sw['any_category_a_elective'] !== undefined) {
      // Exclude Applied Learning if present
      if (subj !== 'Applied Learning') {
        weight = sw['any_category_a_elective'];
      }
    }
    // Fallback to 'any_one_subject' wildcard
    if (weight === undefined && sw['any_one_subject'] !== undefined) {
      weight = sw['any_one_subject'];
    }
    // Default weight 1 when rule has categories but no subject_weights
    if (weight === undefined && Object.keys(sw).length === 0) {
      weight = 1;
    }
    if (weight === undefined) return null;
    return weight;
  };

  const hasForbiddenConflict = (subject: string, usedSet: Set<string>): boolean => {
    return hasForbiddenConflictForSelection(subject, usedSet);
  };

  const isEligibleForActiveBonusRule = (subject: string): boolean => {
    if (!Array.isArray(effectiveBonusRules) || effectiveBonusRules.length === 0) {
      return false;
    }

    const grade = results[subject];
    const rawScore = getRawScore(subject, grade);

    return effectiveBonusRules.some(rule => {
      if (!canActivateBonusRule(rule)) return false;
      const allowedCategories = rule.categories || getProgrammeDefaultRuleCategories(program, false);
      if (!matchesRuleCategoryList(subject, allowedCategories)) return false;
      return rawScore >= (rule.min_level || 0);
    });
  };

  const preferNonBonusEligibleMainSubject = (
    candidateSubject: string,
    incumbentSubject: string,
  ): boolean | null => {
    const candidateEligible = isEligibleForActiveBonusRule(candidateSubject);
    const incumbentEligible = isEligibleForActiveBonusRule(incumbentSubject);
    if (candidateEligible == incumbentEligible) {
      return null;
    }
    return !candidateEligible && incumbentEligible;
  };

  const compareSubjectsForRule = (
    candidateSubject: string,
    incumbentSubject: string,
    allowedCategories: string[] | undefined,
    subjectPriority: string[] | undefined,
  ): number => {
    const preserveBonusSubject = preferNonBonusEligibleMainSubject(candidateSubject, incumbentSubject);
    if (preserveBonusSubject !== null) {
      return preserveBonusSubject ? -1 : 1;
    }

    const candidateSubjectPriority = getRuleSubjectPriority(candidateSubject, subjectPriority);
    const incumbentSubjectPriority = getRuleSubjectPriority(incumbentSubject, subjectPriority);
    if (candidateSubjectPriority !== incumbentSubjectPriority) {
      return candidateSubjectPriority - incumbentSubjectPriority;
    }

    const candidateCategoryPriority = getRuleCategoryPriority(candidateSubject, allowedCategories);
    const incumbentCategoryPriority = getRuleCategoryPriority(incumbentSubject, allowedCategories);
    if (candidateCategoryPriority !== incumbentCategoryPriority) {
      return candidateCategoryPriority - incumbentCategoryPriority;
    }

    return candidateSubject.localeCompare(incumbentSubject);
  };

  const isBetterRuleCandidate = (
    candidate: { subject: string; raw: number; weightedScore: number },
    incumbent: { subject: string; raw: number; weightedScore: number } | null,
    allowedCategories: string[] | undefined,
    subjectPriority?: string[] | undefined,
  ): boolean => {
    if (!incumbent) return true;
    if (selectByRaw && candidate.raw !== incumbent.raw) {
      return candidate.raw > incumbent.raw;
    }
    if (selectByRaw) {
      const candidateSubjectPriority = getRuleSubjectPriority(candidate.subject, subjectPriority);
      const incumbentSubjectPriority = getRuleSubjectPriority(incumbent.subject, subjectPriority);
      if (candidateSubjectPriority !== incumbentSubjectPriority) {
        return candidateSubjectPriority < incumbentSubjectPriority;
      }
    }
    if (candidate.weightedScore !== incumbent.weightedScore) {
      return candidate.weightedScore > incumbent.weightedScore;
    }
    if (candidate.raw !== incumbent.raw) {
      return candidate.raw > incumbent.raw;
    }

    return compareSubjectsForRule(
      candidate.subject,
      incumbent.subject,
      allowedCategories,
      subjectPriority,
    ) < 0;
  };

  const isEligibleForSupplementalSlot = (subject: string): boolean => {
    if (flexSlots.some(slot => getSlotWeight(slot, subject) !== null)) {
      return true;
    }

    return mandatoryFallbackSlots.some(slot => (
      !subjectListIncludes(slot.excludeSubjects, subject)
      && matchesRuleCategoryList(subject, slot.allowedCategories)
    ));
  };

  // Flexible slots should choose distinct subjects. Reduced-weight nth-subject
  // cases are modeled via bonus_subjects, not by reusing a main-slot subject.
  let bestFlexProjectedScore = Number.NEGATIVE_INFINITY;
  let bestFlexScore = Number.NEGATIVE_INFINITY;
  let bestFlexRawScore = Number.NEGATIVE_INFINITY;
  let bestFlexBonusPotential = Number.NEGATIVE_INFINITY;
  let bestFlexAssign: { subject: string; category: 'A' | 'B' | 'C'; grade: string; raw: number; weight: number; slotIdx: number; ruleIndex: number }[] = [];
  const allSlots = flexSlots.map(slot => ({ ...slot, isRequired: false }));
  const getFlexSlotSubjectPriority = (slotIdx: number): string[] | undefined => {
    const slot = allSlots[slotIdx] as (typeof allSlots[number] & { subjectPriority?: string[] }) | undefined;
    return slot?.subjectPriority;
  };
  const flexSubjectWeights = Object.entries(scoringFormula.subject_rules || {}).length >= 0
    ? (scoringFormula.subject_rules || []).reduce<Record<string, number>>((acc, rule) => {
      for (const [subj, w] of Object.entries(rule.subject_weights || {})) {
        if (typeof w === 'number') {
          const canonicalSubject = subj === 'any_one_subject' ? subj : canonicalizeSubjectName(subj);
          acc[canonicalSubject] = Math.max(acc[canonicalSubject] ?? Number.NEGATIVE_INFINITY, w);
        }
      }
      return acc;
    }, {})
    : {};

  const getAssignmentBonusPotential = (assign: typeof bestFlexAssign): number => {
    if (!Array.isArray(effectiveBonusRules) || effectiveBonusRules.length === 0) {
      return 0;
    }

    const usedSubjects = new Set<string>([
      ...baseFlexUsedSubjects,
      ...assign.map(item => item.subject),
    ]);
    let totalBonus = 0;

    for (const rule of effectiveBonusRules) {
      if (!canActivateBonusRule(rule)) continue;

      for (let i = 0; i < (rule.count || 1); i += 1) {
        let bestBonusSubject: string | null = null;
        let bestBonusScore = -1;

        for (const subject of scoringCandidateSubjects) {
          if (usedSubjects.has(subject)) continue;
          if (hasForbiddenConflict(subject, usedSubjects)) continue;

          const allowedCategories = rule.categories || getProgrammeDefaultRuleCategories(program, false);
          if (!matchesRuleCategoryList(subject, allowedCategories)) continue;
          if (violatesSubjectGroupLimitsForBonus(subject, usedSubjects, scoringFormula)) continue;

          const rawScore = getRawScore(subject, results[subject]);
          if (rawScore < (rule.min_level || 0)) continue;

          let bonusScore = 0;
          if (rule.formula === 'percentage_of_weighted_score') {
            const effectiveWeights = rule.bonus_subject_weights || flexSubjectWeights;
            const weight = getResolvedSubjectWeight(effectiveWeights, subject);
            bonusScore = rawScore * (weight ?? 1) * (rule.rate || 0);
          } else if (rule.formula === 'percentage_of_max') {
            let baselineMax = 8.5;
            if (getSubjectCategory(subject) === 'C') {
              const baselineGrade = rule.category_c_bonus_baseline || 'C2';
              baselineMax = getGradeScore(baselineGrade, program, subject);
            }
            const percentageBase = rule.base_score ?? effectiveBonusBaseScore;
            if (baselineMax > 0) {
              bonusScore = (rawScore / baselineMax) * percentageBase * (rule.rate || 0);
            }
          }

          if (
            rule.formula === 'percentage_of_max'
              ? isBetterSixthSubjectBonusCandidate(
                subject,
                rawScore,
                bestBonusSubject,
                bestBonusSubject ? getRawScore(bestBonusSubject, results[bestBonusSubject]) : -1,
              )
              : (
            bonusScore > bestBonusScore
            || (
              bonusScore === bestBonusScore
              && bestBonusSubject !== null
              && getRuleCategoryPriority(subject, allowedCategories) < getRuleCategoryPriority(bestBonusSubject, allowedCategories)
            )
            || (bonusScore === bestBonusScore && bestBonusSubject === null)
              )
          ) {
            bestBonusScore = bonusScore;
            bestBonusSubject = subject;
          }
        }

        if (!bestBonusSubject || bestBonusScore <= 0) {
          continue;
        }

        totalBonus += bestBonusScore;
        usedSubjects.add(bestBonusSubject);
      }
    }

    return totalBonus;
  };

  const remainingMainFlexSlots = Math.max(0, mainSlotTarget - breakdown.length);

  const getBonusPotentialUpperBound = (usedSubjects: Set<string>, onlySubject?: string): number => {
    if (!Array.isArray(effectiveBonusRules) || effectiveBonusRules.length === 0) {
      return 0;
    }

    let totalBonus = 0;
    const remainingSubjects = new Set<string>(usedSubjects);

    for (const rule of effectiveBonusRules) {
      if (!canActivateBonusRule(rule)) continue;

      for (let i = 0; i < (rule.count || 1); i += 1) {
        let bestBonusSubject: string | null = null;
        let bestBonusScore = -1;

        for (const subject of scoringCandidateSubjects) {
          if (onlySubject !== undefined && subject !== onlySubject) continue;
          if (remainingSubjects.has(subject)) continue;
          if (hasForbiddenConflict(subject, remainingSubjects)) continue;

          const allowedCategories = rule.categories || getProgrammeDefaultRuleCategories(program, false);
          if (!matchesRuleCategoryList(subject, allowedCategories)) continue;
          if (violatesSubjectGroupLimitsForBonus(subject, remainingSubjects, scoringFormula)) continue;

          const rawScore = getRawScore(subject, results[subject]);
          if (rawScore < (rule.min_level || 0)) continue;

          let bonusScore = 0;
          if (rule.formula === 'percentage_of_weighted_score') {
            const effectiveWeights = rule.bonus_subject_weights || flexSubjectWeights;
            const weight = getResolvedSubjectWeight(effectiveWeights, subject);
            bonusScore = rawScore * (weight ?? 1) * (rule.rate || 0);
          } else if (rule.formula === 'percentage_of_max') {
            let baselineMax = 8.5;
            if (getSubjectCategory(subject) === 'C') {
              const baselineGrade = rule.category_c_bonus_baseline || 'C2';
              baselineMax = getGradeScore(baselineGrade, program, subject);
            }
            const percentageBase = rule.base_score ?? effectiveBonusBaseScore;
            if (baselineMax > 0) {
              bonusScore = (rawScore / baselineMax) * percentageBase * (rule.rate || 0);
            }
          }

          if (
            rule.formula === 'percentage_of_max'
              ? isBetterSixthSubjectBonusCandidate(
                subject,
                rawScore,
                bestBonusSubject,
                bestBonusSubject ? getRawScore(bestBonusSubject, results[bestBonusSubject]) : -1,
              )
              : (
            bonusScore > bestBonusScore
            || (
              bonusScore === bestBonusScore
              && bestBonusSubject !== null
              && getRuleCategoryPriority(subject, allowedCategories) < getRuleCategoryPriority(bestBonusSubject, allowedCategories)
            )
            || (bonusScore === bestBonusScore && bestBonusSubject === null)
              )
          ) {
            bestBonusScore = bonusScore;
            bestBonusSubject = subject;
          }
        }

        if (!bestBonusSubject || bestBonusScore <= 0) {
          continue;
        }

        totalBonus += bestBonusScore;
        remainingSubjects.add(bestBonusSubject);
      }
    }

    return totalBonus;
  };

  const updateBestFlexAssignment = (assign: typeof bestFlexAssign, score: number, rawScore: number) => {
    const bonusPotential = getAssignmentBonusPotential(assign);
    const projectedScore = score + bonusPotential;
    const compareAssignmentPriority = (): number => {
      const maxLen = Math.max(assign.length, bestFlexAssign.length);
      for (let index = 0; index < maxLen; index += 1) {
        const candidateItem = assign[index];
        const incumbentItem = bestFlexAssign[index];
        if (!candidateItem && incumbentItem) return 1;
        if (candidateItem && !incumbentItem) return -1;
        if (!candidateItem || !incumbentItem) continue;

        const comparison = compareSubjectsForRule(
          candidateItem.subject,
          incumbentItem.subject,
          allSlots[candidateItem.slotIdx]?.allowedCategories,
          getFlexSlotSubjectPriority(candidateItem.slotIdx),
        );
        if (comparison !== 0) {
          return comparison;
        }
      }
      return 0;
    };

    if (
      (selectByRaw && rawScore > bestFlexRawScore)
      || (selectByRaw && rawScore === bestFlexRawScore && projectedScore > bestFlexProjectedScore)
      || (selectByRaw && rawScore === bestFlexRawScore && projectedScore === bestFlexProjectedScore && score > bestFlexScore)
      || (selectByRaw && rawScore === bestFlexRawScore && projectedScore === bestFlexProjectedScore && score === bestFlexScore && compareAssignmentPriority() < 0)
      || (selectByRaw && rawScore === bestFlexRawScore && projectedScore === bestFlexProjectedScore && score === bestFlexScore && compareAssignmentPriority() === 0 && assign.length > bestFlexAssign.length)
      || (selectByRaw && rawScore === bestFlexRawScore && projectedScore === bestFlexProjectedScore && score === bestFlexScore && compareAssignmentPriority() === 0 && assign.length === bestFlexAssign.length && bonusPotential > bestFlexBonusPotential)
      || (!selectByRaw && projectedScore > bestFlexProjectedScore)
      || (!selectByRaw && projectedScore === bestFlexProjectedScore && score > bestFlexScore)
      || (!selectByRaw && projectedScore === bestFlexProjectedScore && score === bestFlexScore && assign.length > bestFlexAssign.length)
      || (!selectByRaw && projectedScore === bestFlexProjectedScore && score === bestFlexScore && assign.length === bestFlexAssign.length && rawScore > bestFlexRawScore)
      || (!selectByRaw && projectedScore === bestFlexProjectedScore && score === bestFlexScore && assign.length === bestFlexAssign.length && rawScore === bestFlexRawScore && bonusPotential > bestFlexBonusPotential)
    ) {
      bestFlexProjectedScore = projectedScore;
      bestFlexScore = score;
      bestFlexRawScore = rawScore;
      bestFlexBonusPotential = bonusPotential;
      bestFlexAssign = assign.map(x => ({ ...x }));
    }
  };

  const allFlexCandidates = scoringCandidateSubjects.map(subject => {
    const found = flexAvail.find(f => f.subject === subject);
    return found || { subject, category: getSubjectCategory(subject), grade: results[subject], raw: getRawScore(subject, results[subject]) };
  });

  const slotCandidates = allSlots.map((slot, slotIdx) => {
    const candidates = allFlexCandidates
      .map(subj => {
        const weight = getSlotWeight(slot, subj.subject);
        if (weight === null) return null;
        if (slot.isRequired) {
          const explicit = Object.keys(slot.subjectWeights).filter(s => s !== 'any_one_subject');
          if (explicit.length > 0 && !explicit.includes(subj.subject)) return null;
        }
        return {
          subject: subj.subject,
          category: subj.category,
          grade: subj.grade,
          raw: subj.raw,
          weight,
          weightedScore: subj.raw * weight,
          slotIdx,
          ruleIndex: slot.ruleIndex,
          allowedCategories: slot.allowedCategories,
          subjectPriority: (slot as typeof slot & { subjectPriority?: string[] }).subjectPriority,
        };
      })
      .filter((candidate): candidate is {
        subject: string;
        category: 'A' | 'B' | 'C';
        grade: string;
        raw: number;
        weight: number;
        weightedScore: number;
        slotIdx: number;
        ruleIndex: number;
        allowedCategories: string[];
        subjectPriority: string[] | undefined;
      } => candidate !== null);

    candidates.sort((a, b) => {
      if (selectByRaw && a.raw !== b.raw) {
        return b.raw - a.raw;
      }
      if (selectByRaw) {
        const aSubjectPriority = getRuleSubjectPriority(a.subject, a.subjectPriority);
        const bSubjectPriority = getRuleSubjectPriority(b.subject, b.subjectPriority);
        if (aSubjectPriority !== bSubjectPriority) {
          return aSubjectPriority - bSubjectPriority;
        }
      }
      if (a.weightedScore !== b.weightedScore) {
        return b.weightedScore - a.weightedScore;
      }
      if (a.raw !== b.raw) {
        return b.raw - a.raw;
      }

      return compareSubjectsForRule(
        a.subject,
        b.subject,
        a.allowedCategories,
        a.subjectPriority,
      );
    });

    return {
      slotIdx,
      ruleIndex: slot.ruleIndex,
      candidates,
    };
  });

  const orderedSlots = [...slotCandidates].sort((a, b) => {
    if (a.candidates.length !== b.candidates.length) {
      return a.candidates.length - b.candidates.length;
    }
    return a.slotIdx - b.slotIdx;
  });

  const canBeatCurrentBest = (scoreUpperBound: number, rawUpperBound: number, assignLength: number, bonusUpperBound: number, jointUpperBound: number): boolean => {
    const projectedUpperBound = Math.min(scoreUpperBound + bonusUpperBound, jointUpperBound);
    if (selectByRaw) {
      if (rawUpperBound > bestFlexRawScore) return true;
      if (rawUpperBound < bestFlexRawScore) return false;
      if (projectedUpperBound > bestFlexProjectedScore) return true;
      if (projectedUpperBound < bestFlexProjectedScore) return false;
      if (scoreUpperBound > bestFlexScore) return true;
      if (scoreUpperBound < bestFlexScore) return false;
      if (assignLength > bestFlexAssign.length) return true;
      if (assignLength < bestFlexAssign.length) return false;
      return true;
    }

    if (projectedUpperBound > bestFlexProjectedScore) return true;
    if (projectedUpperBound < bestFlexProjectedScore) return false;
    if (scoreUpperBound > bestFlexScore) return true;
    if (scoreUpperBound < bestFlexScore) return false;
    if (assignLength > bestFlexAssign.length) return true;
    return assignLength === bestFlexAssign.length
      && rawUpperBound > bestFlexRawScore;
  };

  const computeUpperBound = (startIdx: number, usedSubjects: Set<string>, score: number, rawScore: number, assignLength: number) => {
    const remainingCapacity = Math.max(0, remainingMainFlexSlots - assignLength);
    if (remainingCapacity <= 0) {
      return { scoreUpperBound: score, rawUpperBound: rawScore, maxAssignLength: assignLength, jointUpperBound: Number.POSITIVE_INFINITY };
    }

    const remainingSlotBestScores: number[] = [];
    const remainingSlotBestRawScores: number[] = [];
    const remainingSubjectBestScores = new Map<string, number>();
    const remainingSubjectRawScores = new Map<string, number>();
    let remainingSlotCount = 0;

    for (let idx = startIdx; idx < orderedSlots.length; idx += 1) {
      const slot = orderedSlots[idx];
      const candidates = slot.candidates.filter(candidate => (
        !usedSubjects.has(candidate.subject)
        && !hasForbiddenConflict(candidate.subject, usedSubjects)
      ));
      if (candidates.length === 0) continue;
      remainingSlotBestScores.push(Math.max(...candidates.map(candidate => candidate.weightedScore)));
      remainingSlotBestRawScores.push(Math.max(...candidates.map(candidate => candidate.raw)));
      remainingSlotCount += 1;
      for (const candidate of candidates) {
        remainingSubjectBestScores.set(
          candidate.subject,
          Math.max(remainingSubjectBestScores.get(candidate.subject) ?? Number.NEGATIVE_INFINITY, candidate.weightedScore),
        );
        remainingSubjectRawScores.set(candidate.subject, candidate.raw);
      }
    }

    remainingSlotBestScores.sort((a, b) => b - a);
    remainingSlotBestRawScores.sort((a, b) => b - a);

    // Bound by both available slots and distinct subjects: one high-weight subject
    // cannot fill every slot, even when all slots share the same candidate list.
    const subjectBestScores = [...remainingSubjectBestScores.values()].sort((a, b) => b - a);
    const subjectRawScores = [...remainingSubjectRawScores.values()].sort((a, b) => b - a);
    const maxAdditionalAssignments = Math.min(remainingCapacity, remainingSlotCount, remainingSubjectBestScores.size);
    const scoreUpperBound = score + Math.min(
      remainingSlotBestScores.slice(0, remainingCapacity).reduce((sum, value) => sum + value, 0),
      subjectBestScores.slice(0, remainingCapacity).reduce((sum, value) => sum + value, 0),
    );
    const rawUpperBound = rawScore + Math.min(
      remainingSlotBestRawScores.slice(0, remainingCapacity).reduce((sum, value) => sum + value, 0),
      subjectRawScores.slice(0, remainingCapacity).reduce((sum, value) => sum + value, 0),
    );
    const maxAssignLength = assignLength + maxAdditionalAssignments;

    let jointUpperBound = Number.POSITIVE_INFINITY;
    if (effectiveBonusRules?.length === 1 && (effectiveBonusRules[0].count || 1) === 1) {
      const rankedSubjects = [...remainingSubjectBestScores].sort((a, b) => b[1] - a[1]);
      jointUpperBound = scoreUpperBound;
      for (const subject of scoringCandidateSubjects) {
        if (usedSubjects.has(subject)) continue;
        const bonus = getBonusPotentialUpperBound(usedSubjects, subject);
        if (bonus <= 0) continue;

        // Reserve the bonus subject before bounding the main slots so a boosted
        // subject cannot contribute to both parts of the optimistic total.
        const mainScore = rankedSubjects
          .filter(([mainSubject]) => mainSubject !== subject)
          .slice(0, remainingCapacity)
          .reduce((sum, [, value]) => sum + value, 0);
        jointUpperBound = Math.max(jointUpperBound, score + mainScore + bonus);
      }
    }

    return { scoreUpperBound, rawUpperBound, maxAssignLength, jointUpperBound };
  };

  const seedGreedyAssignment = () => {
    const usedSubjects = new Set<string>(baseFlexUsedSubjects);
    const assign: typeof bestFlexAssign = [];
    let score = 0;
    let rawScore = 0;

    for (const slot of orderedSlots) {
      if (assign.length >= remainingMainFlexSlots) {
        break;
      }

      const candidate = slot.candidates.find(item => (
        !usedSubjects.has(item.subject)
        && !hasForbiddenConflict(item.subject, usedSubjects)
        && canSelectSubjectForScore(item.subject, getProjectedCategoryCounts(assign), assign.map(entry => entry.subject))
      ));
      if (!candidate) continue;

      usedSubjects.add(candidate.subject);
      assign.push({
        subject: candidate.subject,
        category: candidate.category,
        grade: candidate.grade,
        raw: candidate.raw,
        weight: candidate.weight,
        slotIdx: candidate.slotIdx,
        ruleIndex: candidate.ruleIndex,
      });
      score += candidate.weightedScore;
      rawScore += candidate.raw;
    }

    updateBestFlexAssignment(assign, score, rawScore);
  };

  const search = (
    slotOrderIdx: number,
    assign: typeof bestFlexAssign,
    usedSubjects: Set<string>,
    score: number,
    rawScore: number,
  ) => {
    if (slotOrderIdx === orderedSlots.length || assign.length >= remainingMainFlexSlots) {
      updateBestFlexAssignment(assign, score, rawScore);
      return;
    }

    const { scoreUpperBound, rawUpperBound, maxAssignLength, jointUpperBound } = computeUpperBound(
      slotOrderIdx,
      usedSubjects,
      score,
      rawScore,
      assign.length,
    );
    const bonusUpperBound = getBonusPotentialUpperBound(usedSubjects);
    if (!canBeatCurrentBest(scoreUpperBound, rawUpperBound, maxAssignLength, bonusUpperBound, jointUpperBound)) {
      return;
    }

    const slot = orderedSlots[slotOrderIdx];

    for (const candidate of slot.candidates) {
      if (usedSubjects.has(candidate.subject)) continue;
      if (hasForbiddenConflict(candidate.subject, usedSubjects)) continue;
      if (!canSelectSubjectForScore(candidate.subject, getProjectedCategoryCounts(assign), assign.map(item => item.subject))) continue;

      usedSubjects.add(candidate.subject);
      assign.push({
        subject: candidate.subject,
        category: candidate.category,
        grade: candidate.grade,
        raw: candidate.raw,
        weight: candidate.weight,
        slotIdx: candidate.slotIdx,
        ruleIndex: candidate.ruleIndex,
      });

      search(
        slotOrderIdx + 1,
        assign,
        usedSubjects,
        score + candidate.weightedScore,
        rawScore + candidate.raw,
      );

      assign.pop();
      usedSubjects.delete(candidate.subject);
    }

    search(slotOrderIdx + 1, assign, usedSubjects, score, rawScore);
  };

  if (remainingMainFlexSlots > 0) {
    seedGreedyAssignment();
    search(0, [], new Set<string>(baseFlexUsedSubjects), 0, 0);
  } else {
    updateBestFlexAssignment([], 0, 0);
  }

  for (const item of bestFlexAssign) {
    if (breakdown.length < (scoringFormula.num_of_subjects || 5)) {
      breakdown.push({
        subject: item.subject,
        grade: item.grade,
        rawScore: item.raw,
        weight: item.weight,
        weightedScore: item.raw * item.weight,
        description: `Flexible slot ${item.slotIdx + 1}`,
        category: item.category,
        mandatory: false,
        slotType: 'REMAINING',
        slotIdx: item.slotIdx
      });
      totalScore += item.raw * item.weight;
      availableSubjects.delete(item.subject);
      categoryCounts[item.category]++;
    }
  }

  if (breakdown.length < mainSlotTarget && availableSubjects.size > 0 && mandatoryFallbackSlots.length > 0) {
    const fallbackUsedSubjects = new Set(breakdown.map(item => item.subject));

    for (const slot of mandatoryFallbackSlots) {
      if (breakdown.length >= mainSlotTarget) break;

      let bestCandidate: { subject: string; grade: string; raw: number; weight: number; weightedScore: number; category: 'A' | 'B' | 'C' } | null = null;
      for (const subject of Array.from(availableSubjects)) {
        if (!matchesRuleCategoryList(subject, slot.allowedCategories)) continue;
        if (subjectListIncludes(slot.excludeSubjects, subject)) continue;
        if (hasForbiddenConflict(subject, fallbackUsedSubjects)) continue;
        if (!canSelectSubjectForScore(subject, getProjectedCategoryCounts(), breakdown.map(item => item.subject))) continue;

        const grade = results[subject];
        const raw = getRawScore(subject, grade);
        const weightedScore = raw * slot.weight;
        const candidate = {
          subject,
          grade,
          raw,
          weight: slot.weight,
          weightedScore,
          category: getSubjectCategory(subject),
        };

        if (isBetterRuleCandidate(candidate, bestCandidate, slot.allowedCategories, undefined)) {
          bestCandidate = candidate;
        }
      }

      if (!bestCandidate) continue;

      breakdown.push({
        subject: bestCandidate.subject,
        grade: bestCandidate.grade,
        rawScore: bestCandidate.raw,
        weight: bestCandidate.weight,
        weightedScore: bestCandidate.weightedScore,
        description: 'Main slot fallback',
        category: bestCandidate.category,
        mandatory: false,
        slotType: 'REMAINING'
      });
      totalScore += bestCandidate.weightedScore;
      availableSubjects.delete(bestCandidate.subject);
      fallbackUsedSubjects.add(bestCandidate.subject);
      categoryCounts[bestCandidate.category]++;
      if (unresolvedRequiredSlots > 0) {
        unresolvedRequiredSlots--;
      }
    }
  }

  if (unresolvedRequiredSlots === 0 && breakdown.length < mainSlotTarget && availableSubjects.size > 0) {
    const topUpUsedSubjects = new Set(breakdown.map(item => item.subject));

    while (breakdown.length < mainSlotTarget) {
      let bestCandidate: {
        subject: string;
        grade: string;
        raw: number;
        weight: number;
        weightedScore: number;
        category: 'A' | 'B' | 'C';
      } | null = null;

      for (const subject of Array.from(availableSubjects)) {
        if ((flexSlots.length > 0 || mandatoryFallbackSlots.length > 0) && !isEligibleForSupplementalSlot(subject)) {
          continue;
        }
        if (hasForbiddenConflict(subject, topUpUsedSubjects)) continue;
        if (!canSelectSubjectForScore(subject, getProjectedCategoryCounts())) continue;

        const grade = results[subject];
        const raw = getRawScore(subject, grade);
        const weight = estimateBestRemainingSubjectWeight(scoringFormula, subject);
        const weightedScore = raw * weight;
        const candidate = {
          subject,
          grade,
          raw,
          weight,
          weightedScore,
          category: getSubjectCategory(subject),
        };

        if (!bestCandidate) {
          bestCandidate = candidate;
          continue;
        }

        if (selectByRaw && candidate.raw !== bestCandidate.raw) {
          if (candidate.raw > bestCandidate.raw) {
            bestCandidate = candidate;
          }
          continue;
        }

        if (candidate.weightedScore !== bestCandidate.weightedScore) {
          if (candidate.weightedScore > bestCandidate.weightedScore) {
            bestCandidate = candidate;
          }
          continue;
        }

        if (candidate.raw !== bestCandidate.raw) {
          if (candidate.raw > bestCandidate.raw) {
            bestCandidate = candidate;
          }
          continue;
        }

        const preserveBonusSubject = preferNonBonusEligibleMainSubject(candidate.subject, bestCandidate.subject);
        if (preserveBonusSubject !== null) {
          if (preserveBonusSubject) {
            bestCandidate = candidate;
          }
          continue;
        }

        if (candidate.subject.localeCompare(bestCandidate.subject) < 0) {
          bestCandidate = candidate;
        }
      }

      if (!bestCandidate) {
        break;
      }

      breakdown.push({
        subject: bestCandidate.subject,
        grade: bestCandidate.grade,
        rawScore: bestCandidate.raw,
        weight: bestCandidate.weight,
        weightedScore: bestCandidate.weightedScore,
        description: 'Best remaining slot',
        category: bestCandidate.category,
        mandatory: false,
        slotType: 'REMAINING'
      });
      totalScore += bestCandidate.weightedScore;
      availableSubjects.delete(bestCandidate.subject);
      topUpUsedSubjects.add(bestCandidate.subject);
      categoryCounts[bestCandidate.category]++;
    }
  }

  const replacementResult = applyPostSelectionReplacementRules(program, scoringFormula, results, breakdown);
  totalScore = replacementResult.total;
  replacementResult.usedSourceSubjects.forEach(subject => {
    availableSubjects.delete(subject);
  });

  totalScore = applyWeightedElectiveCapToBreakdown(breakdown, scoringFormula);

  const buildSubjectWeightMap = (): Record<string, number> => {
    const subjectWeights: Record<string, number> = {};
    if (activeSubjectRules.length > 0) {
      for (const rule of activeSubjectRules) {
        for (const [subj, w] of Object.entries(rule.subject_weights || {})) {
          if (typeof w === 'number') {
            const canonicalSubject = subj === 'any_one_subject' ? subj : canonicalizeSubjectName(subj);
            subjectWeights[canonicalSubject] = Math.max(subjectWeights[canonicalSubject] ?? Number.NEGATIVE_INFINITY, w);
          }
        }
      }
    }
    return subjectWeights;
  };

  if (hasRequiredOrMandatoryRules
    && Array.isArray(effectiveBonusRules)
    && effectiveBonusRules.length > 0) {
    const usedSubjects = new Set(breakdown.map(item => item.subject));
    const subjectWeights = buildSubjectWeightMap();
    const getBonusScore = (rule: any, subject: string, grade: string, rawScore: number): number => {
      if (rule.formula === 'percentage_of_max') {
        const category = getSubjectCategory(subject);
        const baselineGrade = category === 'C'
          ? (rule.category_c_bonus_baseline || 'C2')
          : (rule.category_a_bonus_baseline || '5**');
        const baselineMax = getGradeScore(baselineGrade, program, subject);
        const percentageBase = rule.base_score ?? effectiveBonusBaseScore;
        if (baselineMax > 0) {
          return (rawScore / baselineMax) * percentageBase * rule.rate;
        }
        return 0;
      }

      if (rule.formula === 'percentage_of_weighted_score') {
        const effectiveWeights = rule.bonus_subject_weights || subjectWeights;
        const weight = getResolvedSubjectWeight(effectiveWeights, subject);

        return rawScore * (weight ?? 1) * (rule.rate || 0);
      }

      return 0;
    };

    const getBonusDisplayWeight = (rule: any, subject: string): number => {
      if (rule.formula !== 'percentage_of_weighted_score') {
        return 1;
      }
      const effectiveWeights = rule.bonus_subject_weights || subjectWeights;
      return getResolvedSubjectWeight(effectiveWeights, subject) ?? 1;
    };

    for (const rule of effectiveBonusRules) {
      if (!canActivateBonusRule(rule)) {
        continue;
      }
      const bonusCount = rule.count || 1;
      for (let i = 0; i < bonusCount; i++) {
        let bestBonusSubject: string | null = null;
        let bestBonusScore = -1;

        for (const subject of Array.from(availableSubjects)) {
          if (usedSubjects.has(subject)) continue;
          if (hasForbiddenConflict(subject, usedSubjects)) continue;

          const allowedCategories = rule.categories || getProgrammeDefaultRuleCategories(program, false);
          const categoryAllowed = matchesRuleCategoryList(subject, allowedCategories);
          if (!categoryAllowed) continue;

          const bonusCategoryCounts = { A: 0, B: 0, C: 0 };
          usedSubjects.forEach(usedSubject => {
            bonusCategoryCounts[getSubjectCategory(usedSubject)]++;
          });
          if (!canSelectSubjectForScore(subject, bonusCategoryCounts, Array.from(usedSubjects), { isBonus: true })) continue;

          const grade = results[subject];
          const rawScore = getRawScore(subject, grade);
          if (rawScore < (rule.min_level || 0)) continue;

          const bonusScore = getBonusScore(rule, subject, grade, rawScore);
          if (
            rule.formula === 'percentage_of_max'
              ? isBetterSixthSubjectBonusCandidate(
                subject,
                rawScore,
                bestBonusSubject,
                bestBonusSubject ? getRawScore(bestBonusSubject, results[bestBonusSubject]) : -1,
              )
              : (
            bonusScore > bestBonusScore
            || (
              bonusScore === bestBonusScore
              && bestBonusSubject !== null
              && getRuleCategoryPriority(subject, allowedCategories) < getRuleCategoryPriority(bestBonusSubject, allowedCategories)
            )
            || (
              bonusScore === bestBonusScore
              && bestBonusSubject !== null
              && getRuleCategoryPriority(subject, allowedCategories) === getRuleCategoryPriority(bestBonusSubject, allowedCategories)
              && subject.localeCompare(bestBonusSubject) > 0
            )
            || (
              bonusScore === bestBonusScore
              && bestBonusSubject === null
            )
              )
          ) {
            bestBonusScore = bonusScore;
            bestBonusSubject = subject;
          }
        }

        if (!bestBonusSubject || bestBonusScore <= 0) {
          continue;
        }

        const grade = results[bestBonusSubject];
        const rawScore = getRawScore(bestBonusSubject, grade);
        const displayWeight = getBonusDisplayWeight(rule, bestBonusSubject);
        const displayBonusWeight = rule.formula === 'percentage_of_weighted_score'
          ? roundToDecimals(displayWeight * (rule.rate || 0), 2)
          : displayWeight;
        const roundedBonusScore = roundToDecimals(bestBonusScore, 2);
        breakdown.push({
          subject: bestBonusSubject,
          grade,
          rawScore,
          weight: displayBonusWeight,
          weightedScore: roundedBonusScore,
          bonusScore: roundedBonusScore,
          description: shouldHideBonus ? `Best remaining slot` : rule.description || 'Bonus Subject',
          category: getSubjectCategory(bestBonusSubject),
          mandatory: false,
          slotType: shouldHideBonus ? 'REMAINING' : 'BONUS',
          counted: true,
        });
        totalScore += roundedBonusScore;
        availableSubjects.delete(bestBonusSubject);
        usedSubjects.add(bestBonusSubject);
      }
    }
  }

  if (!hasRequiredOrMandatoryRules
    && Array.isArray(effectiveBonusRules)
    && effectiveBonusRules.length === 1) {
    const bonusRule = effectiveBonusRules[0];
    const getBonusDisplayWeight = (subject: string, subjectWeights: Record<string, number>): number => {
      if (bonusRule.formula !== 'percentage_of_weighted_score') {
        return 1;
      }
      const effectiveWeights = bonusRule.bonus_subject_weights || subjectWeights;
      return getResolvedSubjectWeight(effectiveWeights, subject) ?? 1;
    };
    const calcBonusScore = (subject: string, rawScore: number, subjectWeights: Record<string, number>): number => {
      if (bonusRule.formula === 'percentage_of_max') {
        let baselineMax = 8.5;
        const bonusCat = getSubjectCategory(subject);
        if (bonusCat === 'C') {
          const baselineGrade = bonusRule.category_c_bonus_baseline || 'C2';
          baselineMax = getGradeScore(baselineGrade, program, subject);
        }
        const percentageBase = bonusRule.base_score ?? effectiveBonusBaseScore;
        if (baselineMax > 0) {
          return (rawScore / baselineMax) * percentageBase * bonusRule.rate;
        }
        return 0;
      }

      if (bonusRule.formula === 'percentage_of_weighted_score') {
        const effectiveWeights = bonusRule.bonus_subject_weights || subjectWeights;
        const weight = getResolvedSubjectWeight(effectiveWeights, subject);
        return rawScore * (weight ?? 1) * bonusRule.rate;
      }

      return 0;
    };

    const allSubjects = Array.from(new Set(scoringCandidateSubjects));
    const shouldUseBruteForce = !isMaxCalculation && allSubjects.length <= 12;

    if (!shouldUseBruteForce) {
      const subjectWeights = buildSubjectWeightMap();
      let bestBonusSubject: string | null = null;
      let bestBonusScore = -1;

      for (const subject of Array.from(availableSubjects)) {
        if (hasForbiddenConflict(subject, new Set(breakdown.map(item => item.subject)))) continue;

        const allowedCategories = bonusRule.categories || getProgrammeDefaultRuleCategories(program, false);
        if (!matchesRuleCategoryList(subject, allowedCategories)) continue;
        if (!canSelectSubjectForScore(subject, getProjectedCategoryCounts(), breakdown.map(item => item.subject), { isBonus: true })) continue;

        const rawScore = getRawScore(subject, results[subject]);
        if (rawScore < (bonusRule.min_level || 0)) continue;

        const bonusScore = calcBonusScore(subject, rawScore, subjectWeights);
        if (
          bonusRule.formula === 'percentage_of_max'
            ? isBetterSixthSubjectBonusCandidate(
              subject,
              rawScore,
              bestBonusSubject,
              bestBonusSubject ? getRawScore(bestBonusSubject, results[bestBonusSubject]) : -1,
            )
            : (
          bonusScore > bestBonusScore
          || (
            bonusScore === bestBonusScore
            && bestBonusSubject !== null
            && getRuleCategoryPriority(subject, allowedCategories) < getRuleCategoryPriority(bestBonusSubject, allowedCategories)
          )
          || (
            bonusScore === bestBonusScore
            && bestBonusSubject !== null
            && getRuleCategoryPriority(subject, allowedCategories) === getRuleCategoryPriority(bestBonusSubject, allowedCategories)
            && subject.localeCompare(bestBonusSubject) > 0
          )
          || (
            bonusScore === bestBonusScore
            && bestBonusSubject === null
              )
          )
        ) {
          bestBonusScore = bonusScore;
          bestBonusSubject = subject;
        }
      }

      if (bestBonusSubject && bestBonusScore > 0) {
        const displayWeight = getBonusDisplayWeight(bestBonusSubject, subjectWeights);
        const displayBonusWeight = bonusRule.formula === 'percentage_of_weighted_score'
          ? roundToDecimals(displayWeight * (bonusRule.rate || 0), 2)
          : displayWeight;
        const roundedBonusScore = roundToDecimals(bestBonusScore, 2);
        breakdown.push({
          subject: bestBonusSubject,
          grade: results[bestBonusSubject],
          rawScore: getRawScore(bestBonusSubject, results[bestBonusSubject]),
          weight: displayBonusWeight,
          weightedScore: roundedBonusScore,
          bonusScore: roundedBonusScore,
          description: shouldHideBonus ? `Best remaining slot` : bonusRule.description || 'Bonus Subject',
          category: getSubjectCategory(bestBonusSubject),
          mandatory: false,
          slotType: shouldHideBonus ? 'REMAINING' : 'BONUS',
          counted: true,
        });
        totalScore += roundedBonusScore;
        availableSubjects.delete(bestBonusSubject);
      }
    } else {
      let bestTotal = totalScore;
      let bestMainBreakdown: any[] = [...breakdown];
      let bestBonusBreakdown: any = null;
      const combinations = (arr: string[], k: number): string[][] => {
        if (k === 0) return [[]];
        if (arr.length === 0 || k > arr.length) return [];
        const [first, ...rest] = arr;
        const combsWith = combinations(rest, k - 1).map(c => [first, ...c]);
        const combsWithout = combinations(rest, k);
        return [...combsWith, ...combsWithout];
      };
      const mainSubjectWeights = buildSubjectWeightMap();
      const sixSubjCombos = combinations(allSubjects, 6);
      for (const six of sixSubjCombos) {
        for (let i = 0; i < 6; ++i) {
          const main = six.slice(0, i).concat(six.slice(i + 1));
          const bonus = six[i];
          const allowedCategories = bonusRule.categories || getProgrammeDefaultRuleCategories(program, true);
          const selectedSubjects = [...main, bonus];
          if (
            flexForbiddenGroups
            && countSubjectsAfterForbiddenGroupDedup(selectedSubjects, flexForbiddenGroups) !== selectedSubjects.length
          ) {
            continue;
          }
          // Validate subject_group_limits on main 5
          if (violatesSubjectGroupLimitsForSelection(new Set(main), scoringFormula)) {
            continue;
          }
          // Validate bonus subject against group limits (max_count_bonus + no_duplicate_category_in_bonus)
          if (violatesSubjectGroupLimitsForBonus(bonus, new Set(main), scoringFormula)) {
            continue;
          }
          let mainScore = 0;
          const mainBreakdown: any[] = [];
          for (const subj of main) {
            const raw = getRawScore(subj, results[subj]);
            let weight = 1.0;
            if (mainSubjectWeights[subj] !== undefined) {
              weight = mainSubjectWeights[subj];
            } else if (mainSubjectWeights['any_one_subject'] !== undefined) {
              weight = mainSubjectWeights['any_one_subject'];
            }
            mainScore += raw * weight;
            mainBreakdown.push({
              subject: subj,
              grade: results[subj],
              rawScore: raw,
              weight,
              weightedScore: raw * weight,
              mandatory: false,
              slotType: 'REMAINING',
              counted: true
            });
          }
          const bonusRaw = getRawScore(bonus, results[bonus]);
          const bonusCat = getSubjectCategory(bonus);
          const bonusScore = calcBonusScore(bonus, bonusRaw, mainSubjectWeights);
          const roundedBonusScore = roundToDecimals(bonusScore, 2);
          const bonusWeight = getBonusDisplayWeight(bonus, mainSubjectWeights);
          if (
            !main.includes(bonus)
            && matchesRuleCategoryList(bonus, allowedCategories)
            && bonusRaw >= (bonusRule.min_level || 0)
          ) {
            const total = mainScore + roundedBonusScore;
            if (total > bestTotal) {
              bestTotal = total;
              bestMainBreakdown = mainBreakdown;
              bestBonusBreakdown = {
                subject: bonus,
                grade: results[bonus],
                rawScore: bonusRaw,
                weight: roundToDecimals(bonusWeight * (bonusRule.rate || 0), 2),
                weightedScore: roundedBonusScore,
                bonusScore: roundedBonusScore,
                description: shouldHideBonus ? `Best remaining slot` : bonusRule.description || 'Bonus Subject',
                category: bonusCat,
                mandatory: false,
                slotType: shouldHideBonus ? 'REMAINING' : 'BONUS',
                counted: true
              };
            }
          }
        }
      }
      if (bestMainBreakdown.length > 0 && bestBonusBreakdown) {
        const filteredBreakdown = [...bestMainBreakdown, bestBonusBreakdown];
        const filteredTotal = filteredBreakdown.reduce((sum, x) => sum + x.weightedScore, 0);
        return { total: filteredTotal, breakdown: filteredBreakdown };
      }
    }
  }

  return { total: roundToDecimals(totalScore, 2), breakdown };
};

// Caches for max/min results + scores (programme-dependent only, reusable across renders)
const _maxResultsCache = new Map<string, DSEResults>();
const _minResultsCache = new Map<string, DSEResults>();
const _maxScoreCache = new Map<string, number>();
const _minScoreCache = new Map<string, number>();

/**
 * Create max results for a programme (all subjects at 5**)
 */
const createMaxResults = (program: JupasProgram): DSEResults => {
  const key = program.jupas_id;
  if (_maxResultsCache.has(key)) return _maxResultsCache.get(key)!;
  const maxResults: DSEResults = {};

  // Add Cores
  maxResults["English Language"] = "5**";
  maxResults["Chinese Language"] = "5**";
  maxResults["Mathematics"] = "5**";
  maxResults["Citizenship and Social Development"] = "A";

  // All programmes now use new format: extract subjects from subject_rules
  const scoringFormula = program.scoring_formula as ScoringFormula;
  const subjectRules = scoringFormula?.subject_rules || [];
  subjectRules.forEach(rule => {
    Object.keys(rule.subject_weights || {}).forEach(subject => {
      if (subject !== 'any_one_subject') {
        maxResults[subject] = "5**";
      }
    });
  });

  // For rules with any_one_subject, add common high-scoring subjects
  const hasWildcard = subjectRules.some(rule =>
    rule.subject_weights?.['any_one_subject'] !== undefined
  );
  if (hasWildcard) {
    maxResults["Physics"] = "5**";
    maxResults["Chemistry"] = "5**";
    maxResults["Biology"] = "5**";
    maxResults["Economics"] = "5**";
  }

  // If no subject_rules but has num_of_subjects, ensure enough electives for the calculation
  // This handles programmes like JS4501/JS4502 which only have num_of_subjects without rules
  if (subjectRules.length === 0 && scoringFormula?.num_of_subjects) {
    const numSubjects = scoringFormula.num_of_subjects;
    const coreCount = 3; // Eng, Chi, Math (CSD doesn't count in scoring)
    const electivesNeeded = Math.max(0, numSubjects - coreCount);

    // Add common high-scoring electives if needed
    const commonElectives = ["Physics", "Chemistry", "Biology", "Economics", "Geography", "History"];
    for (let i = 0; i < Math.min(electivesNeeded, commonElectives.length); i++) {
      maxResults[commonElectives[i]] = "5**";
    }
  }

  _maxResultsCache.set(program.jupas_id, maxResults);
  return maxResults;
};

/**
 * Create min results for a programme (all subjects at minimum passing grade)
 */
const createMinResults = (program: JupasProgram): DSEResults => {
  const key = program.jupas_id;
  if (_minResultsCache.has(key)) return _minResultsCache.get(key)!;
  const minResults: DSEResults = {};

  // Add Cores - using minimum acceptable levels
  minResults["English Language"] = "3";
  minResults["Chinese Language"] = "3";
  minResults["Mathematics"] = "2";
  minResults["Citizenship and Social Development"] = "A";

  // All programmes now use new format: extract subjects from subject_rules
  const scoringFormula = program.scoring_formula as ScoringFormula;
  const subjectRules = scoringFormula?.subject_rules || [];
  subjectRules.forEach(rule => {
    Object.keys(rule.subject_weights || {}).forEach(subject => {
      if (subject !== 'any_one_subject') {
        minResults[subject] = "2";
      }
    });
  });

  // For rules with any_one_subject, add common subjects
  const hasWildcard = subjectRules.some(rule =>
    rule.subject_weights?.['any_one_subject'] !== undefined
  );
  if (hasWildcard) {
    minResults["Physics"] = "2";
    minResults["Chemistry"] = "2";
    minResults["Biology"] = "2";
    minResults["Economics"] = "2";
  }

  // If no subject_rules but has num_of_subjects, ensure enough electives for the calculation
  if (subjectRules.length === 0 && scoringFormula?.num_of_subjects) {
    const numSubjects = scoringFormula.num_of_subjects;
    const coreCount = 3; // Eng, Chi, Math (CSD doesn't count in scoring)
    const electivesNeeded = Math.max(0, numSubjects - coreCount);

    // Add common electives if needed
    const commonElectives = ["Physics", "Chemistry", "Biology", "Economics", "Geography", "History"];
    for (let i = 0; i < Math.min(electivesNeeded, commonElectives.length); i++) {
      minResults[commonElectives[i]] = "2";
    }
  }

  _minResultsCache.set(program.jupas_id, minResults);
  return minResults;
};

// ============================================================================
// Main API Function
// ============================================================================

/**
 * Main API: Check DSE results against a programme
 */
export const check_dse_results = (input: { jupas_id: string | number, dse_results: DSEResults, programs: JupasProgram[], program?: JupasProgram, fastMode?: boolean }): CalculationResult | null => {
  const { jupas_id, programs } = input;
  // Use pre-looked-up program if provided (O(1)), otherwise fall back to find (O(n))
  const program = input.program || programs.find(p => p.jupas_id === String(jupas_id));

  if (!program) {
    console.error("Program not found");
    return null;
  }

  const calculationStartTime = calculatorTraceNow();
  traceCalculator('programme:start', {
    jupasId: program.jupas_id,
    name: program.name_eng,
    usedProgramOverride: Boolean(input.program),
  });

  const dse_results = filterInstitutionExcludedResults(program, input.dse_results);

  // 1. Validation
  const merStartTime = calculatorTraceNow();
  const mer = checkMER(program, dse_results);
  const merDuration = calculatorTraceNow() - merStartTime;

  // 2. Score Calculation (Real)
  const realCalcStartTime = calculatorTraceNow();
  const realCalc = calculateScore(program, dse_results);
  const realCalcDuration = calculatorTraceNow() - realCalcStartTime;

  const fastMode = input.fastMode === true;

  // 3. Score Calculation (Max Possible) — cached per programme since max results are deterministic
  const maxCalcStartTime = calculatorTraceNow();
  let maxCalcTotal: number;
  let maxCalcCacheHit = false;
  if (fastMode) {
    maxCalcTotal = realCalc.total;
    maxCalcCacheHit = true;
  } else if (_maxScoreCache.has(program.jupas_id)) {
    maxCalcTotal = _maxScoreCache.get(program.jupas_id)!;
    maxCalcCacheHit = true;
  } else {
    const maxResults = createMaxResults(program);
    maxCalcTotal = calculateScore(program, maxResults, true).total;
    _maxScoreCache.set(program.jupas_id, maxCalcTotal);
  }
  const maxCalc = { total: maxCalcTotal };
  const maxCalcDuration = calculatorTraceNow() - maxCalcStartTime;

  // 4. Score Calculation (Min Possible) — cached per programme since min results are deterministic
  const minCalcStartTime = calculatorTraceNow();
  let minCalcTotal: number;
  let minCalcCacheHit = false;
  if (fastMode) {
    minCalcTotal = realCalc.total;
    minCalcCacheHit = true;
  } else if (_minScoreCache.has(program.jupas_id)) {
    minCalcTotal = _minScoreCache.get(program.jupas_id)!;
    minCalcCacheHit = true;
  } else {
    const minResults = createMinResults(program);
    minCalcTotal = calculateScore(program, minResults, true).total;
    _minScoreCache.set(program.jupas_id, minCalcTotal);
  }
  const minCalc = { total: minCalcTotal };
  const minCalcDuration = calculatorTraceNow() - minCalcStartTime;

  // Format Breakdown
  const formattedBreakdown: { [subject: string]: string } = {};
  realCalc.breakdown.forEach(item => {
    formattedBreakdown[item.subject] = `${item.description} | Grade: ${item.grade} | W: ${item.weight} -> ${item.weightedScore.toFixed(1)}`;
  });

  // Get the effective category limits for this programme
  const effectiveCategoryLimits = getProgrammeCategoryLimits(program);

  // Calculate recommendation score based on past admission scores
  // Use the extracted utility function for better maintainability
  let recommendationRate: number | undefined = undefined;
  const pastScores = program.past_admission_scores;

  if (DEBUG_CALCULATOR) {
    console.log(`🎯 [calculator.ts] Program ${program.jupas_id} - Score calculations:`, {
      minCalc: minCalc.total,
      realCalc: realCalc.total,
      maxCalc: maxCalc.total,
      hasPastScores: !!(pastScores && pastScores.length > 0),
      pastScores: pastScores?.[0]
    });
  }

  if (pastScores && pastScores.length > 0) {
    const recommendationStartTime = calculatorTraceNow();
    const latestScores = pastScores[0];
    if (DEBUG_CALCULATOR) {
      console.log(`📊 [calculator.ts] Program ${program.jupas_id} - Calling calcRecomRate with:`, {
        userScores: {
          min: minCalc.total,
          current: realCalc.total,
          max: maxCalc.total
        },
        admissionScores: {
          LQ: latestScores.LQ,
          MED: latestScores.MED,
          UQ: latestScores.UQ
        }
      });
    }

    // Pass min/max calculated scores for proper recommendation rate calculation
    // debugger;
    recommendationRate = calcRecomRate(
      {
        min: minCalc.total,
        current: realCalc.total,
        max: maxCalc.total
      },
      {
        LQ: latestScores.LQ,
        MED: latestScores.MED,
        UQ: latestScores.UQ
      },
      program.jupas_id
    );

    if (DEBUG_CALCULATOR) {
      console.log(`✅ [calculator.ts] Program ${program.jupas_id} - Recommendation rate result:`, recommendationRate);
    }
    const recommendationDuration = calculatorTraceNow() - recommendationStartTime;
    if (recommendationDuration > 20) {
      warnCalculator('programme:slow-recommendation', {
        jupasId: program.jupas_id,
        name: program.name_eng,
        durationMs: Number(recommendationDuration.toFixed(2)),
      });
    }
  }

  // Limit breakdown to best N subjects if specified
  const limitedBreakdown = program.max_num_of_subjects
    ? realCalc.breakdown
      .sort((a, b) => b.weightedScore - a.weightedScore)
      .slice(0, program.max_num_of_subjects)
    : realCalc.breakdown;

  // if (program.jupas_id === '5118') {
  //   console.log('5118 Breakdown limiting:', {
  //     max_num_of_subjects: program.max_num_of_subjects,
  //     totalBreakdown: realCalc.breakdown.length,
  //     limitedBreakdown: limitedBreakdown.length,
  //     subjects: limitedBreakdown.map(b => b.subject)
  //   });
  // }

  // debugger;

  const totalDuration = calculatorTraceNow() - calculationStartTime;
  const phasePayload = {
    jupasId: program.jupas_id,
    name: program.name_eng,
    totalDurationMs: Number(totalDuration.toFixed(2)),
    phases: {
      merMs: Number(merDuration.toFixed(2)),
      realCalcMs: Number(realCalcDuration.toFixed(2)),
      maxCalcMs: Number(maxCalcDuration.toFixed(2)),
      minCalcMs: Number(minCalcDuration.toFixed(2)),
    },
    maxCalcCacheHit,
    minCalcCacheHit,
    currentScore: Number(realCalc.total.toFixed(2)),
  };
  if (totalDuration > 40 || realCalcDuration > 30) {
    warnCalculator('programme:slow', phasePayload);
  } else {
    traceCalculator('programme:done', phasePayload);
  }

  const calculationResult = {
    jupas_id: program.jupas_id,
    programme_name: program.name_eng,
    mer,
    // GER (General Entrance Requirements) - Standard university-wide requirements
    ger: (() => {
      // Get institution's GER from general_mer - use this data directly
      const instKey = getUniversityKey(program.jupas_id);
      const institutionData = (INSTITUTIONS_DATA as any)[instKey];
      const generalMer = program.general_mer || institutionData?.general_mer || {};

      // GER minimums from institution's general_mer
      const configuredCoreMinima = generalMer.core_minima;
      const gerMinima = configuredCoreMinima && typeof configuredCoreMinima === 'object'
        ? { ...(configuredCoreMinima as Record<string, number | string>) }
        : {
            "English Language": generalMer["English Language"] || 3,
            "Chinese Language": generalMer["Chinese Language"] || 3,
            "Mathematics": generalMer["Mathematics"] || 2,
            "Citizenship and Social Development": generalMer["Citizenship and Social Development"] || "A"
          };

      // Allow programme_mer.core_minima to lower GER requirements (e.g. EdUHK HD programmes)
      if (program.program_mer?.core_minima) {
        for (const [subject, req] of Object.entries(program.program_mer.core_minima)) {
          const normalizedSubject = subject.toUpperCase().startsWith('CITIZENSHIP')
            ? 'Citizenship and Social Development'
            : subject;
          if (normalizedSubject in gerMinima && req !== undefined) {
            const gerVal = gerMinima[normalizedSubject as keyof typeof gerMinima];
            const progVal = req;
            if (typeof gerVal === 'number' && typeof progVal === 'number' && progVal < gerVal) {
              (gerMinima as any)[normalizedSubject] = progVal;
            }
          }
        }
      }

      // Get institution's electives requirements from general_mer
      const gerElectivesCount = generalMer.electives_count || 2;
      const gerElectivesMinLevel = generalMer.electives_min_level || 2;
      const programElectiveRequirements = normalizeElectiveRequirements(program.program_mer.elective_requirements);
      const gerElectiveRequirements = normalizeElectiveRequirements(generalMer.elective_requirements)
        || (program.program_mer.elective_minima && programElectiveRequirements
          ? programElectiveRequirements
          : undefined)
          || programElectiveRequirements;
      const generalMerElectiveAlternatives = Array.isArray(generalMer.elective_alternatives)
        && generalMer.elective_alternatives.length > 0
        ? generalMer.elective_alternatives
        : program.program_mer.elective_alternatives;
      const gerElectiveAlternatives = Array.isArray(generalMerElectiveAlternatives)
        ? generalMerElectiveAlternatives
          .map(path => normalizeElectiveRequirements(path))
          .filter((path): path is NormalizedElectiveRequirement[] => Boolean(path))
        : [];
      const hkustBonusGerRelief = getHkustBonusGerReliefContext(program, realCalc.breakdown, dse_results, instKey);

      // Check cores against GER minimums from general_mer
      const gerCoresMap: { [subject: string]: boolean } = {};
      for (const [subject, minReq] of Object.entries(gerMinima)) {
        const userGrade = dse_results[subject as keyof DSEResults];
        if (!userGrade) {
          gerCoresMap[subject] = false;
          continue;
        }

        const userLevel = getGradeScore(String(userGrade), program);
        let met = false;
        if (minReq === 'A' || minReq === 'Attained') {
          met = isAttainedGrade(userGrade);
        } else {
          met = userLevel >= (typeof minReq === 'number' ? minReq : parseInt(String(minReq)));
        }
        gerCoresMap[subject] = met;
      }

      // GER electives check: count electives with level >= institution's electives_min_level
      const coreSubjects = ["English Language", "Chinese Language", "Mathematics", "Citizenship and Social Development"];
      const gerEligibleElectiveSubjects = Object.entries(dse_results).filter(([subj, grade]) => {
        if (coreSubjects.includes(subj)) return false;
        return getGradeScore(String(grade), program) >= gerElectivesMinLevel;
      }).map(([subj]) => subj);

      const gerElectiveCount = countSubjectsAfterForbiddenGroupDedup(
        gerEligibleElectiveSubjects,
        getEffectiveForbiddenGroups(program, institutionData),
      );

      const gerElectiveRequirementsResult = evaluateElectiveRequirements(
        program,
        dse_results,
        gerElectiveRequirements,
        gerElectivesMinLevel,
        false,
        'extend',
      );
      const gerElectiveAlternativesResult = gerElectiveAlternatives.length > 0
        ? gerElectiveAlternatives.map(path => evaluateElectiveRequirements(
          program,
          dse_results,
          path,
          gerElectivesMinLevel,
          false,
          'extend',
        ) || [])
        : undefined;
      let invalidGerElectiveRequirementSubjects = findInvalidElectiveRequirementSubjects(
        program,
        dse_results,
        gerElectiveRequirements,
        gerElectivesMinLevel,
        false,
        'extend',
      );
      if (hkustBonusGerRelief) {
        const bonusSubjectSet = new Set(hkustBonusGerRelief.bonusSubjects);
        invalidGerElectiveRequirementSubjects = invalidGerElectiveRequirementSubjects.filter(
          subject => !bonusSubjectSet.has(subject),
        );
      }
      // For GER, elective_requirements should be interpreted as "must be able to satisfy the required pattern"
      // rather than "every elective entered must match". Otherwise, a valid extra 6th (bonus) subject
      // (e.g. HKUST Cat B/C bonus) can incorrectly invalidate GER.
      const gerElectiveRequirementsMet = gerElectiveAlternativesResult
        ? gerElectiveAlternativesResult.some(path => path.every(requirement => requirement.met))
        : gerElectiveRequirementsResult
          ? gerElectiveRequirementsResult.every(requirement => requirement.met)
          : true;
      const gerElectivesFound = gerElectiveAlternativesResult
        ? Math.max(
            0,
            ...gerElectiveAlternativesResult.map(path =>
              path.reduce((count, requirement) => count + requirement.matched_subjects.length, 0),
            ),
          )
        : gerElectiveRequirementsResult
          ? gerElectiveRequirementsResult.reduce((count, requirement) => count + requirement.matched_subjects.length, 0)
          : gerElectiveCount;
      const gerElectivesMet = gerElectivesFound >= gerElectivesCount;

      // GER eligibility
      const allCoresMet = Object.values(gerCoresMap).every(v => v === true);
      const gerEligible = allCoresMet
        && (gerElectiveAlternativesResult ? gerElectiveRequirementsMet : gerElectivesMet && gerElectiveRequirementsMet);

      // Debug logging
      if (DEBUG_CALCULATOR) {
        console.log('[GER] Institution:', instKey, 'electives_min_level:', gerElectivesMinLevel, 'electives_count:', gerElectivesCount);
        console.log('[GER] Cores met:', gerCoresMap, '-> allCoresMet:', allCoresMet);
        console.log('[GER] Electives found:', gerElectivesFound, ', required:', gerElectivesCount, '-> met:', gerElectivesMet);
        console.log('[GER] Elective requirements:', gerElectiveRequirementsResult, 'invalid subjects:', invalidGerElectiveRequirementSubjects, '-> met:', gerElectiveRequirementsMet);
      }

      return {
        core_minima: gerCoresMap,
        electives: {
          required: gerElectiveAlternativesResult ? 0 : gerElectivesCount,
          found: gerElectivesFound,
          met: gerElectiveAlternativesResult ? gerElectiveRequirementsMet : gerElectivesMet,
          include_cores: false,
          min_level: gerElectivesMinLevel
        },
        elective_requirements: gerElectiveRequirementsResult,
        elective_alternatives: gerElectiveAlternativesResult,
        eligible: gerEligible,
        remark: typeof generalMer.remark === 'string' ? generalMer.remark : undefined,
        // Add the actual requirement values for display from general_mer
        requirements: gerMinima
      } as any;
    })(),

    // PER (Programme Entrance Requirements) - Programme-specific requirements
    per: (() => {
      // Build requirements object from program_mer.core_minima or scoring_formula
      let requirements: { [subject: string]: number | string } = {};

      if (program.program_mer?.core_minima && Object.keys(program.program_mer.core_minima).length > 0) {
        // Use program_mer.core_minima - filter out undefined values
        for (const [key, value] of Object.entries(program.program_mer.core_minima)) {
          if (value !== undefined) {
            requirements[key] = value;
          }
        }
      } else if (program.scoring_formula?.subject_rules) {
        // Extract requirements from scoring_formula.subject_rules (e.g., PolyU programmes)
        for (const rule of program.scoring_formula.subject_rules) {
          // @ts-ignore
          if (rule.categories?.includes('core') && rule.mandatory) {
            const minLevel = rule.min_level || 2;
            // @ts-ignore
            for (const subject of Object.keys(rule.subject_weights || {})) {
              // Only add subjects that are explicitly in the scoring_formula
              requirements[subject] = minLevel;
            }
          }
        }

        // Add CSD with 'Attained' requirement if user has it
        const csdGrade = dse_results['Citizenship and Social Development'];
        if (csdGrade) {
          requirements['Citizenship and Social Development'] = 'Attained';
        }
      }

      // PER electives min level: use programme-specific or default to 3 (higher than GER's 2)
      const perElectivesMinLevel = program.program_mer?.electives?.min_level || 3;
      requirements['electives_min_level'] = perElectivesMinLevel;

      // Recalculate electives check for PER using the correct min level (3, not 2)
      const coreSubjects = ["English Language", "Chinese Language", "Mathematics", "Citizenship and Social Development"];
      const specificElectiveSubjects = program.program_mer?.specific_electives ? Object.keys(program.program_mer.specific_electives) : [];
      const perSpecificSubjects = (program.program_mer?.electives as any)?.specific_subjects;
      const mathGradeForPer = dse_results["Mathematics"];
      const mathLevelForPer = mathGradeForPer ? getGradeScore(String(mathGradeForPer), program) : 0;
      // JS1202: restriction applies only when Math is exactly Level 2 (not "Math < 3" in general)
      const applySpecificSubjectsForPer =
        Array.isArray(perSpecificSubjects) &&
        perSpecificSubjects.length > 0 &&
        mathLevelForPer === 2;

      const perEligibleElectiveSubjects = Object.entries(dse_results).filter(([subj, grade]) => {
        if (coreSubjects.includes(subj)) return false;
        if (specificElectiveSubjects.includes(subj)) return false;
        if (!isProgramAllowedCategoryBSubject(subj, program)) return false;
        if (!subjectMeetsMerCategoryRequirement(subj, getProgrammeMerAllowedCategories(program))) return false;
        if (applySpecificSubjectsForPer && !perSpecificSubjects.includes(subj)) return false;
        return getGradeScore(String(grade), program, subj) >= perElectivesMinLevel;
      }).map(([subj]) => subj);

      const institutionData = (INSTITUTIONS_DATA as any)[getUniversityKey(program.jupas_id)];
      const perElectiveCount = countSubjectsAfterForbiddenGroupDedup(
        perEligibleElectiveSubjects,
        getEffectiveForbiddenGroups(program, institutionData),
      );

      const perHasElectiveAlternatives = Array.isArray(mer.elective_alternatives)
        && mer.elective_alternatives.length > 0;
      const perElectivesRequired = perHasElectiveAlternatives ? 0 : (program.program_mer?.electives?.count || 2);
      const perElectivesMet = perHasElectiveAlternatives
        ? mer.elective_alternatives!.some(path => path.every(requirement => requirement.met))
        : perElectiveCount >= perElectivesRequired;
      const isJS4238 = program.jupas_id === '4238';
      const js4238ConditionalMath = isJS4238 && mathLevelForPer < 5;
      const js4238ExtendedMathSubjects = [MATH_EXTENDED_MODULE_1, MATH_EXTENDED_MODULE_2];
      const js4238ExtendedMathMatches = js4238ExtendedMathSubjects.filter(subject => {
        const grade = dse_results[subject as keyof DSEResults];
        return grade && getGradeScore(String(grade), program, subject) >= 5;
      });
      const js4238SecondElectiveSubjects = perEligibleElectiveSubjects.filter(
        subject => !js4238ExtendedMathSubjects.includes(subject),
      );
      const js4238SecondElectiveMet = countSubjectsAfterForbiddenGroupDedup(
        js4238SecondElectiveSubjects,
        getEffectiveForbiddenGroups(program, institutionData),
      ) >= 1;
      const isJS1202 = program.jupas_id === '1202';
      const js1202RestrictedElectives = [
        'Biology',
        'Business, Accounting and Financial Studies (Accounting)',
        'Business, Accounting and Financial Studies (Business Management)',
        'Design and Applied Technology',
        'Information and Communication Technology',
        'Physics'
      ];
      const js1202OneOfElectives = isJS1202 && mathLevelForPer === 2
        ? {
            subjects: js1202RestrictedElectives,
            min_level: 3,
            requireCount: 1,
            met: js1202RestrictedElectives.some(subject => {
              const grade = dse_results[subject as keyof DSEResults];
              return grade && getGradeScore(String(grade), program) >= 3;
            })
          }
        : undefined;
      
      // Recalculate PER eligibility with the correct electives check
      const js1202PerEligible = isJS1202
        ? checkJS1202ConditionalPER(program, dse_results, Object.values(mer.core_minima).every(v => v), mer)
        : true;

      // JS1202 display fixes:
      // - When Math >= 3, show Mathematics min level as 3 (display only)
      // - In the PER tooltip table (GerPerBadges), specific_electives render AFTER one_of_* sections.
      //   To show "Chemistry (elective 1)" ABOVE the restricted elective list, represent Chemistry as
      //   the first one_of_group, and the restricted list as the second one_of_group (Math==2 only).
      if (isJS1202) {
        if (mathLevelForPer >= 3) {
          requirements['Mathematics'] = 3;
        }
      }
      if (js4238ConditionalMath) {
        requirements['Mathematics'] = 3;
      }

      const js1202ChemGrade = dse_results['Chemistry'];
      const js1202ChemMet = Boolean(js1202ChemGrade) && getGradeScore(String(js1202ChemGrade), program) >= 3;
      const js1202PerGroups =
        isJS1202
          ? [
              {
                name: 'Chemistry',
                subjects: ['Chemistry'],
                min_level: 3,
                requireCount: 1,
                met: js1202ChemMet,
                matched_subjects: js1202ChemMet ? ['Chemistry'] : [],
              },
              ...(mathLevelForPer === 2
                ? [
                    {
                      name: 'One of the following elective subjects',
                      subjects: js1202RestrictedElectives,
                      min_level: 3,
                      requireCount: 1,
                      met: js1202RestrictedElectives.some(subject => {
                        const grade = dse_results[subject as keyof DSEResults];
                        return grade && getGradeScore(String(grade), program) >= 3;
                      }),
                      matched_subjects: js1202RestrictedElectives.filter(subject => {
                        const grade = dse_results[subject as keyof DSEResults];
                        return grade && getGradeScore(String(grade), program) >= 3;
                      }),
                    },
                  ]
                : []),
            ]
          : undefined;

      const perEligible = 
        Object.values(mer.core_minima).every(v => v) && 
        perElectivesMet && 
        (mer.elective_requirements ? mer.elective_requirements.every((req: any) => req.met) : true) &&
        (mer.elective_alternatives ? mer.elective_alternatives.some(path => path.every((req: any) => req.met)) : true) &&
        (mer.one_of_electives ? mer.one_of_electives.met : true) &&
        (mer.one_of_groups ? mer.one_of_groups.every((g: any) => g.met) : true) &&
        (mer.specific_electives ? Object.values(mer.specific_electives).every((req: any) => req.met) : true) &&
        (mer.additional_any_one_subject ? mer.additional_any_one_subject.met : true) &&
        (mer.additional_any_two_subjects ? mer.additional_any_two_subjects.met : true) &&
        js1202PerEligible;

      const js4238PerEligible = js4238ConditionalMath
        ? mathLevelForPer >= 3 && Object.entries(mer.core_minima)
            .filter(([subject]) => subject !== 'Mathematics')
            .every(([, met]) => met) &&
          js4238ExtendedMathMatches.length > 0 &&
          js4238SecondElectiveMet
        : perEligible;
      const js4502PerEligible =
        program.jupas_id !== '4502' || meetsJS4502AdditionalRequirements(program, dse_results);
      
      return {
        ...mer,
        electives: {
          required: perElectivesRequired,
          found: perElectiveCount,
          met: perElectivesMet,
          include_cores: mer.electives?.include_cores || false,
          min_level: perElectivesMinLevel
        },
        // JS1202: only inject one_of_groups when Math=2 (conditional case); normal case uses elective_requirements
        ...(isJS1202 && mathLevelForPer === 2
          ? {
              one_of_groups: js1202PerGroups,
              one_of_electives: undefined,
              specific_electives: undefined,
              elective_requirements: undefined,
            }
          : (isJS1202
            ? { one_of_groups: undefined, one_of_electives: undefined, specific_electives: undefined }
            : (js1202OneOfElectives ? { one_of_electives: js1202OneOfElectives } : {}))),
          // JS4238: only inject one_of_electives when Math<5 (conditional case); normal case uses elective_requirements
          ...(js4238ConditionalMath
            ? {
                core_minima: {
                  ...mer.core_minima,
                  Mathematics: mathLevelForPer >= 3,
                },
                one_of_electives: {
                  subjects: js4238ExtendedMathSubjects,
                  min_level: 5,
                  requireCount: 1,
                  met: js4238ExtendedMathMatches.length > 0,
                  matched_subjects: js4238ExtendedMathMatches,
                },
                additional_any_one_subject: {
                  min_level: 3,
                  met: js4238SecondElectiveMet,
                },
                elective_requirements: undefined,
              }
            : {}),
          eligible: js4238PerEligible && js4502PerEligible,
        requirements: js4238ConditionalMath
          ? { ...requirements, Mathematics: 3 }
          : requirements
      } as any;
    })(),

    scores: {
      breakdown: formattedBreakdown,
      breakdownDetails: limitedBreakdown,
      current: roundToDecimals(realCalc.total, 2),
      possibleMax: roundToDecimals(maxCalc.total, 2),
      percentage: roundToDecimals((realCalc.total / maxCalc.total) * 100, 2)
    },
    categoryLimits: effectiveCategoryLimits,
    recommendationRate: recommendationRate,
    past_admission_scores: pastScores
  };

  if (program.jupas_id === '8507' && calculationResult.ger) {
    calculationResult.per = calculationResult.ger;
  }

  return calculationResult;
};
